import { useEffect, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { Button } from "@heroui/react";
import { useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, BadgeDollarSign, CloudDownload, Copy, Download, Eye, FileImage, FileText, GitCompareArrows, History, LockKeyhole, LogOut, Pencil, Plus, Receipt, RefreshCw, RotateCcw, Search, Trash2, Upload, Video, X } from "lucide-react";
import { api, apiBlob } from "./api";
import { createDiamondComparisonPdf, createDiamondCustomerQuote, createOperationalPdf, createStandaloneQuotationPdf } from "./documents";
import { exportBrandedWorkbook } from "./excel";
import { useSession } from "./store";

type JsonRecord = Record<string, unknown>;
type Refresh = () => void | Promise<unknown>;

type StandaloneQuoteLine = {
  key: string;
  item_type: "design" | "diamond" | "activity" | "inventory_stone" | "inventory_metal" | "inventory_jewelry";
  source_id: number;
  label: string;
  description: string;
  quantity: number;
  unit_price_minor: number;
  image?: File;
};

export function StandaloneQuotationPanel({ token, onCreated }: { token: string | null; onCreated: () => void | Promise<unknown> }) {
  const [context, setContext] = useState<JsonRecord>({});
  const [customerId, setCustomerId] = useState("");
  const [currency, setCurrency] = useState<"AED" | "USD" | "EUR">("AED");
  const [designId, setDesignId] = useState("");
  const [activityId, setActivityId] = useState("");
  const [diamondId, setDiamondId] = useState("");
  const [inventoryId, setInventoryId] = useState("");
  const [includeVat, setIncludeVat] = useState(false);
  const [documentType, setDocumentType] = useState<"quotation" | "invoice">("quotation");
  const [remarks, setRemarks] = useState("");
  const [lines, setLines] = useState<StandaloneQuoteLine[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  useEffect(()=>{try{const saved=localStorage.getItem("octadiam-commercial-document-draft-v27")||localStorage.getItem("octadiam-commercial-document-draft-v26");if(!saved)return;const value=JSON.parse(saved);setCustomerId(String(value.customerId||""));setCurrency(value.currency||"AED");setIncludeVat(Boolean(value.includeVat));setDocumentType(value.documentType||"quotation");setRemarks(String(value.remarks||""));setLines(Array.isArray(value.lines)?value.lines:[]);}catch{/* Ignore an invalid browser draft. */}},[]);
  useEffect(()=>{try{localStorage.setItem("octadiam-commercial-document-draft-v27",JSON.stringify({customerId,currency,includeVat,documentType,remarks,lines:lines.map(({image,...line})=>line)}));}catch{/* Browser storage may be disabled. */}},[customerId,currency,includeVat,documentType,remarks,lines]);
  const load = async () => {
    setError("");
    try {
      const result = await api<JsonRecord>("/v1/commercial/quotations/standalone-context", { method: "POST", body: JSON.stringify({
        ...(customerId ? { customer_id: Number(customerId) } : {}), currency,
        include_all_designs: true, include_all_diamonds: true, include_all_inventory: true
      }) }, token);
      setContext(result);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not load quotation choices."); }
  };
  useEffect(() => { void load(); }, [customerId, currency, token]);
  const customers = Array.isArray(context.customers) ? context.customers as JsonRecord[] : [];
  const designs = Array.isArray(context.designs) ? context.designs as JsonRecord[] : [];
  const activities = Array.isArray(context.activities) ? context.activities as JsonRecord[] : [];
  const diamonds = Array.isArray(context.diamonds) ? context.diamonds as JsonRecord[] : [];
  const inventory = Array.isArray(context.inventory) ? context.inventory as JsonRecord[] : [];
  const inventoryChoices = inventory.map((row) => ({ ...row, selection_key: `${String(row.inventory_type)}:${String(row.id)}` }));
  const options = (rows: JsonRecord[], key: string) => Array.from(new Set(rows.map((row) => String(row[key] || "").trim()).filter(Boolean))).sort();
  const add = (type: StandaloneQuoteLine["item_type"], source: JsonRecord | undefined) => {
    if (!source) return;
    const sourceId = Number(source.id); const key = `${type}:${sourceId}`;
    if (lines.some((line) => line.key === key)) { setError("That item is already included in this quotation."); return; }
    const label = type === "design" ? String(source.design_id) : type === "diamond" ? `Diamond ${String(source.stock_number)}`
      : type.startsWith("inventory_") ? `${String(source.stock_code)} · ${String(source.item_name || source.description || source.inventory_type)}` : String(source.service_type);
    const description = type === "design" ? [source.category, source.sub_category, source.metal_type].filter(Boolean).join(" / ")
      : type === "diamond" ? [source.shape, `${source.carat} ct`, source.color, source.clarity, source.lab].filter(Boolean).join(" / ")
      : type.startsWith("inventory_") ? [source.inventory_type,source.description,source.location].filter(Boolean).join(" / ")
      : [source.service_department, source.service_type, `per ${source.unit}`].filter(Boolean).join(" / ");
    setLines([...lines, { key, item_type: type, source_id: sourceId, label, description, quantity: 1,
      unit_price_minor: Number(source.default_unit_price_minor || 0) }]); setError("");
  };
  const update = (key: string, values: Partial<StandaloneQuoteLine>) => setLines(lines.map((line) => line.key === key ? { ...line, ...values } : line));
  const totalMinor = lines.reduce((sum, line) => sum + Math.round(line.quantity * line.unit_price_minor), 0);
  const generate = async () => {
    setBusy(true); setError(""); setMessage("");
    try {
      const name = await createStandaloneQuotationPdf({ customerId: Number(customerId), documentType, remarks, currency, token, items: lines.map((line) => ({
        item_type: line.item_type, source_id: line.source_id, description: line.description,
        quantity: line.quantity, unit_price_minor: line.unit_price_minor, image: line.image
      })), includeVat });
      setMessage(`${name} was generated, stored and downloaded. The current item list has been retained for revisions or another document.`); await onCreated();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "The quotation could not be generated."); }
    finally { setBusy(false); }
  };
  return <section className="standalone-quote-builder">
    <div className="quote-builder-grid">
      <fieldset className="document-type-field"><legend>Document type <b>*</b></legend><div className="document-type-options"><label className={documentType === "quotation" ? "selected" : ""}><input type="radio" name="commercial-document-type" value="quotation" checked={documentType === "quotation"} onChange={() => setDocumentType("quotation")}/><FileText size={18}/><span>Quotation</span></label><label className={documentType === "invoice" ? "selected" : ""}><input type="radio" name="commercial-document-type" value="invoice" checked={documentType === "invoice"} onChange={() => setDocumentType("invoice")}/><Receipt size={18}/><span>Invoice</span></label></div></fieldset>
      <label className="form-field"><span>Currency <b>*</b></span><select value={currency} onChange={(event) => setCurrency(event.target.value as typeof currency)}>
        <option value="AED">AED - UAE Dirham</option><option value="USD">USD - US Dollar</option><option value="EUR">EUR - Euro</option>
      </select></label>
      <SearchableChoice name="customer_id" label="Customer" rows={customers} value={customerId} onChange={setCustomerId} required labelKeys={["customer_code","customer_name"]}/>
    </div>
    <div className="quote-source-grid">
      <article><h3>Jewelry design</h3><div className="quote-source-control"><SearchableChoice name="design_id" label="Jewelry design" rows={designs} value={designId} onChange={setDesignId} valueKey="id" labelKeys={["design_id","entry_date","customer_name","category","sub_category","metal_type"]} filterOptions={{category:options(designs,"category"),sub_category:options(designs,"sub_category"),metal_type:options(designs,"metal_type")}}/><Button isIconOnly className="action-button action-create" title="Add selected jewelry design" aria-label="Add selected jewelry design" isDisabled={!designId} onPress={() => add("design", designs.find((row) => String(row.id) === designId))}><Plus size={17}/></Button></div></article>
      <article><h3>Diamond stock</h3><div className="quote-source-control"><SearchableChoice name="diamond_id" label="Diamond stock" rows={diamonds} value={diamondId} onChange={setDiamondId} valueKey="id" labelKeys={["stock_number","certificate_no","carat","shape","color","clarity","lab"]} filterOptions={{shape:options(diamonds,"shape"),color:options(diamonds,"color"),clarity:options(diamonds,"clarity"),lab:options(diamonds,"lab")}}/><Button isIconOnly className="action-button action-create" title="Add selected diamond" aria-label="Add selected diamond" isDisabled={!diamondId} onPress={() => add("diamond", diamonds.find((row) => String(row.id) === diamondId))}><Plus size={17}/></Button></div></article>
      <article><h3>Activity / service</h3><div className="quote-source-control"><select value={activityId} onChange={(event) => setActivityId(event.target.value)}><option value="">Choose an activity</option>{activities.map((row) => <option key={String(row.id)} value={String(row.id)}>{String(row.service_department)} · {String(row.service_type)} · {String(row.unit)}</option>)}</select><Button isIconOnly className="action-button action-create" title="Add selected activity" aria-label="Add selected activity" isDisabled={!activityId} onPress={() => add("activity", activities.find((row) => String(row.id) === activityId))}><Plus size={17}/></Button></div></article>
      <article><h3>Inventory · Stone, Metal or Jewelry</h3><div className="quote-source-control"><SearchableChoice name="inventory_id" label="Inventory item" rows={inventoryChoices} value={inventoryId} onChange={setInventoryId} valueKey="selection_key" labelKeys={["inventory_type","stock_code","description","category","location"]} filterOptions={{inventory_type:options(inventoryChoices,"inventory_type"),category:options(inventoryChoices,"category"),location:options(inventoryChoices,"location")}}/><Button isIconOnly className="action-button action-create" title="Add selected inventory item" aria-label="Add selected inventory item" isDisabled={!inventoryId} onPress={()=>{const [type,id]=inventoryId.split(":");add(`inventory_${type}` as StandaloneQuoteLine["item_type"],inventory.find(row=>String(row.inventory_type)===type&&String(row.id)===id));}}><Plus size={17}/></Button></div></article>
    </div>
    <label className="form-field quote-remarks"><span>Remarks</span><textarea value={remarks} onChange={(event) => setRemarks(event.target.value)} maxLength={4000} placeholder={`Optional remarks to print on the ${documentType}`}/></label>
    <div className="quote-lines"><div className="quote-lines-head"><span>Type / item</span><span>Description</span><span>Picture</span><span>Qty</span><span>Unit price ({currency})</span><span>Line total</span><span>Action</span></div>
      {lines.map((line) => <div className="quote-line" key={line.key}><span><b>{line.item_type.toUpperCase()}</b><small>{line.label}</small></span><input value={line.description} onChange={(event) => update(line.key,{description:event.target.value})}/><label className={`quote-image-upload ${line.item_type !== "activity" ? "disabled" : ""}`} title={line.item_type === "activity" ? "Upload an activity picture" : "Pictures can be uploaded for activity/service lines"}><FileImage size={16}/><span>{line.image?.name || (line.item_type === "activity" ? "Choose or take picture" : "Not applicable")}</span><input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" disabled={line.item_type !== "activity"} onChange={(event) => { const file=event.currentTarget.files?.[0]; if(!file)return; if(file.size>10_000_000){setError("Activity pictures must be 10 MB or smaller.");event.currentTarget.value="";return;} update(line.key,{image:file});setError(""); }}/></label><input type="number" min="0.01" step="0.01" value={line.quantity} onChange={(event) => update(line.key,{quantity:Math.max(.01,Number(event.target.value||0))})}/><input type="number" min="0" step="0.01" value={(line.unit_price_minor/100).toFixed(2)} onChange={(event) => update(line.key,{unit_price_minor:Math.max(0,Math.round(Number(event.target.value||0)*100))})}/><strong>{currency} {(line.quantity*line.unit_price_minor/100).toFixed(2)}</strong><Button isIconOnly className="action-button action-delete" title={`Remove ${line.label}`} aria-label={`Remove ${line.label}`} onPress={() => setLines(lines.filter((entry) => entry.key !== line.key))}><Trash2 size={16}/></Button></div>)}
      {!lines.length && <div className="empty-state compact">Add one or more designs, available diamonds, inventory items or activities.</div>}
    </div>
    <div className="quote-builder-footer"><label className="quote-vat-option"><input type="checkbox" checked={includeVat} onChange={(event)=>setIncludeVat(event.target.checked)}/><span>Include VAT (5%)</span></label><div className="quote-total-summary"><span>Subtotal <b>{currency} {(totalMinor/100).toFixed(2)}</b></span><span>VAT {includeVat ? "5%" : "not included"} <b>{currency} {(includeVat?totalMinor*.05/100:0).toFixed(2)}</b></span><strong>Total {currency} {((totalMinor+(includeVat?Math.round(totalMinor*.05):0))/100).toFixed(2)}</strong></div><Button isIconOnly className="action-button action-create" title={`Generate and save ${documentType} PDF`} aria-label={`Generate and save ${documentType} PDF`} isLoading={busy} isDisabled={!customerId||!lines.length} onPress={() => void generate()}><Receipt size={19}/></Button></div>
    {error && <div className="error-banner">{error}</div>}{message && <div className="success-banner">{message}</div>}
  </section>;
}

function CommercialSourcePicker({kind,rows,onClose,onSelect}:{kind:"design"|"diamond"|"inventory";rows:JsonRecord[];onClose:()=>void;onSelect:(row:JsonRecord)=>void}){
  const [q,setQ]=useState(""),[filter,setFilter]=useState("");
  const filterKey=kind==="design"?"category":kind==="diamond"?"shape":"inventory_type";
  const options=Array.from(new Set(rows.map(row=>String(row[filterKey]||"")).filter(Boolean))).sort();
  const filtered=rows.filter(row=>(!filter||String(row[filterKey])===filter)&&(!q||JSON.stringify(row).toLowerCase().includes(q.toLowerCase())));
  return <div className="dialog-backdrop" role="presentation"><section className="workflow-dialog commercial-picker-dialog" role="dialog" aria-modal="true"><header><div><p className="eyebrow">Search and select</p><h2>{kind==="design"?"Jewelry Design":kind==="diamond"?"Diamond Stock":"Inventory"}</h2><p>Use the filters below, then double-click a result or press Select.</p></div><button className="dialog-close" onClick={onClose} aria-label="Close"><X/></button></header><div className="workflow-body workflow-form"><div className="commercial-picker-filters"><label className="form-field"><span>Search all key fields</span><input value={q} onChange={e=>setQ(e.target.value)} placeholder={kind==="design"?"Design ID, customer, metal, date…":kind==="diamond"?"Stock, certificate, carat, color, clarity…":"Stock code, item, category, location…"}/></label><label className="form-field"><span>{kind==="design"?"Category":kind==="diamond"?"Shape":"Inventory type"}</span><select value={filter} onChange={e=>setFilter(e.target.value)}><option value="">All</option>{options.map(option=><option key={option}>{option}</option>)}</select></label></div><div className="detail-table-wrap commercial-picker-results"><table className="detail-table"><thead><tr>{(kind==="design"?["Design ID","Customer","Category","Metal","Date"]:kind==="diamond"?["Stock","Carat","Shape","Color","Clarity","Lab","Certificate"]:["Type","Stock code","Description","Category","Location"]).map(label=><th key={label}>{label}</th>)}<th>Action</th></tr></thead><tbody>{filtered.map(row=><tr key={`${kind}:${String(row.id)}`} onDoubleClick={()=>onSelect(row)}>{(kind==="design"?[row.design_id,row.customer_name,row.category,row.metal_type,row.entry_date]:kind==="diamond"?[row.stock_number,row.carat,row.shape,row.color,row.clarity,row.lab,row.certificate_no]:[row.inventory_type,row.stock_code,row.description,row.category,row.location]).map((value,index)=><td key={index}>{String(value||"—")}</td>)}<td><Button className="action-button action-create" size="sm" onPress={()=>onSelect(row)}>Select</Button></td></tr>)}</tbody></table></div>{!filtered.length&&<div className="empty-state compact">No matching records. Adjust the filters or run the quick search first to retrieve more records.</div>}</div></section></div>;
}

const idempotencyKey = () => crypto.randomUUID();
const today = () => new Date().toISOString().slice(0, 10);
const number = (value: FormDataEntryValue | null) => Number(value || 0);
const text = (value: FormDataEntryValue | null) => String(value || "").trim();
const filePayloads = async (values: FormData, name: string) => Promise.all(
  values.getAll(name)
    .filter((value): value is File => value instanceof File && value.size > 0)
    .map(async (file) => ({
      original_name: file.name,
      mime_type: file.type || "application/octet-stream",
      content_base64: await fileBase64(file)
    }))
);
const fileBase64 = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || "").split(",", 2)[1] || "");
  reader.onerror = () => reject(reader.error || new Error(`Could not read ${file.name}`));
  reader.readAsDataURL(file);
});

type PendingMedia = {
  key: string;
  file: File;
  media_type: string;
  stone_line_uid?: string | null;
  is_main?: boolean;
};

const DESIGN_MEDIA_CATEGORIES = [
  "Hero Image", "Reference Photo", "Render Picture", "Render Video", "Final Photo",
  "Final Video", "Technical Drawing", "Jewelry Certificate"
];

async function pendingMediaPayload(items: PendingMedia[]) {
  return Promise.all(items.map(async (item) => ({
    media_type: item.media_type,
    original_name: item.file.name,
    content_base64: await fileBase64(item.file),
    stone_line_uid: item.stone_line_uid ?? null,
    ...(item.is_main !== undefined ? { is_main: item.is_main } : {})
  })));
}

function ManageFiles({
  title,
  categories,
  existing = [],
  pending,
  setPending,
  deletedIds,
  setDeletedIds,
  stoneLineUid = null,
  iconOnly = false
}: {
  title: string;
  categories: string[];
  existing?: JsonRecord[];
  pending: PendingMedia[];
  setPending: Dispatch<SetStateAction<PendingMedia[]>>;
  deletedIds: number[];
  setDeletedIds: Dispatch<SetStateAction<number[]>>;
  stoneLineUid?: string | null;
  iconOnly?: boolean;
}) {
  const token = useSession((state) => state.accessToken);
  const [open, setOpen] = useState(false);
  const [availableCategories, setAvailableCategories] = useState(categories);
  const [category, setCategory] = useState(categories[0] || "Attachment");
  useEffect(() => {
    const lookupCategory=title === "Manage Job Card Pictures & Files" ? "job_file_category" : title === "Manage Design Attachments" || /certificate files/i.test(title) ? "design_file_category" : "";
    if (!lookupCategory) { setAvailableCategories(categories); return; }
    void api<JsonRecord[]>(`/v1/reference/lookup-values?category=${lookupCategory}`, {}, token)
      .then((rows) => setAvailableCategories(rows.map((row) => String(row.value || "")).filter(Boolean)))
      .catch(() => setAvailableCategories(categories));
  }, [title, token, categories.join("|")]);
  useEffect(() => { if (!availableCategories.includes(category)) setCategory(availableCategories[0] || "Attachment"); }, [availableCategories, category]);
  const saved = existing.filter((item) =>
    String(item.stone_line_uid || "") === String(stoneLineUid || "") && !deletedIds.includes(Number(item.id))
  );
  const selected = pending.filter((item) => String(item.stone_line_uid || "") === String(stoneLineUid || ""));
  return (
    <>
      <Button isIconOnly={iconOnly} type="button" className="action-button action-view"
        aria-label={`${title}; ${saved.length + selected.length} files`} title={`Manage Files (${saved.length + selected.length})`}
        startContent={iconOnly ? undefined : <Upload size={16} />} onPress={() => setOpen(true)}>
        {iconOnly ? <Upload size={17} /> : `Manage Files (${saved.length + selected.length})`}
      </Button>
      {open && (
        <div className="dialog-backdrop manage-files-backdrop" role="presentation">
          <section className="workflow-dialog manage-files-dialog" role="dialog" aria-modal="true" aria-label={title}>
            <header>
              <div><p className="eyebrow">Attachments</p><h2>{title}</h2></div>
              <button className="dialog-close" onClick={() => setOpen(false)} aria-label="Close"><X /></button>
            </header>
            <div className="workflow-body">
              <div className="manage-files-toolbar">
                <Choice name={`media-category-${stoneLineUid || "design"}`} label="File category" options={availableCategories} value={category} onChange={setCategory} />
                <label className="file-add-button">
                  <Upload size={17} /><span>Add one or more files</span>
                  <input type="file" multiple onChange={(event) => {
                    const files = [...(event.currentTarget.files || [])];
                    setPending((current) => [...current, ...files.map((file) => ({
                      key: crypto.randomUUID(), file, media_type: category, stone_line_uid: stoneLineUid
                    }))]);
                    event.currentTarget.value = "";
                  }} />
                </label>
              </div>
              <div className="managed-file-list">
                {!saved.length && !selected.length && <div className="inline-empty">No files attached.</div>}
                {saved.map((item) => (
                  <div key={`saved-${String(item.id)}`} className="managed-file-row">
                    <div><strong>{String(item.original_name || "Saved file")}</strong><small>Saved · {String(item.media_type || "Attachment")}</small></div>
                    <div className="row-actions"><Button type="button" className="action-button action-view" startContent={<Eye size={15} />} onPress={async () => {
                      const blob = await jewelryMediaBlob(Number(item.id), token); window.open(URL.createObjectURL(blob), "_blank", "noopener,noreferrer");
                    }}>View</Button><Button type="button" className="action-button action-delete" startContent={<Trash2 size={15} />}
                      onPress={() => setDeletedIds((current) => [...new Set([...current, Number(item.id)])])}>Delete</Button>
                    </div>
                  </div>
                ))}
                {selected.map((item) => (
                  <div key={item.key} className="managed-file-row">
                    <div><strong>{item.file.name}</strong><small>New · {item.media_type}</small></div>
                    <div className="row-actions"><Button type="button" className="action-button action-view" startContent={<Eye size={15} />} onPress={() => window.open(URL.createObjectURL(item.file), "_blank", "noopener,noreferrer")}>View</Button><Button type="button" className="action-button action-delete" startContent={<Trash2 size={15} />}
                      onPress={() => setPending((current) => current.filter((entry) => entry.key !== item.key))}>Remove</Button>
                    </div>
                  </div>
                ))}
              </div>
              <footer className="form-footer"><Button type="button" className="action-button action-save" onPress={() => setOpen(false)}>Done</Button></footer>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

function InlineDesignFiles({ categories, pending, setPending, existing = [], deletedIds = [], setDeletedIds, token = null, mediaKind = "jewelry" }: {
  categories: string[]; pending: PendingMedia[]; setPending: Dispatch<SetStateAction<PendingMedia[]>>;
  existing?: JsonRecord[]; deletedIds?: number[]; setDeletedIds?: Dispatch<SetStateAction<number[]>>; token?: string | null; mediaKind?: "jewelry"|"job-card";
}) {
  const [category, setCategory] = useState(categories[0] || "Attachment");
  useEffect(() => { if (!categories.includes(category)) setCategory(categories[0] || "Attachment"); }, [categories, category]);
  return <div className="inline-design-files">
    <div className="manage-files-toolbar"><Choice name="design-file-category" label="File category" options={categories} value={category} onChange={setCategory} />
      <label className="file-add-button"><Upload size={17} /><span>Add one or more files</span><input type="file" multiple onChange={(event) => {
        const files = [...(event.currentTarget.files || [])];
        setPending((current) => [...current, ...files.map((file) => ({ key: crypto.randomUUID(), file, media_type: category }))]);
        event.currentTarget.value = "";
      }} /></label></div>
    <div className="managed-file-list">{!pending.length && !existing.filter((item) => !deletedIds.includes(Number(item.id))).length && <div className="inline-empty">No files attached.</div>}
      {existing.filter((item) => !deletedIds.includes(Number(item.id))).map((item) => <div key={`saved-${String(item.id)}`} className="managed-file-row"><div><strong>{String(item.original_name || "Saved file")}</strong><small>Saved · {String(item.media_type || "Attachment")}</small></div>
        <div className="row-actions"><Button type="button" className="action-button action-view" startContent={<Eye size={15} />} onPress={async () => { const blob = mediaKind === "job-card" ? await jobCardMediaBlob(Number(item.id), token) : await jewelryMediaBlob(Number(item.id), token); window.open(URL.createObjectURL(blob), "_blank", "noopener,noreferrer"); }}>View</Button>
          {setDeletedIds && <Button type="button" className="action-button action-delete" startContent={<Trash2 size={15} />} onPress={() => setDeletedIds((current) => [...new Set([...current, Number(item.id)])])}>Delete</Button>}</div></div>)}
      {pending.filter((item) => !item.stone_line_uid).map((item) => <div key={item.key} className="managed-file-row"><div><strong>{item.file.name}</strong><small>{item.media_type} · {(item.file.size / 1024).toFixed(2)} KB</small></div>
        <div className="row-actions"><Button type="button" className="action-button action-view" startContent={<Eye size={15} />} onPress={() => window.open(URL.createObjectURL(item.file), "_blank", "noopener,noreferrer")}>View</Button>
          <Button type="button" className="action-button action-delete" startContent={<Trash2 size={15} />} onPress={() => setPending((current) => current.filter((entry) => entry.key !== item.key))}>Delete</Button></div></div>)}</div>
  </div>;
}

async function jewelryMediaBlob(mediaId: number, token: string | null) {
  const payload = await api<JsonRecord>(`/v1/jewelry/media/${mediaId}/content`, {}, token);
  const binary = atob(String(payload.content_base64 || ""));
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new Blob([bytes], { type: String(payload.mime_type || "application/octet-stream") });
}

async function jobCardMediaBlob(mediaId: number, token: string | null) {
  const payload = await api<JsonRecord>(`/v1/operations/job-card-media/${mediaId}/content`, {}, token);
  const binary = atob(String(payload.content_base64 || ""));
  return new Blob([Uint8Array.from(binary, (character) => character.charCodeAt(0))], { type: "application/octet-stream" });
}

function JobCardStoredImage({ mediaId, token }: { mediaId: number; token: string | null }) {
  const [source, setSource] = useState("");
  useEffect(() => { let currentUrl=""; void jobCardMediaBlob(mediaId,token).then((blob)=>{currentUrl=URL.createObjectURL(blob);setSource(currentUrl);}).catch(()=>setSource("")); return()=>{if(currentUrl)URL.revokeObjectURL(currentUrl);}; },[mediaId,token]);
  return source ? <img src={source} alt="Job Card"/> : <div className="preview-placeholder">Preview unavailable</div>;
}

function StoredImage({ mediaId, token, alt }: { mediaId: number; token: string | null; alt: string }) {
  const [source, setSource] = useState("");
  useEffect(() => {
    let currentUrl = "";
    void jewelryMediaBlob(mediaId, token).then((blob) => {
      currentUrl = URL.createObjectURL(blob); setSource(currentUrl);
    }).catch(() => setSource(""));
    return () => { if (currentUrl) URL.revokeObjectURL(currentUrl); };
  }, [mediaId, token]);
  return source ? <img src={source} alt={alt} /> : <div className="preview-placeholder">Preview unavailable</div>;
}

function PendingImage({ file }: { file: File }) {
  const source = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(source), [source]);
  return <img src={source} alt={file.name} />;
}

export function OperationalToolbar({
  moduleKey,
  rows,
  selectedRows,
  token,
  refresh,
  placement = "all",
  exportColumns
}: {
  moduleKey: string;
  rows: JsonRecord[];
  selectedRows: JsonRecord[];
  token: string | null;
  refresh: Refresh;
  placement?: "all" | "primary" | "selection";
  exportColumns?: string[];
}) {
  const user = useSession((state) => state.user);
  const environment = useSession((state) => state.environment);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [quotationOpen, setQuotationOpen] = useState(false);
  const [diamondQuoteOpen, setDiamondQuoteOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{label:string;path:string}|null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [syncProgress, setSyncProgress] = useState("");
  const selected = selectedRows[0] || null;
  const selectedUnavailable=selectedRows.length===1&&String(selected?.internal_status||"")==="Unavailable from supplier";
  const selectedMediaUrl = (field: "image_url" | "video_url" | "certificate_url") => {
    const value = selectedRows.length === 1 ? String(selected?.[field] || "").trim() : "";
    return /^https?:\/\//i.test(value) ? value : "";
  };
  const openSelectedMedia = (field: "image_url" | "video_url" | "certificate_url") => {
    const url = selectedMediaUrl(field);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };
  const createable = ["designs", "jobCards", "inventory", "customers", "suppliers", "receivedInvoices", "pettyCash"]
    .includes(moduleKey);
  const editable = ["designs", "jobCards", "inventory", "customers", "suppliers", "receivedInvoices"]
    .includes(moduleKey);
  useEffect(() => {
    setMessage("");
    setOpen(false);
    setEditOpen(false);
    setDetailsOpen(false);
    setQuotationOpen(false);
    setDiamondQuoteOpen(false);
  }, [moduleKey]);

  const synchronize = async () => {
    setBusy(true);
    setMessage("");
    setSyncProgress(moduleKey === "diamondStock" ? "Connecting to the supplier and retrieving the latest stock…" : "");
    try {
      if (moduleKey === "diamondStock") {
        const result = await api<JsonRecord>("/v1/diamond-stock/synchronize", { method: "POST", body: JSON.stringify({ force: true }) }, token);
        const returned = Number(result.received_count ?? result.item_count ?? result.count ?? 0);
        const active = Number(result.active_count ?? 0);
        const catalogue = Number(result.catalogue_count ?? active);
        const lifecycle = Math.max(0, Number(result.lifecycle_count ?? 0));
        const historical = Math.max(0, Number(result.historical_inactive_count ?? 0));
        setMessage(`Diamond Stock synchronisation completed: latest supplier feed contains ${returned.toLocaleString()} records; ${active.toLocaleString()} current supplier rows are available${lifecycle ? `; ${lifecycle.toLocaleString()} protected Reserved/Sold lifecycle records are shown separately` : ""}. ${historical.toLocaleString()} previously synchronized rows were not returned by the latest feed and remain visible as “Unavailable from supplier” for history and comparison only.`);
      } else if (moduleKey === "shopify") {
        const result = await api<JsonRecord>("/v1/shopify/import", { method: "POST" }, token);
        setMessage(`Shopify import completed: ${result.imported_count ?? result.count ?? "catalog updated"}.`);
      } else if (moduleKey === "metalPrices") {
        const result = await api<JsonRecord>("/v1/jewelry/metal-prices/refresh", {
          method: "POST",
          body: JSON.stringify({ force: true })
        }, token);
        setMessage(result.saved === false
          ? "Rates checked; provider values are unchanged."
          : "Latest gold and silver rates were retrieved and saved.");
      }
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The operation failed.");
    } finally {
      setBusy(false);
      setSyncProgress("");
    }
  };
  const createPdf = async (
    kind: "quotation" | "item-sheet" | "job-card",
    quotationCustomerId?: number,
    quotationQuantities: Record<string, number> = {},
    quotationCurrency: "AED" | "USD" | "EUR" = "AED"
  ) => {
    if (!selected) return;
    setBusy(true);
    setMessage("");
    try {
      const name = await createOperationalPdf({
        kind,
        selected,
        selectedRows: kind === "quotation"
          ? selectedRows.map((row) => ({ ...row, quotation_quantity: quotationQuantities[String(row.id)] || 1 }))
          : [selected],
        quotationCustomerId,
        quotationCurrency,
        token
      });
      setMessage(`${name} was generated, saved, and downloaded.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The document could not be generated.");
    } finally {
      setBusy(false);
    }
  };
  const requestDelete = () => {
    if (!selected) return;
    const id=Number(selected.id);
    const inventoryType=String(selected.inventory_type||selected.item_type||"stone").toLowerCase();
    const config:Record<string,{path:string;label:string}>={
      designs:{path:`/v1/jewelry/designs/${id}`,label:String(selected.design_id||id)},
      jobCards:{path:`/v1/operations/job-cards/${id}`,label:String(selected.job_card_number||id)},
      customers:{path:`/v1/reference/customers/${id}`,label:String(selected.customer_name||selected.name||id)},
      suppliers:{path:`/v1/reference/suppliers/${id}`,label:String(selected.supplier_name||selected.name||id)},
      employees:{path:`/v1/employees/${id}`,label:String(selected.employee_name||selected.employee_code||id)},
      receivedInvoices:{path:`/v1/commercial/received-invoices/${id}`,label:String(selected.invoice_number||id)},
      pettyCash:{path:`/v1/commercial/petty-cash/${id}`,label:String(selected.voucher_number||id)},
      inventory:{path:`/v1/operations/inventory/${inventoryType}/${id}`,label:String(selected.stock_code||selected.item_name||id)}
    };
    if(config[moduleKey])setDeleteConfirmation(config[moduleKey]);
  };
  const deleteSelected = async () => {
    if (!deleteConfirmation) return;
    setBusy(true); setMessage("");
    try {
      await api(deleteConfirmation.path, { method: "DELETE" }, token);
      setMessage(`${deleteConfirmation.label} was moved to Deleted Records and may be restored by an administrator.`);
      setDeleteConfirmation(null);
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The record could not be deleted.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className={`operational-toolbar ${moduleKey === "diamondStock" ? `diamond-toolbar ${placement}` : ""}`}>
        {placement === "all" && createable && (
          <Button isIconOnly className="action-button action-create" startContent={<Plus size={17} />} onPress={() => setOpen(true)}
            aria-label={moduleKey === "designs" ? "New jewelry design" : moduleKey === "jobCards" ? "New job card" : `Create ${moduleKey}`}
            title={moduleKey === "designs" ? "New jewelry design" : moduleKey === "jobCards" ? "New job card" : `Create ${moduleKey}`}>
            <span className="sr-only">{moduleKey === "designs" ? "New jewelry design" :
              moduleKey === "jobCards" ? "New job card" :
                moduleKey === "inventory" ? "New inventory entry" :
                  moduleKey === "receivedInvoices" ? "Record invoice" :
                    moduleKey === "pettyCash" ? "New petty-cash entry" :
                      `New ${moduleKey === "customers" ? "customer" : "supplier"}`}</span>
          </Button>
        )}
        {syncProgress && placement !== "selection" && <span className="sync-progress" role="status" aria-live="polite"><span className="sync-progress-bar" />{syncProgress}</span>}
        {placement !== "selection" && (moduleKey === "diamondStock" || moduleKey === "shopify" || moduleKey === "metalPrices") && (
          <Button
            className="action-button action-refresh"
            startContent={moduleKey === "diamondStock" ? <CloudDownload size={18} /> : <RefreshCw size={17} />}
            onPress={() => void synchronize()}
            isLoading={busy}
            aria-label={moduleKey === "diamondStock" ? "Synchronise Stock" : undefined}
            title={moduleKey === "diamondStock" ? "Synchronise Stock" : undefined}
          >
            {moduleKey === "diamondStock" ? null :
              moduleKey === "shopify" ? "Import from Shopify" : "Refresh market rates"}
          </Button>
        )}
        {placement !== "selection" && <Button isIconOnly
          className="action-button action-export"
          startContent={<Download size={17} />}
          onPress={async () => {
            setBusy(true);
            setMessage("");
            try {
              await exportBrandedWorkbook({
                moduleKey,
                rows,
                exportedBy: user?.displayName || "OctaDiam user",
                environment,
                columns: exportColumns
              });
              setMessage("Excel export was downloaded successfully.");
            } catch (error) {
              setMessage(error instanceof Error ? error.message : "Excel export failed.");
            } finally {
              setBusy(false);
            }
          }}
          isDisabled={!rows.length}
          aria-label="Export to Excel" title="Export to Excel" />}
        {placement === "primary" && moduleKey === "diamondStock" && <Button
          className="action-button action-neutral" startContent={<RefreshCw size={17} />}
          aria-label="Reload saved Diamond Stock" title="Reload the saved Diamond Stock list from the server (does not contact the supplier)"
          onPress={async () => { setBusy(true); setMessage(""); try { await refresh(); setMessage("Saved Diamond Stock list reloaded."); } finally { setBusy(false); } }} />}
        {placement !== "primary" && <Button isIconOnly
          className="action-button action-view"
          startContent={<Eye size={17} />}
          onPress={() => setDetailsOpen(true)}
          isDisabled={selectedRows.length !== 1}
          aria-label="View details" title="View details" />}
        {placement !== "primary" && moduleKey === "diamondStock" && <>
          <Button isIconOnly className="action-button action-view" startContent={<GitCompareArrows size={17}/>} aria-label="Compare selected stones" title="Compare 2 or 3 selected stones" isDisabled={selectedRows.length<2||selectedRows.length>3} onPress={()=>setCompareOpen(true)}/>
          <span className="button-tooltip" title="View Image"><Button className="action-button action-view" startContent={<FileImage size={17} />}
            aria-label="View Image"
            onPress={() => openSelectedMedia("image_url")} isDisabled={!selectedMediaUrl("image_url")} /></span>
          <Button className="action-button action-view" startContent={<Video size={17} />}
            aria-label="View video" title="View video"
            onPress={() => openSelectedMedia("video_url")} isDisabled={!selectedMediaUrl("video_url")} />
          <Button className="action-button action-view" startContent={<BadgeCheck size={17} />}
            aria-label="View certificate" title="View certificate"
            onPress={() => openSelectedMedia("certificate_url")} isDisabled={!selectedMediaUrl("certificate_url")} />
          <span className="button-tooltip" title="Generate Quote"><Button className="action-button action-create" startContent={<Receipt size={17} />}
            aria-label="Generate Quote"
            onPress={() => setDiamondQuoteOpen(true)} isDisabled={selectedRows.length !== 1 || selectedUnavailable || busy} /></span>
        </>}
        {placement === "all" && editable && (
          <Button isIconOnly
            className="action-button action-edit"
            startContent={<Pencil size={17} />}
            onPress={() => setEditOpen(true)}
            isDisabled={selectedRows.length !== 1}
            aria-label="Edit selected record" title="Edit selected record" />
        )}
        {placement === "all" && moduleKey === "designs" && (
          <>
            <Button isIconOnly className="action-button action-create" title={selectedRows.length > 1 ? `Generate combined quotation for ${selectedRows.length} designs` : "Generate quotation PDF"} aria-label={selectedRows.length > 1 ? "Generate combined quotation" : "Generate quotation PDF"}
              startContent={<Receipt size={17} />}
              isDisabled={!selected || busy}
              onPress={() => setQuotationOpen(true)}
            />
            <Button isIconOnly className="action-button action-view" title="Generate item details PDF" aria-label="Generate item details PDF"
              startContent={<FileText size={17} />}
              isDisabled={selectedRows.length !== 1 || busy}
              onPress={() => void createPdf("item-sheet")}
            />
          </>
        )}
        {placement === "all" && ["designs","jobCards","inventory","customers","suppliers","employees","receivedInvoices","pettyCash"].includes(moduleKey) && (
          <Button isIconOnly className="action-button action-delete" startContent={<Trash2 size={17} />}
            isDisabled={selectedRows.length !== 1 || busy} onPress={requestDelete}
            aria-label="Delete selected record" title="Move selected record to Deleted Records" />
        )}
        {placement === "all" && moduleKey === "jobCards" && (
          <Button isIconOnly
            className="action-button action-view"
            startContent={<FileText size={17} />}
            isDisabled={selectedRows.length !== 1 || busy}
            onPress={() => void createPdf("job-card")}
            aria-label="Generate Job Card PDF" title="Generate Job Card PDF" />
        )}
        {message && (
          <span className={/fail|error/i.test(message) ? "toolbar-error" : "toolbar-message"}>
            {message}
            <button type="button" className="message-dismiss" aria-label="Dismiss message" onClick={() => setMessage("")}><X size={14} /></button>
          </span>
        )}
      </div>
      {open && (
        <WorkflowDialog
          moduleKey={moduleKey}
          token={token}
          onClose={() => setOpen(false)}
          onSaved={async () => {
            setOpen(false);
            setMessage("Record saved successfully.");
            await refresh();
          }}
        />
      )}
      {deleteConfirmation && <WorkflowConfirmDialog description={`${deleteConfirmation.label} · It will move to Deleted Records and can be restored by an administrator. Linked business records will not be deleted.`} onCancel={()=>setDeleteConfirmation(null)} onConfirm={deleteSelected}/>}
      {detailsOpen && selected && (
        <RecordDetailsDialog
          moduleKey={moduleKey}
          selected={selected}
          token={token}
          onClose={() => setDetailsOpen(false)}
          onChanged={async () => { setDetailsOpen(false); await refresh(); }}
        />
      )}
      {editOpen && selected && (
        <EditRecordDialog
          moduleKey={moduleKey}
          selected={selected}
          token={token}
          onClose={() => setEditOpen(false)}
          onSaved={async () => {
            setEditOpen(false);
            setMessage("A new record version was saved successfully.");
            await refresh();
          }}
        />
      )}
      {quotationOpen && selectedRows.length > 0 && (
        <QuotationDialog
          selectedRows={selectedRows}
          token={token}
          busy={busy}
          onClose={() => setQuotationOpen(false)}
          onCreate={async (customerId, quantities, currency) => {
            await createPdf("quotation", customerId, quantities, currency);
            setQuotationOpen(false);
          }}
        />
      )}
      {diamondQuoteOpen && selected && (
        <DiamondQuotationDialog selected={selected} token={token} busy={busy}
          onClose={() => setDiamondQuoteOpen(false)} onMessage={setMessage} onBusy={setBusy} />
      )}
      {compareOpen&&<DiamondCompareDialog stones={selectedRows.slice(0,3)} token={token} onClose={()=>setCompareOpen(false)}/>}
    </>
  );
}

function WorkflowConfirmDialog({description,onCancel,onConfirm}:{description:string;onCancel:()=>void;onConfirm:()=>void|Promise<void>}){
  const [busy,setBusy]=useState(false);const [error,setError]=useState("");
  return <div className="dialog-backdrop" role="presentation" onMouseDown={onCancel}><section className="workflow-dialog confirmation-dialog" role="alertdialog" aria-modal="true" aria-labelledby="workflow-confirm-title" onMouseDown={event=>event.stopPropagation()}><header><div><p className="eyebrow">Confirmation required</p><h2 id="workflow-confirm-title">Delete Records</h2></div><Button isIconOnly className="action-button action-neutral" title="Close" aria-label="Close" onPress={onCancel}><X size={18}/></Button></header><div className="dialog-body"><div className="confirmation-warning"><Trash2 size={24}/><div><p className="confirmation-question">Are you sure you want to delete the record?</p><p className="confirmation-record-details">{description}</p></div></div>{error&&<div className="error-banner">{error}</div>}</div><footer><Button className="action-button action-delete" isLoading={busy} onPress={async()=>{setBusy(true);setError("");try{await onConfirm();}catch(reason){setError(reason instanceof Error?reason.message:"The operation failed.");}finally{setBusy(false);}}}>Delete</Button></footer></section></div>;
}

export function normalizeDiamondComparisonDetail(stone:JsonRecord,response:JsonRecord):JsonRecord {
  const item=response.item&&typeof response.item==="object"?response.item as JsonRecord:response;
  const supplierFields=response.supplier_fields&&typeof response.supplier_fields==="object"?response.supplier_fields as JsonRecord:{};
  return {...stone,...item,supplier_fields:supplierFields,lifecycle_history:Array.isArray(response.lifecycle_history)?response.lifecycle_history:[]};
}

function DiamondCompareDialog({stones,token,onClose}:{stones:JsonRecord[];token:string|null;onClose:()=>void}) {
  const [details,setDetails]=useState(stones),[error,setError]=useState(""),[printing,setPrinting]=useState(false);
  useEffect(()=>{let active=true;Promise.all(stones.map(async stone=>{
    const response=await api<JsonRecord>(`/v1/diamond-stock/${Number(stone.id)}`,{},token);
    return normalizeDiamondComparisonDetail(stone,response);
  })).then(rows=>active&&setDetails(rows)).catch(reason=>active&&setError(reason instanceof Error?reason.message:"Could not load complete stone details."));return()=>{active=false};},[stones,token]);
  const groups:Array<[string,Array<[string,string]>]>=[
    ["Commercial",[["Discount %","discount_percent"],["Supplier $/ct","price_per_carat"],["Supplier total","total_price"],["OctaDiam discount %","octadiam_discount_percent"],["OctaDiam AED/ct","octadiam_price_per_carat"],["OctaDiam total AED","octadiam_total_price"]]],
    ["Identity & grading",[["Shape","shape"],["Carat","carat"],["Color","color"],["Fancy color","fancy_color"],["Clarity","clarity"],["Cut","cut_grade"],["Polish","polish"],["Symmetry","symmetry"],["Fluorescence","fluorescence"],["Lab","lab"],["Certificate","certificate_no"]]],
    ["Measurements & proportions",[["Measurements","measurements"],["Depth %","depth_percent"],["Table %","table_percent"],["Ratio","ratio"],["Girdle","girdle"],["Culet","culet"],["Crown angle","crown_angle"],["Crown height","crown_height"],["Pavilion angle","pavilion_angle"],["Pavilion depth","pavilion_depth"]]],
    ["Inspection & availability",[["Shade","shade"],["Luster","luster"],["Eye clean","eye_clean"],["Hearts & arrows","hearts_arrows"],["Table inclusion","table_inclusion"],["Table black","table_black"],["Side inclusion","side_inclusion"],["Side black","side_black"],["Natural","natural"],["Extra facet","extra_facet"],["Location","location"],["Source status","availability_status"],["Internal status","internal_status"]]]
  ];
  const value=(stone:JsonRecord,key:string)=>{const fields=stone.supplier_fields&&typeof stone.supplier_fields==="object"?stone.supplier_fields as JsonRecord:{};const raw=stone[key]??fields[key]??fields[key.replaceAll("_"," ")]??"—";return /(percent|price|total|carat|depth|table|ratio|angle|height)/.test(key)&&Number.isFinite(Number(raw))?Number(raw).toFixed(2):String(raw||"—")};
  return <div className="dialog-backdrop" role="presentation"><section className="workflow-dialog diamond-compare-dialog" role="dialog" aria-modal="true" aria-label="Compare selected stones">
    <header><div><p className="eyebrow">Diamond Stock</p><h2>Compare stone detail</h2><p>Detailed side-by-side review of up to three stones.</p></div><button className="dialog-close" onClick={onClose} aria-label="Close comparison"><X/></button></header>
    {error&&<div className="error-banner">{error}</div>}
    <div className="diamond-compare-scroll"><div className="diamond-compare-columns" style={{gridTemplateColumns:`repeat(${details.length},minmax(280px,1fr))`}}>{details.map(stone=><article className="compare-stone-column" key={String(stone.id)}>
      <div className="compare-sticky-head"><h3>{String(stone.stock_number||stone.id)}</h3>{/^https?:/i.test(String(stone.image_url||""))?<img src={String(stone.image_url)} alt={`Stone ${String(stone.stock_number)}`}/>:<div className="compare-image-empty"><FileImage/></div>}
        <div className="compare-media-controls">{Boolean(stone.image_url)&&<a href={String(stone.image_url)} target="_blank" rel="noreferrer">Image</a>}{Boolean(stone.video_url)&&<a href={String(stone.video_url)} target="_blank" rel="noreferrer">Video</a>}{Boolean(stone.certificate_url)&&<a href={String(stone.certificate_url)} target="_blank" rel="noreferrer">Certificate</a>}</div>
        <div className="compare-price-band"><b>{value(stone,"discount_percent")}%</b><b>{value(stone,"price_per_carat")} $/CT</b></div></div>
      {groups.map(([title,fields])=><section className="compare-group" key={title}><h4>{title}</h4>{fields.map(([label,key])=><div key={key}><span>{label}</span><strong>{value(stone,key)}</strong></div>)}</section>)}
    </article>)}</div></div>
    <footer className="form-footer"><Button variant="bordered" onPress={onClose}>Close</Button><Button className="action-button action-create" isLoading={printing} startContent={<FileText size={17}/>} onPress={async()=>{setPrinting(true);setError("");try{await createDiamondComparisonPdf(details)}catch(reason){setError(reason instanceof Error?reason.message:"Could not create comparison PDF.")}finally{setPrinting(false)}}}>Print comparison PDF</Button></footer>
  </section></div>;
}

function DiamondQuotationDialog({ selected, token, busy, onClose, onMessage, onBusy }: {
  selected: JsonRecord; token: string | null; busy: boolean; onClose: () => void;
  onMessage: (message: string) => void; onBusy: (busy: boolean) => void;
}) {
  const [customers, setCustomers] = useState<JsonRecord[]>([]);
  const [customerId, setCustomerId] = useState("");
  const [currency, setCurrency] = useState<"USD" | "AED" | "EUR">("USD");
  const [detail, setDetail] = useState<JsonRecord | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    Promise.all([
      api<JsonRecord>("/v1/commercial/quotations/customers", {}, token),
      api<JsonRecord>(`/v1/diamond-stock/${Number(selected.id)}`, {}, token)
    ]).then(([context, stone]) => {
      if (!active) return;
      setCustomers(Array.isArray(context.customers) ? context.customers as JsonRecord[] : []);
      setDetail(stone);
    }).catch((reason) => active && setError(reason instanceof Error ? reason.message : "Could not prepare the stone quotation."));
    return () => { active = false; };
  }, [selected.id, token]);
  const generate = async () => {
    const customer = customers.find((entry) => String(entry.id) === customerId);
    if (!customer || !detail) return;
    onBusy(true); setError("");
    try {
      const name = await createDiamondCustomerQuote({ stone: detail, customer, currency, token });
      onMessage(`${name} was generated and downloaded.`); onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "The stone quotation could not be generated.");
    } finally { onBusy(false); }
  };
  return <div className="dialog-backdrop" role="presentation">
    <section className="workflow-dialog quotation-dialog" role="dialog" aria-modal="true" aria-label="Generate stone quotation">
      <header><div><p className="eyebrow gold">Commercial document</p><h2>Generate Quote</h2></div>
        <button className="dialog-close" onClick={onClose} aria-label="Close"><X /></button></header>
      <div className="workflow-body workflow-form">
        <p className="section-help">Select the customer receiving the quotation for stock {String(selected.stock_number || selected.id)}.</p>
        <label className="form-field"><span>Quotation customer <b>*</b></span>
          <select value={customerId} onChange={(event) => setCustomerId(event.target.value)}>
            <option value="">Select an active customer</option>
            {customers.map((customer) => <option key={String(customer.id)} value={String(customer.id)}>
              {String(customer.customer_code)} · {String(customer.customer_name)}
            </option>)}
          </select>
        </label>
        <label className="form-field"><span>Quotation currency <b>*</b></span>
          <select value={currency} onChange={(event) => setCurrency(event.target.value as "USD" | "AED" | "EUR")}>
            <option value="USD">USD - US Dollar ($)</option><option value="AED">AED - UAE Dirham</option><option value="EUR">EUR - Euro</option>
          </select>
        </label>
        {!detail && !error && <p>Loading stone and customer information…</p>}
        {error && <div className="error-banner">{error}</div>}
        <footer className="form-footer"><Button variant="bordered" onPress={onClose}>Cancel</Button>
          <Button className="action-button action-create" startContent={<Receipt size={17} />}
            isDisabled={!customerId || !detail || busy} isLoading={busy} onPress={() => void generate()}>
            Generate quotation PDF
          </Button></footer>
      </div>
    </section>
  </div>;
}

function QuotationDialog({
  selectedRows,
  token,
  busy,
  onClose,
  onCreate
}: {
  selectedRows: JsonRecord[];
  token: string | null;
  busy: boolean;
  onClose: () => void;
  onCreate: (customerId: number, quantities: Record<string, number>, currency: "AED" | "USD" | "EUR") => Promise<void>;
}) {
  const [customerId, setCustomerId] = useState("");
  const [currency, setCurrency] = useState<"AED" | "USD" | "EUR">("AED");
  const [quantityByDesign, setQuantityByDesign] = useState<Record<string, number>>({});
  const [error, setError] = useState("");
  const [context, setContext] = useState<JsonRecord | null>(null);
  useEffect(() => {
    let active = true;
    api<JsonRecord>("/v1/commercial/quotations/editor-context", {
      method: "POST",
      body: JSON.stringify({ design_ids: selectedRows.map((row) => Number(row.id)) })
    }, token).then((result) => {
      if (!active) return;
      setContext(result);
      const existing = [...new Set(selectedRows.map((row) => Number(row.customer_id)).filter((id) => id > 0))];
      if (existing.length === 1) setCustomerId(String(existing[0]));
    }).catch((reason) => active && setError(reason instanceof Error ? reason.message : "Could not load quotation customers."));
    return () => { active = false; };
  }, [selectedRows, token]);
  const customers = Array.isArray(context?.customers) ? context.customers as JsonRecord[] : [];
  return (
    <div className="dialog-backdrop" role="presentation">
      <section className="workflow-dialog quotation-dialog" role="dialog" aria-modal="true" aria-label="Create quotation">
        <header><div><p className="eyebrow gold">Commercial document</p><h2>Create quotation</h2></div><button className="dialog-close" onClick={onClose}><X /></button></header>
        <div className="workflow-body workflow-form">
          <p className="section-help">Select the customer receiving this quotation. The selected jewelry records remain unchanged.</p>
          <label className="form-field"><span>Quotation customer <b>*</b></span>
            <select value={customerId} onChange={(event) => setCustomerId(event.target.value)}>
              <option value="">Select an active customer</option>
              {customers.map((customer) => <option key={String(customer.id)} value={String(customer.id)}>{String(customer.customer_code)} · {String(customer.customer_name)}</option>)}
            </select>
          </label>
          <label className="form-field"><span>Quotation currency <b>*</b></span>
            <select value={currency} onChange={(event) => setCurrency(event.target.value as "AED" | "USD" | "EUR")}>
              <option value="AED">AED - UAE Dirham</option><option value="USD">USD - US Dollar ($)</option><option value="EUR">EUR - Euro</option>
            </select>
          </label>
          <div className="quotation-designs">
            {selectedRows.map((row) => {
              const key = String(row.id);
              return <div key={key}><span>{String(row.design_id || `Design ${key}`)}</span><label>Quantity <input type="number" min="1" step="1" value={quantityByDesign[key] || 1} onChange={(event) => setQuantityByDesign({ ...quantityByDesign, [key]: Math.max(1, Number(event.target.value || 1)) })} /></label></div>;
            })}
          </div>
          {error && <div className="error-banner">{error}</div>}
          <footer className="form-footer"><Button variant="bordered" onPress={onClose}>Cancel</Button><Button color="primary" isDisabled={!customerId || busy} isLoading={busy} onPress={() => void onCreate(Number(customerId), quantityByDesign, currency)}>Generate quotation PDF</Button></footer>
        </div>
      </section>
    </div>
  );
}

export function RecordDetailsDialog({
  moduleKey,
  selected,
  token,
  onClose,
  onChanged
}: {
  moduleKey: string;
  selected: JsonRecord;
  token: string | null;
  onClose: () => void;
  onChanged?: () => void | Promise<unknown>;
}) {
  const [detail, setDetail] = useState<unknown>(selected);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);
  const [activeTab, setActiveTab] = useState("");
  useEffect(() => {
    const id = String(selected.id ?? "");
    const path = detailPath(moduleKey, id, selected);
    if (!path) { setBusy(false); return; }
    void api<unknown>(path, {}, token)
      .then(setDetail)
      .catch((reason: Error) => setError(reason.message))
      .finally(() => setBusy(false));
  }, [moduleKey, selected, token]);
  const tabs = useMemo(() => buildDetailTabs(moduleKey, detail), [moduleKey, detail]);
  useEffect(() => {
    if (tabs.length && !tabs.some((tab) => tab.key === activeTab)) setActiveTab(tabs[0].key);
  }, [tabs, activeTab]);
  const currentTab = tabs.find((tab) => tab.key === activeTab) || tabs[0];
  const previewMedia = moduleKey === "designs" && detail && typeof detail === "object"
    ? selectDisplayMedia(Array.isArray((detail as JsonRecord).media) ? (detail as JsonRecord).media as JsonRecord[] : [])
    : null;
  return (
    <div className="dialog-backdrop" role="presentation">
      <section className="workflow-dialog detail-dialog" role="dialog" aria-modal="true" aria-label="Record details">
        <header>
          <div><p className="eyebrow">Saved record</p><h2>{recordLabel(selected, 0)}</h2></div>
          <button className="dialog-close" onClick={onClose} aria-label="Close"><X /></button>
        </header>
        <div className="workflow-body">
          {busy && <p>Loading complete record…</p>}
          {error && <div className="error-banner">{error}</div>}
          {!busy && !error && (
            moduleKey === "diamondStock" ? <DiamondStockDetail value={detail} token={token} onChanged={onChanged} /> : <>
              {moduleKey === "designs" && (
                <section className="design-photo-preview">
                  <div>
                    <p className="eyebrow">Display photo</p>
                    <h3>{previewMedia ? String(previewMedia.media_type || "Design preview") : "No preview picture saved"}</h3>
                    <small>Priority: Hero Image, Render Picture, Final Photo, then Reference Photo. The newest file in the highest-priority category is used.</small>
                  </div>
                  <div className="design-photo-frame">
                    {previewMedia ? <StoredImage mediaId={Number(previewMedia.id)} token={token} alt={String(previewMedia.original_name || "Design preview")} /> :
                      <div className="preview-placeholder">Add a supported design image through Manage Files.</div>}
                  </div>
                </section>
              )}
              <nav className="record-tabs" aria-label="Record sections">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={tab.key === currentTab?.key ? "active" : ""}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                    {Array.isArray(tab.value) && <span>{tab.value.length}</span>}
                  </button>
                ))}
              </nav>
              <section className="record-tab-panel" aria-live="polite">
                {currentTab?.key === "versions" && moduleKey === "designs"
                  ? <DesignVersionHistory versions={Array.isArray(currentTab.value) ? currentTab.value as JsonRecord[] : []} designId={Number(selected.id)} token={token} />
                  : currentTab?.key === "versions" && moduleKey === "jobCards"
                    ? <JobCardVersionHistory versions={Array.isArray(currentTab.value) ? currentTab.value as JsonRecord[] : []} token={token} />
                  : currentTab && <DetailValue value={currentTab.value} />}
              </section>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function JobCardVersionHistory({ versions, token }: { versions: JsonRecord[]; token: string | null }) {
  const [viewing, setViewing] = useState<{ version: JsonRecord; detail: JsonRecord } | null>(null);
  const [message, setMessage] = useState("");
  const openVersion = (version: JsonRecord) => {
    try {
      const detail = typeof version.snapshot_json === "string" ? JSON.parse(String(version.snapshot_json)) : version.snapshot_json;
      setViewing({ version, detail: detail as JsonRecord });
    } catch { setMessage("This historical Job Card snapshot could not be opened."); }
  };
  return <section className="design-version-history"><div className="version-history-heading"><div><h3>Previous versions</h3><p>Select a revision to view every saved Job Card detail in read-only mode.</p></div><span>{versions.length} versions</span></div>
    {message && <div className="error-banner">{message}</div>}
    <div className="version-card-list">{versions.map((version) => <article key={String(version.id)}><div><strong>Revision {String(version.revision_number)}</strong><span>{String(version.action || "saved")} · {String(version.changed_at || "")}</span><small>{String(version.changed_by_name || "")}</small></div><Button isIconOnly className="action-button action-view" title={`View revision ${String(version.revision_number)}`} aria-label={`View revision ${String(version.revision_number)}`} onPress={() => openVersion(version)}><Eye size={16} /></Button></article>)}</div>
    {viewing && <div className="dialog-backdrop nested-dialog" role="presentation"><section className="workflow-dialog design-edit-dialog historical-design-dialog" role="dialog" aria-modal="true" aria-label={`Job Card revision ${String(viewing.version.revision_number)}`}><header><div><p className="eyebrow">Historical snapshot · read only</p><h2>{String((viewing.detail.job_card as JsonRecord)?.job_card_number || "Job Card")} · Revision {String(viewing.version.revision_number)}</h2></div><button className="dialog-close" onClick={() => setViewing(null)} aria-label="Close"><X /></button></header><div className="workflow-body"><JobCardForm token={token} onSaved={()=>undefined} initialDetail={viewing.detail} editId={String((viewing.detail.job_card as JsonRecord)?.id||"")} readOnly /></div></section></div>}
  </section>;
}

function DesignVersionHistory({ versions, designId, token }: { versions: JsonRecord[]; designId: number; token: string | null }) {
  const [viewing, setViewing] = useState<{ version: JsonRecord; detail: JsonRecord } | null>(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const openVersion = (version: JsonRecord) => {
    try {
      const detail = typeof version.snapshot_json === "string" ? JSON.parse(String(version.snapshot_json)) : version.snapshot_json;
      setViewing({ version, detail: detail as JsonRecord });
    } catch { setMessage("This historical snapshot could not be opened."); }
  };
  const historicalRow = viewing ? { ...((viewing.detail.design || {}) as JsonRecord), id: designId, version_number: Number(viewing.version.version_number) } : null;
  return <section className="design-version-history"><div className="version-history-heading"><div><h3>Previous versions</h3><p>Select a version to view its complete saved design in read-only mode or generate a quotation from that exact snapshot.</p></div><span>{versions.length} versions</span></div>
    {message && <div className="success-banner">{message}</div>}
    <div className="version-card-list">{versions.map((version) => <article key={String(version.id)}><div><strong>Version {String(version.version_number)}</strong><span>{String(version.action || "saved")} · {String(version.changed_at || "")}</span><small>{String(version.changed_by_name || "")}</small></div><div className="row-actions"><Button isIconOnly className="action-button action-view" title={`View version ${String(version.version_number)}`} aria-label={`View version ${String(version.version_number)}`} onPress={() => openVersion(version)}><Eye size={16} /></Button></div></article>)}</div>
    {viewing && <div className="dialog-backdrop nested-dialog" role="presentation"><section className="workflow-dialog design-edit-dialog historical-design-dialog" role="dialog" aria-modal="true" aria-label={`Jewelry Design version ${String(viewing.version.version_number)}`}><header><div><p className="eyebrow">Historical snapshot · read only</p><h2>{String((viewing.detail.design as JsonRecord)?.design_id || "Jewelry Design")} · Version {String(viewing.version.version_number)}</h2></div><div className="historical-header-actions"><Button isIconOnly className="action-button action-create" title="Generate quotation for this version" aria-label="Generate quotation for this version" onPress={() => setQuoteOpen(true)}><Receipt size={17} /></Button><button className="dialog-close" onClick={() => setViewing(null)} aria-label="Close"><X /></button></div></header><div className="workflow-body"><DesignForm token={token} onSaved={() => undefined} initialDetail={viewing.detail} editId={String(designId)} readOnly /></div></section></div>}
    {quoteOpen && historicalRow && <QuotationDialog selectedRows={[historicalRow]} token={token} busy={busy} onClose={() => setQuoteOpen(false)} onCreate={async (customerId, quantities, currency) => { setBusy(true); try { const name = await createOperationalPdf({ kind: "quotation", selected: historicalRow, selectedRows: [{ ...historicalRow, quotation_quantity: quantities[String(designId)] || 1 }], quotationCustomerId: customerId, quotationCurrency: currency, token }); setMessage(`${name} was generated from version ${String(viewing?.version.version_number)}.`); setQuoteOpen(false); } finally { setBusy(false); } }} />}
  </section>;
}

function DiamondStockDetail({ value, token, onChanged }: { value: unknown; token: string | null; onChanged?: () => void | Promise<unknown> }) {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [quoteMessage, setQuoteMessage] = useState("");
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [lifecycleAction, setLifecycleAction] = useState<"Available" | "Reserved" | "Sold">("Reserved");
  const [mediaMode, setMediaMode] = useState<"image" | "video" | "certificate">("image");
  const response = value && typeof value === "object" ? value as JsonRecord : {};
  const item = response.item && typeof response.item === "object" ? response.item as JsonRecord : response;
  const lifecycleHistory = Array.isArray(response.lifecycle_history) ? response.lifecycle_history as JsonRecord[] : [];
  const sourceFields = response.supplier_fields && typeof response.supplier_fields === "object"
    ? response.supplier_fields as JsonRecord : {};
  const field = (key: string) => item[key] ?? "—";
  const present = (value: unknown) => value != null && String(value).trim() !== "" && String(value) !== "—";
  const decimal = (value: unknown) => {
    const numeric = Number(value); return Number.isFinite(numeric) ? numeric.toFixed(2) : "—";
  };
  const normalizedSource = new Map(Object.entries(sourceFields).map(([key, entry]) =>
    [key.toLowerCase().replace(/[^a-z0-9]+/g, ""), entry]));
  const source = (...keys: string[]) => {
    for (const key of keys) {
      const entry = normalizedSource.get(key.toLowerCase().replace(/[^a-z0-9]+/g, ""));
      if (present(entry)) return entry;
    }
    return undefined;
  };
  const optionalRow = (label: string, value: unknown): [string, unknown] | null => present(value) ? [label, value] : null;
  const detailRows: Array<[string, string]> = [
    ["Cut", "cut_grade"], ["Polish", "polish"], ["Symmetry", "symmetry"],
    ["Fluorescence", "fluorescence"], ["Shade", "shade"], ["Eye clean", "eye_clean"]
  ];
  const leftSpecifications = [
    optionalRow("Measurement", field("measurements")), optionalRow("Table (%)", decimal(field("table_percent"))),
    optionalRow("Depth (%)", decimal(field("depth_percent"))), optionalRow("Ratio", source("ratio")),
    optionalRow("Girdle (%)", source("girdle", "girdlepercent", "girdlepct")),
    optionalRow("PV. angle", source("pavangle", "pavilionangle", "pvangle")),
    optionalRow("PV. height", source("pavheight", "pavilionheight", "pvheight")),
    optionalRow("CR. angle", source("crownangle", "crangle")), optionalRow("CR. height", source("crownheight", "crheight")),
    optionalRow("Culet", source("culet")), optionalRow("Star length", source("starlength")),
    optionalRow("Lower half", source("lowerhalf")), optionalRow("Extra facet", source("extrafacet"))
  ].filter((row): row is [string, unknown] => Boolean(row));
  const rightSpecifications = [
    optionalRow("Location", field("location")), optionalRow("Table inc", source("tableinc", "tableinclusion")),
    optionalRow("Table side inc", source("tablesideinc", "tablesideinclusion")),
    optionalRow("Table side black", source("tablesideblack")), optionalRow("Table black", source("tableblack")),
    optionalRow("Table open", source("tableopen")), optionalRow("Side inc", source("sideinc", "sideinclusion")),
    optionalRow("Side black", source("sideblack")), optionalRow("Side open", source("sideopen")),
    optionalRow("H&A", field("hearts_arrows")), optionalRow("Luster", field("luster")), optionalRow("Natural", source("natural"))
  ].filter((row): row is [string, unknown] => Boolean(row));
  const comments = [
    optionalRow("NGS comment", source("ngscomment", "ngscomments")),
    optionalRow("Key to symbols", source("keytosymbols", "keytosymbol", "keysymbols")),
    optionalRow("Report comm.", source("reportcomment", "reportcomments", "reportcomm"))
  ].filter((row): row is [string, unknown] => Boolean(row));
  const imageUrl = /^https?:\/\//i.test(String(item.image_url || "")) ? String(item.image_url) : "";
  const videoUrl = /^https?:\/\//i.test(String(item.video_url || "")) ? String(item.video_url) : "";
  const certificateUrl = /^https?:\/\//i.test(String(item.certificate_url || "")) ? String(item.certificate_url) : "";
  useEffect(() => {
    if (mediaMode === "image" && !imageUrl) setMediaMode(videoUrl ? "video" : "certificate");
  }, [imageUrl, videoUrl, mediaMode]);
  return <div className="diamond-detail-view">
    <div className="diamond-detail-title"><img src="/octadiam-icon.png" alt="OctaDiam" /><h2>Stone Detail</h2>
      <span className={`diamond-internal-status status-${String(item.internal_status || "Available").toLowerCase().replaceAll(" ","-")}`}>Internal: {String(item.internal_status || "Available")}</span>
      {String(item.internal_status || "Available") === "Available" && <>
        <Button isIconOnly className="action-button action-edit" title="Reserve stone" aria-label="Reserve stone" onPress={() => { setLifecycleAction("Reserved"); setLifecycleOpen(true); }}><LockKeyhole /></Button>
        <Button isIconOnly className="action-button action-create" title="Sell stone" aria-label="Sell stone" onPress={() => { setLifecycleAction("Sold"); setLifecycleOpen(true); }}><BadgeDollarSign /></Button>
      </>}
      {String(item.internal_status) === "Reserved" && <>
        <Button isIconOnly className="action-button action-refresh" title="Return reserved stone to internally Available" aria-label="Return stone to available" onPress={() => { setLifecycleAction("Available"); setLifecycleOpen(true); }}><RotateCcw /></Button>
        <Button isIconOnly className="action-button action-create" title="Mark reserved stone as sold" aria-label="Mark stone sold" onPress={() => { setLifecycleAction("Sold"); setLifecycleOpen(true); }}><BadgeDollarSign /></Button>
      </>}
      {String(item.internal_status) === "Sold" && <Button isIconOnly className="action-button action-refresh" title="Return sold stone to internally Available" aria-label="Return sold stone to available" onPress={() => { setLifecycleAction("Available"); setLifecycleOpen(true); }}><RotateCcw /></Button>}
      <Button isIconOnly className="action-button action-create diamond-detail-quote" aria-label="Generate Quote" title="Generate Quote"
        onPress={() => setQuoteOpen(true)}><Receipt size={18} /></Button></div>
    {quoteMessage && <div className="success-banner">{quoteMessage}</div>}
    <div className="diamond-detail-layout"><div className="diamond-detail-information">
    <section className="diamond-detail-summary">
      <div><strong>{String(field("shape"))}</strong><span>{String(field("carat"))} CT</span></div>
      <div><strong>{String(field("stock_number"))}</strong><span>Stock number</span></div>
      <div><strong>{[field("color"), field("clarity")].filter((entry) => entry !== "—").join(" · ") || "—"}</strong><span>Color · clarity</span></div>
      <div><strong>(${decimal(field("octadiam_price_per_carat"))} - {decimal(field("octadiam_desc"))}%)</strong><span>Price/CT$-Desc%</span></div>
      <div><strong>${decimal(field("octadiam_total_price"))}</strong><span>Stone Price</span></div>
      <div><strong>{String(field("lab"))}</strong><span>{String(field("certificate_no"))}</span></div>
    </section>
    {String(item.internal_status || "Available") !== "Available" && <section className="diamond-detail-strip diamond-lifecycle-context">
      <div><span>Customer</span><strong>{String(item.customer_name || item.reserved_sold_for || "—")}</strong></div>
      <div><span>Linked Jewelry Design</span><strong>{String(item.linked_design_label || item.linked_design_id || "—")}</strong></div>
    </section>}
    <section className="diamond-detail-strip">
      {detailRows.map(([label, key]) => <div key={key}><span>{label}</span><strong>{String(field(key))}</strong></div>)}
    </section>
    {(leftSpecifications.length > 0 || rightSpecifications.length > 0) && <section className="diamond-detail-specs">
      <div>{leftSpecifications.map(([label, entry]) => <div key={label}><span>{label}</span><strong>{String(entry)}</strong></div>)}</div>
      <div>{rightSpecifications.map(([label, entry]) => <div key={label}><span>{label}</span><strong>{String(entry)}</strong></div>)}</div>
    </section>}
    {comments.length > 0 && <section className="diamond-detail-comments">
      {comments.map(([label, entry]) => <div key={label}><span>{label}</span><strong>{String(entry)}</strong></div>)}
    </section>}
    </div>
    <aside className="diamond-detail-media">
      <div className="diamond-media-stage">
        {mediaMode === "image" && (imageUrl ? <img src={imageUrl} alt={`Stone ${String(field("stock_number"))}`} /> : <div className="preview-placeholder">No picture available</div>)}
        {mediaMode === "video" && (videoUrl ? (/\.(mp4|webm|ogg)(\?|$)/i.test(videoUrl)
          ? <video src={videoUrl} controls autoPlay playsInline preload="metadata" />
          : <iframe src={videoUrl} title={`Stone video ${String(field("stock_number"))}`} allow="autoplay; fullscreen" />)
          : <div className="preview-placeholder">No video available</div>)}
        {mediaMode === "certificate" && (certificateUrl ? <iframe src={certificateUrl} title={`Certificate ${String(field("certificate_no"))}`} /> : <div className="preview-placeholder">Certificate unavailable or restricted</div>)}
      </div>
      <div className="diamond-media-tabs">
        <button type="button" className={mediaMode === "image" ? "active" : ""} title="Picture" aria-label="Picture" disabled={!imageUrl} onClick={() => setMediaMode("image")}><FileImage /></button>
        <button type="button" className={mediaMode === "video" ? "active" : ""} title="Video" aria-label="Video" disabled={!videoUrl} onClick={() => setMediaMode("video")}><Video /></button>
        <button type="button" className={mediaMode === "certificate" ? "active" : ""} title="Certificate" aria-label="Certificate" disabled={!certificateUrl} onClick={() => setMediaMode("certificate")}><BadgeCheck /></button>
        {(mediaMode === "image" ? imageUrl : mediaMode === "video" ? videoUrl : certificateUrl) && <a href={mediaMode === "image" ? imageUrl : mediaMode === "video" ? videoUrl : certificateUrl} target="_blank" rel="noreferrer" title="Open externally" aria-label="Open externally"><Eye /></a>}
      </div>
    </aside></div>
    <section className="diamond-lifecycle-history"><h3><History size={18} /> Internal lifecycle history</h3>
      <p className="section-help">Supplier availability remains separate and is recorded with each internal transition.</p>
      {lifecycleHistory.length ? <div className="detail-table-wrap"><table className="detail-table"><thead><tr><th>Changed</th><th>Transition</th><th>Source status</th><th>For</th><th>Changed by</th><th>Notes</th></tr></thead>
        <tbody>{lifecycleHistory.map((entry) => <tr key={String(entry.id)}><td>{String(entry.changed_at || "—")}</td><td>{String(entry.from_status)} → {String(entry.to_status)}</td><td>{String(entry.supplier_availability_status || "—")}</td><td>{String(entry.party || "—")}</td><td>{String(entry.changed_by_name || "—")}</td><td>{String(entry.notes || "—")}</td></tr>)}</tbody></table></div> : <p className="muted">No internal reservation or sale history for this stone.</p>}
    </section>
    {quoteOpen && <DiamondQuotationDialog selected={item} token={token} busy={quoteBusy}
      onClose={() => setQuoteOpen(false)} onMessage={setQuoteMessage} onBusy={setQuoteBusy} />}
    {lifecycleOpen && <DiamondLifecycleDialog item={item} action={lifecycleAction} token={token}
      onClose={() => setLifecycleOpen(false)} onChanged={async () => { setLifecycleOpen(false); await onChanged?.(); }} />}
  </div>;
}

function DiamondLifecycleDialog({ item, action, token, onClose, onChanged }: {
  item: JsonRecord; action: "Available" | "Reserved" | "Sold"; token: string | null;
  onClose: () => void; onChanged: () => void | Promise<unknown>;
}) {
  const [context, setContext] = useState<JsonRecord>({});
  const [customerId, setCustomerId] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { void api<JsonRecord>("/v1/diamond-stock/editor-context", {}, token).then(setContext).catch((reason: Error) => setError(reason.message)); }, [token]);
  const customers = Array.isArray(context.customers) ? context.customers as JsonRecord[] : [];
  const save = async () => {
    setBusy(true); setError("");
    try {
      const selectedCustomer = customers.find((entry) => String(entry.id) === customerId);
      await api(`/v1/diamond-stock/${Number(item.id)}/internal-status`, { method: "POST", body: JSON.stringify({
        status: action,
        customer_id: customerId ? Number(customerId) : null,
        supplier_id: null,
        party_name: selectedCustomer ? String(selectedCustomer.customer_name || "") : "",
        design_id: item.linked_design_id ? Number(item.linked_design_id) : null,
        notes
      }) }, token);
      await onChanged();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "The internal stone status could not be changed."); }
    finally { setBusy(false); }
  };
  const requiresParty = action !== "Available";
  const validParty = !requiresParty || Boolean(customerId);
  return <div className="dialog-backdrop nested-dialog" role="presentation"><section className="workflow-dialog compact-dialog" role="dialog" aria-modal="true" aria-label={`${action} stone`}>
    <header><div><p className="eyebrow">Internal stone lifecycle</p><h2>{action === "Available" ? "Return stone to Available" : `${action} stone`}</h2></div><button className="dialog-close" onClick={onClose} aria-label="Close"><X /></button></header>
    <div className="workflow-body workflow-form"><p className="section-help">Source status “{String(item.availability_status || "—")}” remains unchanged. This action changes only OctaDiam’s internal status.</p>
      {requiresParty && <label className="form-field"><span>Customer *</span><select value={customerId} onChange={(event) => setCustomerId(event.target.value)}><option value="">Select customer</option>{customers.map((entry) => <option key={String(entry.id)} value={String(entry.id)}>{String(entry.customer_code)} · {String(entry.customer_name)}</option>)}</select></label>}
      {Boolean(item.linked_design_id || item.linked_design_label) && <p className="section-help"><strong>Linked Jewelry Design:</strong> {String(item.linked_design_label || item.linked_design_id)} (information only)</p>}
      <label className="form-field"><span>Notes</span><textarea value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
      {error && <div className="error-banner">{error}</div>}<footer className="form-footer"><Button variant="bordered" onPress={onClose}>Cancel</Button><Button className={`action-button ${action === "Available" ? "action-refresh" : "action-create"}`} isDisabled={!validParty || busy} isLoading={busy} onPress={() => void save()}>Confirm {action}</Button></footer>
    </div></section></div>;
}

function selectDisplayMedia(media: JsonRecord[]) {
  for (const kind of ["Hero Image", "Render Picture", "Final Photo", "Reference Photo"]) {
    const matching = media.filter((item) => !item.stone_line_uid && String(item.media_type) === kind);
    if (matching.length) return matching.sort((a, b) => Number(b.id || 0) - Number(a.id || 0))[0];
  }
  return null;
}

function EditRecordDialog({
  moduleKey,
  selected,
  token,
  onClose,
  onSaved
}: {
  moduleKey: string;
  selected: JsonRecord;
  token: string | null;
  onClose: () => void;
  onSaved: () => void | Promise<unknown>;
}) {
  const [detail, setDetail] = useState<JsonRecord | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);
  const id = String(selected.id ?? "");
  useEffect(() => {
    const path = detailPath(moduleKey, id, selected);
    if (!path) {
      setError("This record type does not support editing.");
      setBusy(false);
      return;
    }
    void api<JsonRecord>(path, {}, token)
      .then(setDetail)
      .catch((reason: Error) => setError(reason.message))
      .finally(() => setBusy(false));
  }, [id, moduleKey, selected, token]);
  return (
    <div className="dialog-backdrop" role="presentation">
      <section className={`workflow-dialog ${["designs", "jobCards"].includes(moduleKey) ? "design-edit-dialog" : ""}`} role="dialog" aria-modal="true" aria-label="Edit record">
        <header>
          <div>
            <p className="eyebrow">Version-controlled update</p>
            <h2>Edit {recordLabel(selected, 0)}</h2>
          </div>
          <button className="dialog-close" onClick={onClose} aria-label="Close"><X /></button>
        </header>
        <div className="workflow-body">
          {busy && <p>Loading the latest saved version…</p>}
          {error && <div className="error-banner">{error}</div>}
          {!busy && detail && (moduleKey === "designs" ? (
            <DesignForm token={token} initialDetail={detail} editId={id} onSaved={onSaved} />
          ) : moduleKey === "jobCards" ? (
            <JobCardForm token={token} initialDetail={detail} editId={id} onSaved={onSaved} />
          ) : (
            <EditRecordForm
              key={`${moduleKey}-${id}-${String(recordForEdit(moduleKey, detail).updated_at ?? recordForEdit(moduleKey, detail).updatedAt ?? "")}`}
              moduleKey={moduleKey}
              selected={selected}
              detail={detail}
              token={token}
              onSaved={onSaved}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function EditRecordForm({
  moduleKey,
  selected,
  detail,
  token,
  onSaved
}: {
  moduleKey: string;
  selected: JsonRecord;
  detail: JsonRecord;
  token: string | null;
  onSaved: () => void | Promise<unknown>;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState("record");
  const [relatedRows, setRelatedRows] = useState<Record<string, JsonRecord[]>>({
    stones: Array.isArray(detail.stones) ? detail.stones as JsonRecord[] : [],
    manufacturing: Array.isArray(detail.manufacturing) ? detail.manufacturing as JsonRecord[] : [],
    stages: Array.isArray(detail.stages) ? detail.stages as JsonRecord[] : [],
    repairs: Array.isArray(detail.repair_operations) ? detail.repair_operations as JsonRecord[] : []
  });
  const existingMedia = Array.isArray(detail.media) ? detail.media as JsonRecord[] : [];
  const [pendingMedia, setPendingMedia] = useState<PendingMedia[]>([]);
  const [deletedMediaIds, setDeletedMediaIds] = useState<number[]>([]);
  const record: JsonRecord = {
    ...recordForEdit(moduleKey, detail),
    ...(moduleKey === "inventory" ? { inventory_type: selected.inventory_type } : {})
  };
  const fields = editableFields(moduleKey, record);
  const [editCategory, setEditCategory] = useState(String(record.category || ""));
  const [editChoices, setEditChoices] = useState<Record<string, string[]>>({});
  useEffect(() => {
    const categories = [
      "jewelry_category", "designer_name", "metal_type", "supply_method",
      "stone_type", "stone_shape", "gem_lab"
    ];
    void api<Record<string, string[]>>(
      `/v1/reference/lookup-bundle?categories=${categories.join(",")}`, {}, token
    ).then((bundle) => setEditChoices((current) => ({
      ...current,
      category: bundle.jewelry_category || [],
      item_type: bundle.jewelry_category || [],
      designer_name: bundle.designer_name || [],
      metal_type: bundle.metal_type || [],
      supply_method: bundle.supply_method || [],
      stone_type: bundle.stone_type || [],
      stone_shape: bundle.stone_shape || [],
      lab: bundle.gem_lab || [],
      metal_form: ["Bar", "Grain", "Sheet", "Wire", "Scrap", "Finished component"]
    }))).catch((reason: Error) => setError(reason.message));
    if (moduleKey === "jobCards") {
      void api<JsonRecord>("/v1/operations/job-cards/editor-context", {}, token).then((context) => {
        const values = (key: string) => (context[key] as JsonRecord[] || [])
          .map((row) => String(row.value || "")).filter(Boolean);
        const designs = context.designs as JsonRecord[] || [];
        setEditChoices((current) => ({
          ...current,
          item_type: [...new Set(designs.flatMap((row) => [String(row.category || ""), String(row.sub_category || "")]).filter(Boolean))],
          metal_type: [...new Set(designs.map((row) => String(row.metal_type || "")).filter(Boolean))],
          factory_workshop: values("job_workshop"),
          stage_name: values("job_stage"),
          operator_name: values("job_operator"),
          issue_type: values("job_issue_type"),
          return_type: values("job_issue_type"),
          stage_status: ["Pending", "In Progress", "Completed", "On Hold", "Cancelled"]
        }));
      }).catch((reason: Error) => setError(reason.message));
    }
  }, [moduleKey, token]);
  useEffect(() => {
    if (!editCategory) return;
    void api<JsonRecord[]>(
      `/v1/reference/jewelry-subcategories?category=${encodeURIComponent(editCategory)}`, {}, token
    ).then((rows) => setEditChoices((current) => ({
      ...current,
      sub_category: rows.map((row) => String(row.value || "")).filter(Boolean)
    }))).catch((reason: Error) => setError(reason.message));
    const sizeCategory = designSizeCategory(editCategory);
    if (!sizeCategory) {
      setEditChoices((current) => ({ ...current, size_specification: ["Custom size"] }));
      return;
    }
    void api<JsonRecord[]>(
      `/v1/reference/lookup-values?category=${encodeURIComponent(sizeCategory)}`, {}, token
    ).then((rows) => setEditChoices((current) => ({
      ...current,
      size_specification: [...rows.map((row) => String(row.value || "")).filter(Boolean), "Custom size"]
    }))).catch((reason: Error) => setError(reason.message));
  }, [editCategory, token]);
  const detailTabs = useMemo(
    () => buildDetailTabs(moduleKey, detail).filter((tab) => tab.key !== "summary"),
    [moduleKey, detail]
  );
  return (
    <form className="workflow-form" onSubmit={async (event) => {
      event.preventDefault();
      setBusy(true);
      setError("");
      const values = new FormData(event.currentTarget);
      const changes: JsonRecord = {};
      for (const field of fields) {
        const raw = values.get(field.name);
        changes[field.name] = field.type === "number" ? number(raw) : text(raw);
      }
      try {
        await saveEditedRecord({
          moduleKey, selected, detail, changes, relatedRows, token,
          newMedia: await pendingMediaPayload(pendingMedia),
          deletedMediaIds
        });
        if (moduleKey === "designs") await queryClient.invalidateQueries({ queryKey: ["entity", "diamondStock"] });
        await onSaved();
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : "Could not update this record.");
      } finally {
        setBusy(false);
      }
    }}>
      <div className="record-preview">
        The current version is preserved in history when you save this update.
      </div>
      <nav className="record-tabs" aria-label="Editable record sections">
        <button type="button" className={activeTab === "record" ? "active" : ""} onClick={() => setActiveTab("record")}>Main details</button>
        {detailTabs.map((tab) => (
          <button type="button" key={tab.key} className={activeTab === tab.key ? "active" : ""} onClick={() => setActiveTab(tab.key)}>
            {tab.label}{Array.isArray(tab.value) && <span>{tab.value.length}</span>}
          </button>
        ))}
      </nav>
      <div className={`form-grid three ${activeTab === "record" ? "" : "hidden-panel"}`}>
          {fields.map((field) => {
            const options = field.options || editChoices[field.name] || [];
            const currentValue = String(record[field.name] ?? "");
            const effectiveOptions = currentValue && !options.includes(currentValue) ? [currentValue, ...options] : options;
            return effectiveOptions.length ? (
            <Choice
              key={field.name}
              name={field.name}
              label={field.label}
              options={effectiveOptions}
              required={field.required}
              value={currentValue}
              onChange={field.name === "category" ? setEditCategory : undefined}
            />
          ) : (
            <Field
              key={field.name}
              name={field.name}
              label={field.label}
              type={field.type}
              step={field.type === "number" ? "0.01" : field.step}
              required={field.required}
              defaultValue={String(record[field.name] ?? "")}
              multiline={field.multiline}
            />
          );})}
      </div>
      {activeTab !== "record" && (
        <section className="record-tab-panel edit-related-tab">
          {activeTab === "media" && (moduleKey === "designs" || moduleKey === "jobCards") ? (
            <div className="manage-files-section">
              <p className="section-help">Add several files at once or remove saved files. Changes are applied only when you save the new record version.</p>
              <ManageFiles
                title={moduleKey === "designs" ? "Manage Design Attachments" : "Manage Job Card Pictures & Files"}
                categories={moduleKey === "designs" ? DESIGN_MEDIA_CATEGORIES : ["Job Card Picture", "Workshop Document", "Customer Reference"]}
                existing={existingMedia}
                pending={pendingMedia}
                setPending={setPendingMedia}
                deletedIds={deletedMediaIds}
                setDeletedIds={setDeletedMediaIds}
              />
            </div>
          ) : editableRelatedColumns(moduleKey, activeTab) ? (
            <>
              <p className="section-help">Edit the related lines directly. Saving creates a new version while retaining the prior version in history.</p>
              <EditableRows
                rows={relatedRows[activeTab] || []}
                setRows={(update) => setRelatedRows((current) => ({
                  ...current,
                  [activeTab]: typeof update === "function"
                    ? update(current[activeTab] || [])
                    : update
                }))}
                columns={editableRelatedColumns(moduleKey, activeTab) || []}
                choices={moduleKey === "designs" && activeTab === "stones" ? {
                  stone_type: editChoices.stone_type || [],
                  stone_shape: editChoices.stone_shape || [],
                  lab: editChoices.lab || []
                } : moduleKey === "jobCards" && activeTab === "stages" ? {
                  factory_workshop: editChoices.factory_workshop || [],
                  stage_status: editChoices.stage_status || [],
                  stage_name: editChoices.stage_name || [],
                  operator_name: editChoices.operator_name || [],
                  issue_type: editChoices.issue_type || [],
                  return_type: editChoices.return_type || []
                } : {}}
                addLabel={`Add ${activeTab.replace(/s$/, "")} row`}
                toolbarActions={moduleKey === "designs" && activeTab === "stones" ? <DiamondStockPicker token={token}
                  existingProviderKeys={(relatedRows.stones || []).map((stone) => String(stone.diamond_provider_key || ""))}
                  onAdd={(rows, certificates) => {
                    const types = editChoices.stone_type || [], shapes = editChoices.stone_shape || [];
                    const normalized = rows.map((row) => ({ ...row,
                      stone_type: types.find((value) => value.toLowerCase() === "diamond") || types.find((value) => value.toLowerCase().includes("diamond")) || row.stone_type,
                      stone_shape: shapes.find((value) => value.toLowerCase() === String(row.stone_shape || "").toLowerCase()) || row.stone_shape
                    }));
                    setRelatedRows((current) => ({ ...current, stones: [...(current.stones || []), ...normalized] }));
                    setPendingMedia((current) => [...current, ...certificates]);
                  }} /> : undefined}
                rowActions={moduleKey === "designs" && activeTab === "stones" ? (stone, index) => <ManageFiles
                  iconOnly title={`Stone ${index + 1} certificate files`} categories={["Stone Certificate"]}
                  existing={existingMedia} pending={pendingMedia} setPending={setPendingMedia}
                  deletedIds={deletedMediaIds} setDeletedIds={setDeletedMediaIds}
                  stoneLineUid={String(stone.line_uid || "")} /> : undefined}
              />
            </>
          ) : (
            <>
              <p className="section-help">This section shows the complete saved record. Attachments and immutable version history are preserved when a new version is saved.</p>
              <DetailValue value={detailTabs.find((tab) => tab.key === activeTab)?.value ?? []} />
            </>
          )}
        </section>
      )}
      <FormFooter busy={busy} error={error} label="Save new version" />
    </form>
  );
}

function editableRelatedColumns(moduleKey: string, tab: string): EditableColumn[] | null {
  if (moduleKey === "designs" && tab === "stones") return [
    ["stone_type", "Stone type", "text"],
    ["stone_shape", "Shape", "text"],
    ["stone_size", "Size", "text"],
    ["color", "Colour", "text"],
    ["clarity", "Clarity", "text"],
    ["expected_quantity", "Expected qty", "integer"],
    ["expected_weight", "Expected wt. (ct)", "number"],
    ["actual_quantity", "Actual qty", "integer"],
    ["actual_weight", "Actual wt. (ct)", "number"],
    ["inhouse_quantity", "In-house qty", "integer"],
    ["inhouse_weight", "In-house wt. (ct)", "number"],
    ["expected_cost_per_carat", "Expected cost / ct", "number"],
    ["actual_cost_per_carat", "Actual cost / ct", "number"],
    ["lab", "Lab", "text"],
    ["certificate_id", "Certificate ID", "text"]
  ];
  if (moduleKey === "designs" && tab === "manufacturing") return [
    ["service_department", "Service Dept", "text"],
    ["service_type", "Service Type", "text"],
    ["unit", "Unit", "text"],
    ["expected_cost", "Expected cost (AED)", "number"],
    ["actual_cost", "Actual cost (AED)", "number"]
  ];
  if (moduleKey === "jobCards" && tab === "stages") return [
    ["factory_workshop", "Factory / workshop", "text"],
    ["stage_status", "Status", "text"],
    ["stage_name", "Stage", "text"],
    ["operator_name", "Operator", "text"],
    ["issue_type", "Issue type", "text"],
    ["issued_weight", "Issued g", "number"],
    ["time_out", "Time out", "text"],
    ["return_type", "Return type", "text"],
    ["returned_weight", "Return g", "number"],
    ["recovery_weight", "Recovery g", "number"],
    ["time_in", "Time in", "text"],
    ["notes", "Notes", "text"]
  ];
  if (moduleKey === "jobCards" && tab === "repairs") return [
    ["department", "Department", "text"],
    ["service", "Operation", "text"],
    ["unit", "Unit", "text"],
    ["quantity", "Qty", "number"],
    ["job_notes", "Job Card notes", "text"]
  ];
  if (moduleKey === "jobCards" && tab === "stones") return [
    ["stock_code", "Stock code", "text"],
    ["stone_type", "Stone", "text"],
    ["stone_shape", "Shape", "text"],
    ["stone_size", "Size", "text"],
    ["issued_weight", "Issue wt. (ct)", "number"],
    ["issued_quantity", "Issue qty", "integer"],
    ["returned_weight", "Returned wt. (ct)", "number"],
    ["returned_quantity", "Returned qty", "integer"],
    ["notes", "Notes", "text"]
  ];
  return null;
}

type EditField = {
  name: string;
  label: string;
  type?: string;
  step?: string;
  required?: boolean;
  multiline?: boolean;
  options?: string[];
};

function editableFields(moduleKey: string, record: JsonRecord): EditField[] {
  if (moduleKey === "designs") return [
    { name: "designer_design_id", label: "Designer’s initial Design ID" },
    { name: "entry_date", label: "Entry date", type: "date", required: true },
    { name: "category", label: "Category", required: true },
    { name: "sub_category", label: "Sub category", required: true },
    { name: "designer_name", label: "Designer / prepared by (optional)" },
    { name: "metal_type", label: "Metal type / karat", required: true },
    { name: "metal_colour", label: "Metal colour", options: ["Yellow", "White", "Rose", "Black"], required: true },
    { name: "size_specification", label: "Size specification" },
    { name: "custom_size_specification", label: "Custom size specification" },
    { name: "overall_length_mm", label: "Overall length (mm)", type: "number", step: "0.01" },
    { name: "overall_width_mm", label: "Overall width (mm)", type: "number", step: "0.01" },
    { name: "overall_height_mm", label: "Overall height (mm)", type: "number", step: "0.01" },
    { name: "expected_final_weight", label: "Expected final wt. (g)", type: "number", step: "0.01" },
    { name: "expected_net_weight", label: "Expected net wt. (g)", type: "number", step: "0.01" },
    { name: "actual_final_weight", label: "Actual final wt. (g)", type: "number", step: "0.01" },
    { name: "actual_net_weight", label: "Actual net wt. (g)", type: "number", step: "0.01" },
    { name: "quoted_metal_price", label: "Expected metal price / g", type: "number", step: "0.01" },
    { name: "actual_metal_price", label: "Actual metal price / g", type: "number", step: "0.01" },
    { name: "quoted_loss_pct", label: "Metal loss %", type: "number", step: "0.01" },
    { name: "stone_markup_pct", label: "Stones markup %", type: "number", step: "0.01" },
    { name: "final_item_markup_pct", label: "Item markup %", type: "number", step: "0.01" },
    { name: "notes", label: "Notes", multiline: true }
  ];
  if (moduleKey === "jobCards") return [
    { name: "job_type", label: "Job type", required: true, options: ["New", "Repair", "Casting"] },
    { name: "status", label: "Status", required: true, options: ["Draft", "Released", "In Progress", "On Hold", "Completed", "Cancelled"] },
    { name: "priority", label: "Priority", required: true, options: ["Low", "Normal", "High", "Urgent"] },
    { name: "job_date", label: "Job date", type: "date", required: true },
    { name: "due_date", label: "Due date", type: "date" },
    { name: "item_type", label: "Item type" },
    { name: "metal_type", label: "Metal type" },
    { name: "customer_gold_weight", label: "Customer gold (g)", type: "number", step: "0.01" },
    { name: "expected_final_weight", label: "Expected final wt. (g)", type: "number", step: "0.01" },
    { name: "actual_final_weight", label: "Actual final wt. (g)", type: "number", step: "0.01" },
    { name: "expected_net_weight", label: "Expected net wt. (g)", type: "number", step: "0.01" },
    { name: "actual_final_net_weight", label: "Actual final net wt. (g)", type: "number", step: "0.01" },
    { name: "description", label: "Description / work requested", multiline: true },
    { name: "customer_stone_notes", label: "Customer stones / supplied material", multiline: true }
  ];
  if (moduleKey === "inventory") {
    const inventoryType = String(selectedInventoryType(record));
    const base: EditField[] = [
      { name: "entry_date", label: "Received date", type: "date", required: true },
      { name: "supply_method", label: "Supply method", required: true },
      { name: "receipt_reference", label: "Purchase / receipt reference" },
      { name: "location", label: "Location / bin" },
      { name: "notes", label: "Notes", multiline: true }
    ];
    if (inventoryType === "stone") return [...base,
      { name: "stone_type", label: "Stone type", required: true },
      { name: "stone_shape", label: "Shape", required: true },
      { name: "stone_size", label: "Size", required: true },
      { name: "received_weight", label: "Received weight (ct)", type: "number", step: "0.01" },
      { name: "received_quantity", label: "Received quantity", type: "number", step: "1" },
      { name: "cost_per_carat", label: "Average cost / ct", type: "number", step: "0.01" },
      { name: "lab", label: "Gem lab" },
      { name: "certificate_id", label: "Certificate ID" },
      { name: "reorder_quantity", label: "Reorder quantity", type: "number", step: "1" },
      { name: "reorder_weight", label: "Reorder weight", type: "number", step: "0.01" },
      { name: "status", label: "Status", options: ["Active", "Reserved", "Inactive"], required: true }
    ];
    if (inventoryType === "metal") return [...base,
      { name: "metal_type", label: "Metal type", required: true },
      { name: "purity", label: "Purity" },
      { name: "metal_form", label: "Metal form" },
      { name: "received_weight", label: "Received weight (g)", type: "number", step: "0.01" },
      { name: "cost_per_gram", label: "Cost / g", type: "number", step: "0.01" },
      { name: "reorder_weight", label: "Reorder weight", type: "number", step: "0.01" },
      { name: "status", label: "Status", options: ["Active", "Reserved", "Inactive"], required: true }
    ];
    return [...base,
      { name: "item_name", label: "Item name", required: true },
      { name: "category", label: "Category", required: true },
      { name: "sub_category", label: "Sub category" },
      { name: "received_quantity", label: "Received quantity", type: "number", step: "1" },
      { name: "received_gross_weight", label: "Received gross weight (g)", type: "number", step: "0.01" },
      { name: "metal_weight", label: "Metal weight / item (g)", type: "number", step: "0.01" },
      { name: "stone_weight", label: "Stone weight / item (g)", type: "number", step: "0.01" },
      { name: "cost_per_item", label: "Cost / item", type: "number", step: "0.01" },
      { name: "selling_price", label: "Selling price", type: "number", step: "0.01" },
      { name: "status", label: "Status", options: ["Finished", "Reserved", "Sold", "Inactive"], required: true }
    ];
  }
  if (moduleKey === "customers" || moduleKey === "suppliers") return [
    { name: "name", label: moduleKey === "customers" ? "Customer name" : "Supplier name", required: true },
    ...(moduleKey === "customers" ? [{ name: "customerType", label: "Customer type" }] : []),
    { name: "contactPerson", label: "Contact person" },
    { name: "phone", label: "Phone" },
    { name: "email", label: "Email", type: "email" },
    { name: "address", label: "Address", multiline: true },
    { name: "taxRegistration", label: "Tax registration" },
    { name: "paymentTerms", label: "Payment terms" },
    ...(moduleKey === "customers" ? [{ name: "creditLimit", label: "Credit limit", type: "number", step: "0.01" }] : []),
    { name: "status", label: "Status", options: ["Active", "Inactive"], required: true },
    { name: "notes", label: "Notes", multiline: true }
  ];
  if (moduleKey === "receivedInvoices") return [
    { name: "invoice_number", label: "Invoice number", required: true },
    { name: "invoice_date", label: "Invoice date", type: "date", required: true },
    { name: "supplier_id", label: "Supplier ID", type: "number", step: "1" },
    { name: "amount_minor", label: "Amount (minor units)", type: "number", step: "1", required: true },
    { name: "tax_minor", label: "Tax (minor units)", type: "number", step: "1" },
    { name: "status", label: "Status", options: ["Received", "Approved", "Paid", "Disputed"], required: true },
    { name: "description", label: "Description", multiline: true }
  ];
  return [];
}

function recordForEdit(moduleKey: string, detail: JsonRecord): JsonRecord {
  if (moduleKey === "designs") return detail.design as JsonRecord || detail;
  if (moduleKey === "jobCards") return detail.job_card as JsonRecord || detail;
  if (moduleKey === "receivedInvoices") return detail.invoice as JsonRecord || detail;
  return detail.item as JsonRecord || detail;
}

function selectedInventoryType(record: JsonRecord) {
  return String(record.inventory_type ?? "").toLowerCase();
}

async function saveEditedRecord({
  moduleKey,
  selected,
  detail,
  changes,
  relatedRows,
  token,
  newMedia,
  deletedMediaIds
}: {
  moduleKey: string;
  selected: JsonRecord;
  detail: JsonRecord;
  changes: JsonRecord;
  relatedRows: Record<string, JsonRecord[]>;
  token: string | null;
  newMedia: JsonRecord[];
  deletedMediaIds: number[];
}) {
  const id = String(selected.id ?? "");
  const record = recordForEdit(moduleKey, detail);
  if (moduleKey === "designs") {
    const design = { ...record, ...changes };
    const profile = design.calculation_profile_json;
    design.calculation_profile = typeof profile === "string" && profile ? JSON.parse(profile) : {};
    await api(`/v1/jewelry/designs/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        expected_updated_at: record.updated_at,
        design,
        stones: (relatedRows.stones || []).map((row, index) => ({
          ...row,
          quantity: Math.trunc(Number(row.expected_quantity ?? row.quantity ?? 0)),
          expected_quantity: Math.trunc(Number(row.expected_quantity ?? row.quantity ?? 0)),
          actual_quantity: Math.trunc(Number(row.actual_quantity || 0)),
          inhouse_quantity: Math.trunc(Number(row.inhouse_quantity || 0)),
          line_number: index + 1
        })),
        manufacturing: relatedRows.manufacturing || [],
        new_media: newMedia,
        deleted_media_ids: deletedMediaIds
      })
    }, token);
    return;
  }
  if (moduleKey === "jobCards") {
    const repair = relatedRows.repairs || [];
    await api(`/v1/operations/job-cards/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        idempotency_key: idempotencyKey(),
        expected_updated_at: record.updated_at,
        job_card: { ...record, ...changes },
        stages: relatedRows.stages || [],
        stones: relatedRows.stones || [],
        repair_operations: repair.filter((row) => Number(row.quantity || 0) > 0).map((row) => ({
          id: row.id,
          costing_rate_id: row.costing_rate_id,
          quantity: row.quantity,
          job_notes: row.job_notes || ""
        })),
        new_media: newMedia,
        deleted_media_ids: deletedMediaIds,
        main_media_id: null
      })
    }, token);
    return;
  }
  if (moduleKey === "inventory") {
    const type = String(selected.inventory_type || selectedInventoryType(record)).toLowerCase();
    await api(`/v1/operations/inventory/${type}/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        idempotency_key: idempotencyKey(),
        expected_updated_at: record.updated_at,
        item: { ...record, ...changes },
        new_attachments: [],
        deleted_attachment_ids: []
      })
    }, token);
    return;
  }
  if (moduleKey === "customers" || moduleKey === "suppliers") {
    const kind = moduleKey === "customers" ? "customers" : "suppliers";
    await api(`/v1/reference/${kind}/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ ...record, ...changes, expectedUpdatedAt: record.updatedAt })
    }, token);
    return;
  }
  if (moduleKey === "receivedInvoices") {
    await api(`/v1/commercial/received-invoices/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        idempotency_key: idempotencyKey(),
        expected_updated_at: record.updated_at,
        invoice: { ...record, ...changes },
        new_attachments: []
      })
    }, token);
  }
}

function DetailValue({ value, label }: { value: unknown; label?: string }) {
  if (Array.isArray(value)) {
    const records = value.filter((item): item is JsonRecord =>
      Boolean(item) && typeof item === "object" && !Array.isArray(item)
    );
    if (records.length === value.length && records.length) {
      const columns: string[] = [];
      for (const record of records.slice(0, 30)) {
        for (const key of Object.keys(record)) {
          if (!columns.includes(key) && !isComplex(record[key]) && !internalDetailFields.has(key)) {
            columns.push(key);
          }
        }
      }
      return (
        <section className="detail-group">
          {label && <h3>{humanize(label)}</h3>}
          <div className="detail-table-wrap">
            <table className="detail-table">
              <thead><tr>{columns.map((key) => <th key={key}>{humanize(key)}</th>)}</tr></thead>
              <tbody>
                {records.map((record, index) => (
                  <tr key={String(record.id ?? index)}>
                    {columns.map((key) => <td key={key}>{displayValue(record[key], key)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      );
    }
    return (
      <section className="detail-group">
        {label && <h3>{humanize(label)}</h3>}
        {value.length ? value.map((item, index) => <DetailValue key={index} value={item} label={`Item ${index + 1}`} />) :
          <p className="muted">No saved entries.</p>}
      </section>
    );
  }
  if (value && typeof value === "object") {
    return (
      <section className="detail-group">
        {label && <h3>{humanize(label)}</h3>}
        <dl className="detail-grid">
          {Object.entries(value as JsonRecord)
            .filter(([key]) => !internalDetailFields.has(key))
            .map(([key, child]) => {
            if (child && typeof child === "object") {
              return <div className="detail-nested" key={key}><DetailValue value={child} label={key} /></div>;
            }
            return (
              <div key={key}>
                <dt>{humanize(key)}</dt>
                <dd>{displayValue(child, key)}</dd>
              </div>
            );
          })}
        </dl>
      </section>
    );
  }
  return <p>{String(value ?? "—")}</p>;
}

type DetailTab = { key: string; label: string; value: unknown };

const internalDetailFields = new Set([
  "id", "customer_id", "supplier_id", "design_id_fk", "created_by", "updated_by",
  "calculation_profile_id", "calculation_profile_json", "display_media_id",
  "display_stored_name", "stored_name", "sha256", "snapshot_json", "raw_payload"
]);

function buildDetailTabs(moduleKey: string, value: unknown): DetailTab[] {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return [{ key: "details", label: "Details", value }];
  }
  const record = value as JsonRecord;
  const definitions: Record<string, Array<[string, string, string[]]>> = {
    designs: [
      ["summary", "Summary", ["design"]],
      ["stones", "Stones", ["stones"]],
      ["manufacturing", "Manufacturing", ["manufacturing"]],
      ["media", "Media & files", ["media", "documents", "attachments"]],
      ["versions", "Version history", ["versions", "version_history"]]
    ],
    jobCards: [
      ["details", "Job details", ["job_card", "jobCard"]],
      ["stages", "Workshop stages", ["stages", "job_stages"]],
      ["repairs", "Repair operations", ["repair_operations"]],
      ["stones", "Stone setting", ["stones", "stone_details"]],
      ["media", "Pictures & files", ["media", "documents", "attachments"]],
      ["versions", "Version history", ["versions", "revisions", "printed_pdfs"]]
    ],
    inventory: [
      ["details", "Entry details", ["item", "record", "entry", "stone", "metal", "jewelry"]],
      ["movements", "Movement ledger", ["movements", "ledger", "transactions"]],
      ["files", "Files", ["attachments", "media"]],
      ["versions", "Version history", ["versions", "version_history"]]
    ],
    diamondStock: [
      ["standard", "Standardized fields", ["item", "standardized", "diamond"]],
      ["supplier", "All supplier fields", ["supplier_fields", "raw_payload", "all_fields"]]
    ],
    customers: [
      ["details", "Customer details", ["customer"]],
      ["quotations", "Quotations", ["quotations"]],
      ["costing", "Costing profiles", ["costing_profiles", "profiles"]],
      ["files", "Files", ["attachments", "files"]],
      ["versions", "Version history", ["versions"]]
    ],
    suppliers: [
      ["details", "Supplier details", ["supplier"]],
      ["files", "Files", ["attachments", "files"]],
      ["versions", "Version history", ["versions"]]
    ],
    receivedInvoices: [
      ["details", "Invoice details", ["invoice"]],
      ["files", "Files", ["attachments"]],
      ["versions", "Version history", ["versions"]]
    ],
    shopify: [
      ["product", "Product", ["product", "item"]],
      ["metafields", "Metafields", ["metafields"]],
      ["imported", "Imported details", ["raw_payload", "imported_details", "variants", "media"]]
    ]
  };
  const used = new Set<string>();
  const tabs: DetailTab[] = [];
  for (const [key, label, candidates] of definitions[moduleKey] || []) {
    const present = candidates.filter((candidate) => candidate in record);
    if (!present.length) continue;
    present.forEach((candidate) => used.add(candidate));
    const tabValue = present.length === 1
      ? record[present[0]]
      : Object.fromEntries(present.map((candidate) => [candidate, record[candidate]]));
    tabs.push({ key, label, value: tabValue });
  }
  const remaining = Object.fromEntries(Object.entries(record).filter(([key]) => !used.has(key)));
  if (Object.keys(remaining).length) {
    tabs.unshift({ key: "overview", label: tabs.length ? "Overview" : "Details", value: remaining });
  }
  return tabs.length ? tabs : [{ key: "details", label: "Details", value: record }];
}

function isComplex(value: unknown) {
  return Boolean(value) && typeof value === "object";
}

function displayValue(value: unknown, key = "") {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return Array.isArray(value) ? `${value.length} items` : "View details";
  const normalized = key.toLowerCase().replace(/[^a-z0-9]+/g, "_");
  const decimalField = /(weight|cost|price|amount|percent|pct|carat|total|rate|margin|loss|markup|width|height|length)/.test(normalized);
  if ((typeof value === "number" && !/(^id$|_id$|count|quantity|sequence|number$|version|year)/.test(normalized)) ||
      (typeof value === "string" && decimalField && /^-?\d+(?:\.\d+)?$/.test(value.trim()))) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric.toFixed(2);
  }
  return String(value);
}

function WorkflowDialog({
  moduleKey,
  token,
  onClose,
  onSaved
}: {
  moduleKey: string;
  token: string | null;
  onClose: () => void;
  onSaved: () => void | Promise<unknown>;
}) {
  return (
    <div className="dialog-backdrop" role="presentation">
      <section className="workflow-dialog" role="dialog" aria-modal="true" aria-label="Create record">
        <header>
          <div>
            <p className="eyebrow">Operational entry</p>
            <h2>{workflowTitle(moduleKey)}</h2>
          </div>
          <button className="dialog-close" onClick={onClose} aria-label="Close"><X /></button>
        </header>
        <div className="workflow-body">
          {moduleKey === "designs" && <DesignForm token={token} onSaved={onSaved} />}
          {moduleKey === "jobCards" && <JobCardForm token={token} onSaved={onSaved} />}
          {moduleKey === "inventory" && <InventoryForm token={token} onSaved={onSaved} />}
          {(moduleKey === "customers" || moduleKey === "suppliers") &&
            <PartyForm kind={moduleKey === "customers" ? "customer" : "supplier"} token={token} onSaved={onSaved} />}
          {moduleKey === "receivedInvoices" && <InvoiceForm token={token} onSaved={onSaved} />}
          {moduleKey === "pettyCash" && <PettyCashForm token={token} onSaved={onSaved} />}
        </div>
      </section>
    </div>
  );
}

function WorkflowTabs({
  tabs,
  active,
  onChange
}: {
  tabs: Array<[string, string]>;
  active: string;
  onChange: (key: string) => void;
}) {
  return (
    <nav className="workflow-tabs" aria-label="Workflow sections">
      {tabs.map(([key, label]) => (
        <button
          key={key}
          type="button"
          className={active === key ? "active" : ""}
          onClick={() => onChange(key)}
        >
          {label}
        </button>
      ))}
    </nav>
  );
}

type EditableColumn = [key: string, label: string, type: "text" | "number" | "integer" | "datetime-local"];

function EditableRows({
  rows,
  setRows,
  columns,
  addLabel,
  choices = {},
  choiceProvider,
  choiceColumns = [],
  onCellChange,
  toolbarActions,
  rowActions,
  readOnlyColumns = [],
  isCellDisabled,
  rowActionsAfterDelete = false,
  allowCopy = false
}: {
  rows: JsonRecord[];
  setRows: Dispatch<SetStateAction<JsonRecord[]>>;
  columns: EditableColumn[];
  addLabel: string;
  choices?: Record<string, string[]>;
  choiceProvider?: (key: string, row: JsonRecord) => string[];
  choiceColumns?: string[];
  onCellChange?: (row: JsonRecord, key: string, value: string | number) => JsonRecord;
  toolbarActions?: ReactNode;
  rowActions?: (row: JsonRecord, index: number) => ReactNode;
  readOnlyColumns?: string[];
  isCellDisabled?: (row: JsonRecord, key: string) => boolean;
  rowActionsAfterDelete?: boolean;
  allowCopy?: boolean;
}) {
  return (
    <section className="editable-lines">
      <div className="line-toolbar">
        <div>
          <strong>{addLabel.replace(/^Add /, "")} <span className="info-dot" title="Enter values directly in the table. Required numeric fields reject negative values." aria-label="Enter values directly in the table. Required numeric fields reject negative values.">i</span></strong>
        </div>
        <div className="line-toolbar-actions">{toolbarActions}<Button
          type="button"
          className="action-button action-create"
          isIconOnly
          aria-label={addLabel}
          title={addLabel}
          size="sm"
          onPress={() => setRows((current) => [
            ...current,
            { line_uid: crypto.randomUUID(), ...Object.fromEntries(columns.map(([key, , type]) => [key, type === "number" || type === "integer" ? 0 : ""])) }
          ])}
        ><Plus size={17} /></Button></div>
      </div>
      {!rows.length ? (
        <div className="inline-empty">No lines yet. Use “{addLabel}” to begin.</div>
      ) : (
        <div className="editable-table-wrap">
          <table className="editable-table">
            <thead>
              <tr>
                <th>#</th>
                {columns.map(([key, label]) => <th key={key}>{label}</th>)}
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={String(row.line_uid ?? row.id ?? rowIndex)}>
                  <td>{rowIndex + 1}</td>
                  {columns.map(([key, , type]) => {
                    const availableChoices = choiceProvider?.(key, row) ?? choices[key] ?? [];
                    return (
                    <td key={key}>
                      {availableChoices.length || choiceColumns.includes(key) ? (
                        <select
                          aria-label={`${columns.find((column) => column[0] === key)?.[1]} row ${rowIndex + 1}`}
                          value={String(row[key] ?? "")}
                          disabled={!availableChoices.length || readOnlyColumns.includes(key)}
                          onChange={(event) => setRows((current) => current.map((item, index) =>
                            index === rowIndex
                              ? onCellChange ? onCellChange(item, key, event.target.value)
                                : key === "stone_type" ? { ...item, [key]: event.target.value, color: "", clarity: "" }
                                : { ...item, [key]: event.target.value }
                              : item
                          ))}
                        >
                          <option value="">{key === "service_type" && !row.service_department ? "Select department first…" : key === "unit" && !row.service_type ? "Select service first…" : "Select…"}</option>
                          {availableChoices.map((option) => <option key={option} value={option}>{option}</option>)}
                        </select>
                      ) : (
                      <input
                        aria-label={`${columns.find((column) => column[0] === key)?.[1]} row ${rowIndex + 1}`}
                        type={type === "datetime-local" ? "datetime-local" : type === "text" ? "text" : "number"}
                        min={type === "number" || type === "integer" ? 0 : undefined}
                        step={type === "integer" ? 1 : type === "number" ? "0.01" : undefined}
                        value={String(row[key] ?? "")}
                        readOnly={readOnlyColumns.includes(key)}
                        disabled={isCellDisabled?.(row, key)}
                        onChange={(event) => setRows((current) => current.map((item, index) =>
                          index === rowIndex
                            ? onCellChange ? onCellChange(item, key, type === "integer" ? Number(event.target.value) : event.target.value)
                              : { ...item, [key]: type === "integer" ? Number(event.target.value) : event.target.value }
                            : item
                        ))}
                        onBlur={(event) => { if (type !== "number" || event.target.value === "") return; const rounded = Number(Number(event.target.value).toFixed(2)); setRows((current) => current.map((item, index) => index === rowIndex ? onCellChange ? onCellChange(item, key, rounded) : { ...item, [key]: rounded } : item)); }}
                      />)}
                    </td>
                  )})}
                  <td>
                    <div className="editable-row-actions">{!rowActionsAfterDelete && rowActions?.(row, rowIndex)}
                    {allowCopy && <Button type="button" size="sm" className="action-button action-neutral" isIconOnly aria-label={`Copy row ${rowIndex + 1}`} title="Copy row" onPress={() => setRows((current) => { const source = current[rowIndex]; const clone = { ...source, id: undefined, line_uid: crypto.randomUUID() }; return [...current.slice(0, rowIndex + 1), clone, ...current.slice(rowIndex + 1)]; })}><Copy size={16} /></Button>}
                    <Button
                      type="button"
                      size="sm"
                      className="action-button action-delete"
                      isIconOnly
                      aria-label={`Delete row ${rowIndex + 1}`}
                      title="Delete row"
                      onPress={() => setRows((current) => current.filter((_, index) => index !== rowIndex))}
                    ><Trash2 size={16} /></Button>{rowActionsAfterDelete && rowActions?.(row, rowIndex)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function JobStoneEditor({
  context,
  rows,
  setRows,
  stages
}: {
  context: JsonRecord;
  rows: JsonRecord[];
  setRows: Dispatch<SetStateAction<JsonRecord[]>>;
  stages: JsonRecord[];
}) {
  const inventory = context.inventory_stones as JsonRecord[] || [];
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filteredInventory = inventory.filter((stone) =>
    [stone.stock_code, stone.stone_type, stone.stone_shape, stone.stone_size, stone.certificate_id]
      .some((value) => String(value || "").toLowerCase().includes(query.trim().toLowerCase()))
  ).slice(0, 250);
  const add = (stone: JsonRecord) => {
    const selectedId = String(stone.id);
    if (rows.some((row) => String(row.inventory_stone_id) === selectedId)) return;
    setRows((current) => [...current, {
      ...stone,
      inventory_stone_id: stone.id,
      stage_sequence_number: "",
      issued_quantity: 0,
      issued_weight: 0,
      returned_quantity: 0,
      returned_weight: 0,
      notes: ""
    }]);
    setPickerOpen(false);
  };
  const update = (index: number, key: string, value: number | string) =>
    setRows((current) => current.map((row, rowIndex) => rowIndex === index ? { ...row, [key]: value } : row));
  return (
    <section className="editable-lines">
      <div className="stone-picker">
        <div><strong>Available inventory stones</strong><small>Use the searchable picker for large Production inventories.</small></div>
        <Button color="primary" startContent={<Plus size={16} />} onPress={() => setPickerOpen(true)}>Search and add stone</Button>
      </div>
      {pickerOpen && (
        <div className="dialog-backdrop stone-picker-backdrop" role="presentation">
          <section className="workflow-dialog stone-search-dialog" role="dialog" aria-modal="true" aria-label="Search inventory stones">
            <header><div><p className="eyebrow">Stone inventory</p><h2>Search and select a stone</h2></div>
              <button className="dialog-close" onClick={() => setPickerOpen(false)} aria-label="Close"><X /></button></header>
            <div className="workflow-body">
              <input className="stone-search-input" autoFocus placeholder="Stock code, certificate, type, shape or size…" value={query} onChange={(event) => setQuery(event.target.value)} />
              <div className="stone-search-results">
                {filteredInventory.map((stone) => (
                  <button type="button" key={String(stone.id)} onClick={() => add(stone)} disabled={rows.some((row) => Number(row.inventory_stone_id) === Number(stone.id))}>
                    <strong>{String(stone.stock_code || "Stone")}</strong>
                    <span>{[stone.stone_type, stone.stone_shape, stone.stone_size, stone.certificate_id].filter(Boolean).join(" · ")}</span>
                    <small>Created {String(stone.created_at || "—")} · Available {String(stone.quantity_on_hand || 0)} pcs / {String(stone.weight_on_hand || 0)} ct</small>
                  </button>
                ))}
              </div>
              <small>Showing up to 250 matching records. Refine the search to narrow the list.</small>
            </div>
          </section>
        </div>
      )}
      {!rows.length ? <div className="inline-empty">No stones selected for setting.</div> : (
        <div className="editable-table-wrap">
          <table className="editable-table">
            <thead><tr>
              <th>Workshop stage</th><th>Stone</th><th>Issue wt. (ct)</th><th>Issue qty</th>
              <th>Returned wt. (ct)</th><th>Returned qty</th><th>Notes</th><th>Action</th>
            </tr></thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={String(row.inventory_stone_id)}>
                  <td><select value={String(row.stage_sequence_number || "")} onChange={(event) => update(index, "stage_sequence_number", Number(event.target.value) || "")}>
                    <option value="">Select stage…</option>
                    {stages.map((stage, stageIndex) => <option key={stageIndex + 1} value={stageIndex + 1}>{stageIndex + 1}. {String(stage.stage_name || "")}</option>)}
                  </select></td>
                  <td title={`Available ${row.quantity_on_hand || 0} pcs / ${row.weight_on_hand || 0} ct`}>
                    {[row.stock_code, row.stone_type, row.stone_shape, row.stone_size].filter(Boolean).join(" · ")}
                  </td>
                  {(["issued_weight", "issued_quantity", "returned_weight", "returned_quantity"] as const).map((key) => {
                    const integer = key.endsWith("quantity");
                    const maximum = key.startsWith("returned")
                      ? Number(row[key.replace("returned", "issued")] || 0)
                      : key === "issued_quantity" ? Number(row.quantity_on_hand || 0) : Number(row.weight_on_hand || 0);
                    return (
                      <td key={key}>
                        <input
                          type="number"
                          min="0"
                          max={maximum}
                          step={integer ? 1 : "0.01"}
                          value={String(row[key] || 0)}
                          onChange={(event) => update(index, key, integer
                            ? Math.trunc(Math.max(0, Math.min(maximum, Number(event.target.value))))
                            : Math.max(0, Math.min(maximum, Number(event.target.value))))}
                        />
                      </td>
                    );
                  })}
                  <td><input value={String(row.notes || "")} onChange={(event) => update(index, "notes", event.target.value)} /></td>
                  <td><Button color="danger" variant="light" size="sm" onPress={() =>
                    setRows((current) => current.filter((_, rowIndex) => rowIndex !== index))
                  }>Delete</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="section-help">Only lines with a positive issued quantity and weight are saved. Returns cannot exceed the issued values.</p>
    </section>
  );
}

function RepairOperationEditor({
  rows,
  setRows
}: {
  rows: JsonRecord[];
  setRows: Dispatch<SetStateAction<JsonRecord[]>>;
}) {
  return (
    <div className="editable-table-wrap">
      <table className="editable-table repair-table">
        <thead><tr><th>Department</th><th>Operation</th><th>Unit</th><th>Qty</th><th>Job Card notes</th></tr></thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={String(row.costing_rate_id ?? index)}>
              <td>{String(row.service_department || "")}</td>
              <td title={String(row.remarks || "No additional pricing note.")}>
                {String(row.service_type || "")} <span className="info-dot">i</span>
              </td>
              <td>{String(row.unit || "")}</td>
              <td>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={String(row.quantity || 0)}
                  onChange={(event) => setRows((current) => current.map((item, rowIndex) =>
                    rowIndex === index ? { ...item, quantity: Math.max(0, Number(event.target.value)) } : item
                  ))}
                />
              </td>
              <td>
                <input
                  value={String(row.job_notes || "")}
                  placeholder="Optional instruction for this operation"
                  onChange={(event) => setRows((current) => current.map((item, rowIndex) =>
                    rowIndex === index ? { ...item, job_notes: event.target.value } : item
                  ))}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DiamondStockPicker({ token, onAdd, existingProviderKeys = [], usdToAed = 3.70 }: { token: string | null; onAdd: (rows: JsonRecord[], certificates: PendingMedia[]) => void; existingProviderKeys?: string[]; usdToAed?: number }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<JsonRecord[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [internalView, setInternalView] = useState<"All" | "Available" | "Reserved" | "Sold">("All");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const existing = useMemo(() => new Set(existingProviderKeys.map((value) => String(value).trim()).filter(Boolean)), [existingProviderKeys]);
  const find = async (view = internalView) => {
    setBusy(true); setError("");
    try {
      const status = view === "All" ? "" : `&internal_status=${encodeURIComponent(view)}`;
      const result = await api<JsonRecord>(`/v1/diamond-stock/choices?q=${encodeURIComponent(search)}${status}&limit=250`, {}, token);
      setRows(Array.isArray(result.items) ? result.items as JsonRecord[] : []);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Diamond Stock could not be searched."); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (open) void find(); }, [open]);
  const add = async () => {
    setBusy(true); setError("");
    const sourceRows = rows.filter((row) => selected.has(String(row.id)));
    const chosen = sourceRows.map((row) => ({
      source_id: row.id, line_uid: crypto.randomUUID(), stone_type: "Diamond", stone_shape: row.shape || "", stone_size: row.measurements || "",
      color: row.color || row.fancy_color || "", clarity: row.clarity || "", lab: row.lab || "", certificate_id: row.certificate_no || "",
      quantity: 1, expected_quantity: 1, actual_quantity: 0, inhouse_quantity: 0, inhouse_weight: 0,
      expected_weight: Number(row.carat || 0), actual_final_weight: 0,
      expected_cost_per_carat: Number((Number(row.octadiam_price_per_carat || 0) * usdToAed).toFixed(2)), markup_pct: 0, actual_cost_per_carat: 0,
      diamond_provider_key: row.provider_key || "", diamond_internal_status: row.internal_status || "Available",
      diamond_internal_record_id: row.internal_record_id || null,
      diamond_snapshot_json: {
        source_item_id: row.id, provider_key: row.provider_key || "", stock_number: row.stock_number || "", carat: row.carat || 0,
        shape: row.shape || "", color: row.color || "", fancy_color: row.fancy_color || "", clarity: row.clarity || "",
        cut_grade: row.cut_grade || "", polish: row.polish || "", symmetry: row.symmetry || "", fluorescence: row.fluorescence || "",
        lab: row.lab || "", image_url: row.image_url || "", supplier_status: row.availability_status || "", internal_status: row.internal_status || "Available",
        source_currency: "USD", usd_price_per_carat: Number(row.octadiam_price_per_carat || 0), usd_total_price: Number(row.octadiam_total_price || 0),
        usd_to_aed_rate: usdToAed, aed_price_per_carat: Number((Number(row.octadiam_price_per_carat || 0) * usdToAed).toFixed(2))
      }
    }));
    const certificates: PendingMedia[] = [];
    for (const stone of chosen) {
      const sourceRow = sourceRows.find((row) => String(row.id) === String(stone.source_id));
      if (!sourceRow?.certificate_url) continue;
      try {
        const attachment = await api<JsonRecord>(`/v1/diamond-stock/${Number(stone.source_id)}/certificate-attachment`, {}, token);
        const binary = atob(String(attachment.content_base64 || ""));
        const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
        certificates.push({ key: crypto.randomUUID(), file: new File([bytes], String(attachment.filename || `certificate-${sourceRow.certificate_no || sourceRow.stock_number}.pdf`), { type: String(attachment.mime_type || "application/pdf") }), media_type: "Stone Certificate", stone_line_uid: String(stone.line_uid) });
      } catch (reason) {
        setError(reason instanceof Error ? `Stone selected, but its certificate could not be attached: ${reason.message}` : "Stone selected, but its certificate could not be attached.");
      }
    }
    onAdd(chosen.map(({ source_id: _, ...stone }) => stone), certificates);
    setBusy(false); setOpen(false); setSelected(new Set());
  };
  return <div className="diamond-design-link"><Button isIconOnly className="action-button action-view" aria-label="Search Diamond Stock" title="Search Diamond Stock" onPress={() => setOpen(true)}><FileImage size={18} /></Button>
    {open && <div className="dialog-backdrop nested-dialog" role="presentation"><section className="workflow-dialog diamond-picker-dialog" role="dialog" aria-modal="true" aria-label="Search Diamond Stock">
      <header><div><p className="eyebrow">Jewelry Design stone link</p><h2>Select Diamond Stock</h2></div><button className="dialog-close" onClick={() => setOpen(false)} aria-label="Close"><X /></button></header>
      <div className="workflow-body"><div className="diamond-picker-status" role="group" aria-label="Internal stone status">
        {(["All", "Available", "Reserved", "Sold"] as const).map((view) => <Button key={view}
          className={`action-button ${internalView === view ? "action-view" : "action-neutral"}`}
          aria-pressed={internalView === view} onPress={() => { setInternalView(view); setSelected(new Set()); void find(view); }}>{view}</Button>)}
      </div><div className="diamond-picker-search"><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void find(); }} placeholder="Stock, certificate, shape, colour, clarity…" /><Button className="action-button action-view" isLoading={busy} onPress={() => void find()}>Search</Button></div>
        {error && <div className="error-banner">{error}</div>}<div className="detail-table-wrap diamond-picker-results"><table className="detail-table"><thead><tr><th></th><th>Stock</th><th>Carat</th><th>Shape</th><th>Color</th><th>Clarity</th><th>Source status</th><th>Internal status</th></tr></thead><tbody>{rows.map((row) => { const duplicate = existing.has(String(row.provider_key || "").trim()); return <tr key={String(row.id)} title={duplicate ? "This stone is already linked to this Jewelry Design" : undefined}><td><input type="checkbox" disabled={duplicate} checked={!duplicate && selected.has(String(row.id))} onChange={() => setSelected((current) => { const next = new Set(current); const key = String(row.id); next.has(key) ? next.delete(key) : next.add(key); return next; })} /></td><td>{String(row.stock_number || "—")}{duplicate ? " · Already linked" : ""}</td><td>{String(row.carat || "—")}</td><td>{String(row.shape || "—")}</td><td>{String(row.color || "—")}</td><td>{String(row.clarity || "—")}</td><td>{String(row.availability_status || "—")}</td><td>{String(row.internal_status || "Available")}</td></tr>; })}</tbody></table></div>
        <footer className="form-footer"><Button variant="bordered" onPress={() => setOpen(false)}>Cancel</Button><Button className="action-button action-create" isDisabled={!selected.size} onPress={add}>Add selected ({selected.size})</Button></footer>
      </div></section></div>}
  </div>;
}

function designSizeCategory(category: string) {
  const normalized = category.trim().toLowerCase();
  const categories: Record<string, string> = {
    ring: "size_ring",
    bracelet: "size_bracelet",
    bangle: "size_bangle",
    necklace: "size_necklace",
    chain: "size_chain",
    earrings: "size_earrings",
    pendant: "size_pendant"
  };
  return categories[normalized] || "";
}

function CostValue({ label, value, emphasis = false }: { label: string; value: string; emphasis?: boolean }) {
  return <div className={`cost-value${emphasis ? " emphasis" : ""}`}><span>{label}</span><strong>{value}</strong></div>;
}

function SectionTitle({ children, tooltip }: { children: ReactNode; tooltip?: string }) {
  return <h3>{children}{tooltip && <span className="info-dot section-tooltip" title={tooltip} aria-label={tooltip}>i</span>}</h3>;
}

function StoneCostTable({ mode, stones, setStones }: { mode: "expected" | "actual"; stones: JsonRecord[]; setStones: Dispatch<SetStateAction<JsonRecord[]>> }) {
  const quantityKey = `${mode}_quantity`, weightKey = `${mode}_weight`, costKey = `${mode}_cost_per_carat`;
  const update = (index: number, key: string, value: string) => setStones((current) => current.map((stone, rowIndex) => rowIndex === index ? { ...stone, [key]: value } : stone));
  if (!stones.length) return <div className="inline-empty">Add stone lines in Technical Information first.</div>;
  return <div className="editable-table-wrap"><table className="editable-table cost-table"><thead><tr><th>Line</th><th>Stone type</th><th>Shape</th><th>Size / dimensions (mm)</th><th>{mode === "expected" ? "Expected qty" : "Actual qty"}</th><th>{mode === "expected" ? "Expected wt. (ct)" : "Actual wt. (ct)"}</th><th>{mode === "expected" ? "Expected" : "Actual"} cost / ct (AED)</th><th>{mode === "expected" ? "Expected" : "Actual"} total (AED)</th></tr></thead>
    <tbody>{stones.map((stone, index) => { const total = Number(stone[weightKey] || 0) * Number(stone[costKey] || 0); const roundOnBlur = (key: string, value: string) => { if (value !== "") update(index, key, Number(value).toFixed(2)); }; return <tr key={String(stone.line_uid || index)}><td>{index + 1}</td><td>{String(stone.stone_type || "—")}</td><td>{String(stone.stone_shape || "—")}</td><td>{String(stone.stone_size || "—")}</td><td><input type="number" min="0" step="1" value={String(stone[quantityKey] || "")} readOnly={mode === "expected"} onChange={(event) => update(index, quantityKey, event.target.value)} /></td><td><input type="number" min="0" step="0.01" value={String(stone[weightKey] || "")} readOnly={mode === "expected"} onChange={(event) => update(index, weightKey, event.target.value)} onBlur={(event) => roundOnBlur(weightKey, event.target.value)} /></td><td><input type="number" min="0" step="0.01" value={String(stone[costKey] || "")} onChange={(event) => update(index, costKey, event.target.value)} onBlur={(event) => roundOnBlur(costKey, event.target.value)} /></td><td className="calculated-cell">{total.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>; })}</tbody></table></div>;
}

function ManufacturingCostRows({ mode, rows, setRows, costingLines, profileName }: { mode: "expected" | "actual"; rows: JsonRecord[]; setRows: Dispatch<SetStateAction<JsonRecord[]>>; costingLines: JsonRecord[]; profileName: string }) {
  const costKey = `${mode}_cost`, quantityKey = `${mode}_quantity`;
  return <div className="manufacturing-cost-rows"><div className="costing-profile-badge">Costing profile: {profileName}</div><EditableRows rows={rows} setRows={setRows} columns={[["service_department", "Service Dept", "text"], ["service_type", "Service Type", "text"], ["unit", "Unit", "text"], [quantityKey, "Qty", "number"], [costKey, `${mode === "expected" ? "Expected" : "Actual"} cost (AED)`, "number"]]} addLabel="Add manufacturing line" choiceColumns={["service_department", "service_type", "unit"]} readOnlyColumns={[costKey]} choices={{ service_department: [...new Set(costingLines.map((line) => String(line.service_department || "")).filter(Boolean))] }} choiceProvider={(key, row) => key === "service_type" ? costingLines.filter((line) => String(line.service_department) === String(row.service_department || "")).map((line) => String(line.service_type)) : key === "unit" ? costingLines.filter((line) => String(line.service_department) === String(row.service_department || "") && String(line.service_type) === String(row.service_type || "")).map((line) => String(line.unit)) : key === "service_department" ? [...new Set(costingLines.map((line) => String(line.service_department || "")).filter(Boolean))] : []} onCellChange={(row, key, value) => { if (key === "service_department") return { ...row, service_department: value, service_type: "", process_name: "", unit: "", rate_minor: 0, expected_quantity: 0, actual_quantity: 0, expected_cost: 0, actual_cost: 0 }; if (key === "service_type") { const rate = costingLines.find((line) => String(line.service_department) === String(row.service_department) && String(line.service_type) === String(value)); return { ...row, service_type: value, process_name: value, unit: rate?.unit || "", rate_minor: Number(rate?.rate_minor || 0) }; } if (key === quantityKey) { const quantity = Math.max(0, Number(value || 0)); return { ...row, [key]: value, [costKey]: Math.round(quantity * Number(row.rate_minor || 0)) / 100 }; } return { ...row, [key]: value }; }} /></div>;
}

export function automaticManufacturingRows(rates: JsonRecord[], metalType: string, stones: JsonRecord[], expectedFinalWeight: number, actualFinalWeight: number) {
  const normalized = (value: unknown) => String(value || "").trim().toLowerCase();
  const costed = (rate: JsonRecord, expectedQuantity: number, actualQuantity?: number) => {
    const defaultedActualQuantity = Number(actualQuantity || 0) > 0 ? Number(actualQuantity) : expectedQuantity;
    return ({
    line_uid: `costing-${String(rate.id || `${rate.service_department}-${rate.service_type}-${rate.unit}`)}`,
    process_name: String(rate.service_type || ""), service_department: String(rate.service_department || ""),
    service_type: String(rate.service_type || ""), unit: String(rate.unit || ""), rate_minor: Number(rate.rate_minor || 0),
    expected_quantity: Math.max(0, expectedQuantity), actual_quantity: Math.max(0, defaultedActualQuantity),
    expected_cost: Math.round(Math.max(0, expectedQuantity) * Number(rate.rate_minor || 0)) / 100,
    actual_cost: Math.round(Math.max(0, defaultedActualQuantity) * Number(rate.rate_minor || 0)) / 100
  }); };
  const result: JsonRecord[] = [];
  const creative = rates.find((rate) => normalized(rate.service_department) === "creative team" && normalized(rate.service_type) === "3d design");
  if (creative) result.push(costed(creative, 1, 1));
  const purity = metalType.match(/(24|22|21|18|14|10|9|999|925)/)?.[1] || "";
  const family = normalized(metalType).includes("silver") ? "silver" : "gold";
  const productionRates = rates.filter((rate) => normalized(rate.service_department) === "production");
  const production = productionRates.find((rate) => normalized(rate.service_type).includes(family) && (!purity || normalized(rate.service_type).includes(purity)))
    || productionRates.find((rate) => normalized(rate.service_type).includes(family));
  if (production) {
    const unit = normalized(production.unit);
    result.push(costed(production, unit.includes("g") || unit.includes("gram") ? expectedFinalWeight : 1,
      unit.includes("g") || unit.includes("gram") ? actualFinalWeight : 1));
  }
  const settingRates = rates.filter((rate) => normalized(rate.service_department) === "setting");
  const grouped = new Map<string, { rate: JsonRecord; expected: number; actual: number }>();
  for (const stone of stones) {
    if (!settingRates.length) break;
    const descriptors = [stone.stone_type, stone.stone_shape, stone.stone_size].map(normalized).filter(Boolean);
    const expectedQuantity = Math.max(1, Number(stone.expected_quantity || stone.quantity || 1));
    const eachStoneWeight = Number(stone.expected_weight || 0) / expectedQuantity;
    const preciousBigStone = settingRates.find((candidate) => normalized(candidate.service_type) === "big stones - precious");
    const rate = (eachStoneWeight >= 0.25 ? preciousBigStone : undefined)
      || settingRates.find((candidate) => normalized(candidate.service_type) !== "big stones - precious" && descriptors.some((descriptor) => normalized(candidate.service_type).includes(descriptor) || descriptor.includes(normalized(candidate.service_type))))
      || settingRates.find((candidate) => normalized(candidate.service_type) !== "big stones - precious") || settingRates[0];
    const unit = normalized(rate.unit);
    const expected = unit.includes("ct") || unit.includes("carat") ? Number(stone.expected_weight || 0) : Number(stone.expected_quantity || 0);
    const actual = unit.includes("ct") || unit.includes("carat") ? Number(stone.actual_weight || 0) : Number(stone.actual_quantity || 0);
    const key = String(rate.id || `${rate.service_type}-${rate.unit}`);
    const current = grouped.get(key) || { rate, expected: 0, actual: 0 };
    current.expected += expected; current.actual += actual; grouped.set(key, current);
  }
  for (const group of grouped.values()) result.push(costed(group.rate, group.expected, group.actual));
  return result;
}

export function DesignForm({ token, onSaved, initialDetail, editId, readOnly = false, mode = "technical" }: FormProps & { initialDetail?: JsonRecord; editId?: string; readOnly?: boolean; mode?:"technical"|"costing" }) {
  const queryClient = useQueryClient();
  const savedDesign = (initialDetail?.design as JsonRecord | undefined) || {};
  const savedStones = (Array.isArray(initialDetail?.stones) ? initialDetail.stones as JsonRecord[] : []).map((stone) => ({
    ...stone,
    actual_quantity: Number(stone.actual_quantity || 0) > 0 ? stone.actual_quantity : stone.expected_quantity ?? stone.quantity ?? 0,
    actual_weight: Number(stone.actual_weight ?? stone.actual_final_weight ?? 0) > 0 ? stone.actual_weight ?? stone.actual_final_weight : stone.expected_weight ?? 0,
    expected_cost_per_carat: Number(Number(stone.expected_cost_per_carat || 0).toFixed(2)),
    actual_cost_per_carat: Number(Number(Number(stone.actual_cost_per_carat || 0) > 0 ? stone.actual_cost_per_carat : stone.expected_cost_per_carat ?? 0).toFixed(2))
  }));
  const [activeTab, setActiveTab] = useState(mode === "costing" ? "expected" : "identity");
  const [customers, setCustomers] = useState<JsonRecord[]>([]);
  const [categories, setCategories] = useState<JsonRecord[]>([]);
  const [metals, setMetals] = useState<JsonRecord[]>([]);
  const [metalColours, setMetalColours] = useState<JsonRecord[]>([]);
  const [designFileCategories, setDesignFileCategories] = useState<string[]>(DESIGN_MEDIA_CATEGORIES);
  const [designers, setDesigners] = useState<JsonRecord[]>([]);
  const [category, setCategory] = useState(String(savedDesign.category || ""));
  const [subcategory, setSubcategory] = useState(String(savedDesign.sub_category || ""));
  const [metalType, setMetalType] = useState(String(savedDesign.metal_type || ""));
  const [metalColour, setMetalColour] = useState(String(savedDesign.metal_colour || ""));
  const [customerId, setCustomerId] = useState(String(savedDesign.customer_id || ""));
  const [designerName, setDesignerName] = useState(String(savedDesign.designer_name || ""));
  const [entryDate, setEntryDate] = useState(String(savedDesign.entry_date || today()).slice(0, 10));
  const [generatedDesignId, setGeneratedDesignId] = useState(String(savedDesign.design_id || ""));
  const [metalPrice, setMetalPrice] = useState(Number(savedDesign.quoted_metal_price || 0).toFixed(2));
  const [expectedFinalWeight, setExpectedFinalWeight] = useState(String(savedDesign.expected_final_weight ?? ""));
  const [expectedNetWeight, setExpectedNetWeight] = useState(String(savedDesign.expected_net_weight ?? ""));
  const [actualFinalWeight, setActualFinalWeight] = useState(String(Number(savedDesign.actual_final_weight || 0) > 0 ? savedDesign.actual_final_weight : savedDesign.expected_final_weight ?? ""));
  const [actualNetWeight, setActualNetWeight] = useState(String(Number(savedDesign.actual_net_weight || 0) > 0 ? savedDesign.actual_net_weight : savedDesign.expected_net_weight ?? ""));
  const [actualMetalPrice, setActualMetalPrice] = useState("");
  const [metalLossPct, setMetalLossPct] = useState(String(savedDesign.quoted_loss_pct ?? "10"));
  const [stoneMarkupPct, setStoneMarkupPct] = useState(String(savedDesign.stone_markup_pct ?? "30"));
  const [finalItemMarkupPct, setFinalItemMarkupPct] = useState(String(savedDesign.final_item_markup_pct ?? "50"));
  const [metalRates, setMetalRates] = useState<JsonRecord>({});
  const [costingLines, setCostingLines] = useState<JsonRecord[]>([]);
  const [costingProfiles, setCostingProfiles] = useState<JsonRecord[]>([]);
  const [effectiveCostingRates, setEffectiveCostingRates] = useState<JsonRecord[]>([]);
  const [effectiveCostingName, setEffectiveCostingName] = useState("OctaDiam Standard");
  const [usdToAed, setUsdToAed] = useState(3.70);
  const [subcategories, setSubcategories] = useState<JsonRecord[]>([]);
  const [sizes, setSizes] = useState<JsonRecord[]>([]);
  const [sizeSpecification, setSizeSpecification] = useState(String(savedDesign.size_specification || ""));
  const [stoneChoices, setStoneChoices] = useState<Record<string, string[]>>({});
  const [stoneAttributeChoices, setStoneAttributeChoices] = useState<Record<string, Record<string, string[]>>>({
    color: {},
    clarity: {}
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [stones, setStones] = useState<JsonRecord[]>(savedStones);
  const [designMedia, setDesignMedia] = useState<PendingMedia[]>([]);
  const [deletedDesignMedia, setDeletedDesignMedia] = useState<number[]>([]);
  const [manufacturing, setManufacturing] = useState<JsonRecord[]>((Array.isArray(initialDetail?.manufacturing) ? initialDetail.manufacturing as JsonRecord[] : []).map((row) => ({ ...row,
    actual_quantity: Number(row.actual_quantity || 0) > 0 ? row.actual_quantity : row.expected_quantity ?? 0,
    actual_cost: Number(row.actual_cost || 0) > 0 ? row.actual_cost : row.expected_cost ?? 0
  })));
  const existingMedia = Array.isArray(initialDetail?.media) ? initialDetail.media as JsonRecord[] : [];
  const expectedMetalCost = Number(expectedFinalWeight || 0) * Number(metalPrice || 0) * (1 + Number(metalLossPct || 0) / 100);
  const expectedStoneCost = stones.reduce((total, stone) => total + Number(stone.expected_weight || 0) * Number(stone.expected_cost_per_carat || 0), 0);
  const markupEligibleStoneCost = stones.reduce((total, stone) => String(stone.diamond_provider_key || "").trim()
    ? total : total + Number(stone.expected_weight || 0) * Number(stone.expected_cost_per_carat || 0), 0);
  const expectedManufacturingCost = manufacturing.reduce((total, row) => total + Number(row.expected_cost || 0), 0);
  const actualMetalCost = Number(actualFinalWeight || 0) * Number(actualMetalPrice || 0) * (1 + Number(metalLossPct || 0) / 100);
  const actualStoneCost = stones.reduce((total, stone) => total + Number(stone.actual_weight || 0) * Number(stone.actual_cost_per_carat || 0), 0);
  const actualManufacturingCost = manufacturing.reduce((total, row) => total + Number(row.actual_cost || 0), 0);
  const stoneMarkupAmount = markupEligibleStoneCost * Number(stoneMarkupPct || 0) / 100;
  const subtotalBeforeFinalMarkup = expectedMetalCost + expectedStoneCost + stoneMarkupAmount + expectedManufacturingCost;
  const finalItemMarkupAmount = subtotalBeforeFinalMarkup * Number(finalItemMarkupPct || 0) / 100;
  const calculatedSalePrice = subtotalBeforeFinalMarkup + finalItemMarkupAmount;
  const money = (amount: number) => `AED ${amount.toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  useEffect(() => {
    const reportLoadError = (reason: unknown) => setError(reason instanceof Error ? reason.message : "Form values could not be loaded");
    void api<unknown>("/v1/reference/entity-choices?type=customer", {}, token)
      .then((party) => setCustomers(extractRows(party))).catch(reportLoadError);
    void api<Record<string,string[]>>("/v1/reference/lookup-bundle?categories=jewelry_category,metal_type,metal_colour,designer_name,design_file_category", {}, token)
      .then((lookups) => {
        setCategories((lookups.jewelry_category || []).map((value) => ({ value })));
        setMetals((lookups.metal_type || []).map((value) => ({ value })));
        setMetalColours((lookups.metal_colour || []).map((value) => ({ value })));
        setDesigners((lookups.designer_name || []).map((value) => ({ value })));
        setDesignFileCategories(lookups.design_file_category || []);
      }).catch(reportLoadError);
    void api<JsonRecord[]>("/v1/jewelry/metal-prices?limit=1", {}, token).then((prices) => {
      const latestRates = (prices[0]?.rates as JsonRecord) || {};
      if (Object.keys(latestRates).length) setMetalRates(latestRates);
      else void api<JsonRecord>("/v1/jewelry/metal-prices/refresh", {
        method: "POST", headers: { "Content-Type": "application/json", "Idempotency-Key": idempotencyKey() },
        body: JSON.stringify({ force: false })
      }, token).then((snapshot) => setMetalRates((snapshot.rates as JsonRecord) || {})).catch(reportLoadError);
    }).catch(reportLoadError);
    void api<JsonRecord[]>("/v1/jewelry/costing-catalog", {}, token).then(setCostingLines).catch(reportLoadError);
    void api<JsonRecord[]>("/v1/jewelry/costing-profiles", {}, token).then(setCostingProfiles).catch(reportLoadError);
    void api<JsonRecord>("/v1/workspace/settings", {}, token)
      .then((settings) => setUsdToAed(Number(settings.usd_to_aed_exchange_rate || 3.70))).catch(reportLoadError);
    void Promise.all([
      "stone_type", "stone_shape", "gem_lab"
    ].map((lookupCategory) =>
      api<JsonRecord[]>(`/v1/reference/lookup-values?category=${lookupCategory}`, {}, token)
    )).then(([types, shapes, labs]) => setStoneChoices({
      stone_type: types.map((row) => String(row.value || "")).filter(Boolean),
      stone_shape: shapes.map((row) => String(row.value || "")).filter(Boolean),
      lab: labs.map((row) => String(row.value || "")).filter(Boolean)
    })).catch((reason: Error) => setError(reason.message));
    void Promise.all([
      api<JsonRecord[]>("/v1/reference/stone-attributes?attribute=color", {}, token),
      api<JsonRecord[]>("/v1/reference/stone-attributes?attribute=clarity", {}, token)
    ]).then(([colours, clarities]) => {
      const group = (rows: JsonRecord[]) => rows.reduce<Record<string, string[]>>((result, row) => {
        const stoneType = String(row.stoneType || "");
        const value = String(row.value || "");
        if (stoneType && value) result[stoneType] = [...(result[stoneType] || []), value];
        return result;
      }, {});
      setStoneAttributeChoices({ color: group(colours), clarity: group(clarities) });
    }).catch((reason: Error) => setError(reason.message));
  }, [token]);
  useEffect(() => {
    if (!costingProfiles.length) return;
    const customer = customers.find((row) => String(row.id) === customerId);
    const assignedId = Number(customer?.costingProfileId || 0);
    const fallback = costingProfiles.find((profile) => profile.customer_id == null && String(profile.status) === "Active" && String(profile.name) === "OctaDiam Standard")
      || costingProfiles.find((profile) => profile.customer_id == null && String(profile.status) === "Active");
    const profileId = assignedId || Number(fallback?.id || 0);
    if (!profileId) { setEffectiveCostingRates([]); return; }
    void api<JsonRecord>(`/v1/jewelry/costing-profiles/${profileId}`, {}, token).then((context) => {
      setEffectiveCostingRates(Array.isArray(context.lines) ? context.lines as JsonRecord[] : []);
      setEffectiveCostingName(String((context.profile as JsonRecord | undefined)?.name || "OctaDiam Standard"));
    }).catch((reason: Error) => setError(reason.message));
  }, [costingProfiles, customerId, customers, token]);
  useEffect(() => {
    if (!effectiveCostingRates.length) { setManufacturing([]); return; }
    setManufacturing(automaticManufacturingRows(effectiveCostingRates, metalType, stones, Number(expectedFinalWeight || 0), Number(actualFinalWeight || 0)));
  }, [effectiveCostingRates, metalType, stones, expectedFinalWeight, actualFinalWeight]);
  useEffect(() => {
    if (!category) { setSubcategories([]); return; }
    void api<JsonRecord[]>(`/v1/reference/jewelry-subcategories?category=${encodeURIComponent(category)}`, {}, token)
      .then(setSubcategories).catch((reason: Error) => setError(reason.message));
  }, [category, token]);
  useEffect(() => {
    if (!category || !subcategory || !metalType || !entryDate) {
      setGeneratedDesignId(""); return;
    }
    if (editId) { setGeneratedDesignId(String(savedDesign.design_id || "")); return; }
    void api<{ design_id: string }>(`/v1/jewelry/designs/next-identifier?category=${encodeURIComponent(category)}&subcategory=${encodeURIComponent(subcategory)}&metalType=${encodeURIComponent(metalType)}&entryDate=${encodeURIComponent(entryDate)}`, {}, token)
      .then((result) => setGeneratedDesignId(result.design_id)).catch((reason: Error) => setError(reason.message));
  }, [category, subcategory, metalType, entryDate, editId, savedDesign.design_id, token]);
  useEffect(() => {
    const purity = metalType.match(/(24|22|21|18|14|10|9|999|925)/)?.[1] || "";
    const family = metalType.toLowerCase().includes("silver") ? "silver" : "gold";
    const familyRates = metalRates[family] as JsonRecord | undefined;
    const rate = Number((familyRates?.dubai as JsonRecord | undefined)?.[purity] || 0);
    const populatedRate = Number.isFinite(rate) && rate > 0 ? rate.toFixed(2) : "0.00";
    if (!editId || Number(savedDesign.quoted_metal_price || 0) <= 0) setMetalPrice(populatedRate);
    setActualMetalPrice(populatedRate);
  }, [editId, metalType, metalRates, savedDesign.quoted_metal_price]);
  useEffect(() => {
    const sizeCategory = designSizeCategory(category);
    if (!sizeCategory) { setSizes([]); return; }
    void api<JsonRecord[]>(`/v1/reference/lookup-values?category=${encodeURIComponent(sizeCategory)}`, {}, token)
      .then(setSizes).catch((reason: Error) => setError(reason.message));
  }, [category, token]);
  useEffect(() => {
    if (!actualFinalWeight && expectedFinalWeight) setActualFinalWeight(expectedFinalWeight);
  }, [actualFinalWeight, expectedFinalWeight]);
  useEffect(() => {
    if (!actualNetWeight && expectedNetWeight) setActualNetWeight(expectedNetWeight);
  }, [actualNetWeight, expectedNetWeight]);
  useEffect(() => {
    setStones((current) => {
      let changed = false;
      const defaulted = current.map((stone) => {
        const updates: JsonRecord = {};
        if (Number(stone.actual_quantity || 0) <= 0 && Number(stone.expected_quantity ?? stone.quantity ?? 0) > 0) updates.actual_quantity = stone.expected_quantity ?? stone.quantity;
        if (Number(stone.actual_weight ?? stone.actual_final_weight ?? 0) <= 0 && Number(stone.expected_weight || 0) > 0) updates.actual_weight = stone.expected_weight;
        if (Number(stone.actual_cost_per_carat || 0) <= 0 && Number(stone.expected_cost_per_carat || 0) > 0) updates.actual_cost_per_carat = stone.expected_cost_per_carat;
        if (Object.keys(updates).length) { changed = true; return { ...stone, ...updates }; }
        return stone;
      });
      return changed ? defaulted : current;
    });
  }, [stones.map((stone) => `${stone.expected_quantity ?? stone.quantity ?? 0}:${stone.expected_weight || 0}:${stone.expected_cost_per_carat || 0}`).join("|")]);

  return (
    <form className="workflow-form" onSubmit={async (event) => {
      event.preventDefault(); setError(""); setBusy(true);
      const requestedSaveMode = ((event.nativeEvent as SubmitEvent).submitter as HTMLElement | null)?.dataset.saveMode === "new_version" ? "new_version" : "current";
      const values = new FormData(event.currentTarget);
      try {
        const categoryValue = text(values.get("category"));
        const subcategory = text(values.get("sub_category"));
        const metalType = text(values.get("metal_type"));
        const entryDate = text(values.get("entry_date"));
        let designId = text(values.get("design_id"));
        if (!designId) {
          const next = await api<{ design_id: string }>(
            `/v1/jewelry/designs/next-identifier?category=${encodeURIComponent(categoryValue)}&subcategory=${encodeURIComponent(subcategory)}&metalType=${encodeURIComponent(metalType)}&entryDate=${encodeURIComponent(entryDate)}`,
            {}, token
          );
          designId = next.design_id;
        }
        const expectedWeight = number(values.get("expected_final_weight"));
        const media = await pendingMediaPayload(designMedia);
        await api(editId ? `/v1/jewelry/designs/${editId}` : "/v1/jewelry/designs", {
          method: editId ? "PATCH" : "POST",
          body: JSON.stringify({
            ...(editId ? { expected_updated_at: savedDesign.updated_at } : {}),
            save_mode: editId ? requestedSaveMode : "new_version",
            design: {
              design_id: designId, entry_date: entryDate, category: categoryValue, sub_category: subcategory,
              designer_design_id: text(values.get("designer_design_id")),
              customer_id: text(values.get("customer_id")) || null, metal_type: metalType,
              metal_colour: text(values.get("metal_colour")),
              designer_name: text(values.get("designer_name")), size_specification: text(values.get("size_specification")),
              custom_size_specification: text(values.get("custom_size_specification")),
              expected_final_weight: expectedWeight, expected_net_weight: number(values.get("expected_net_weight")),
              actual_final_weight: number(values.get("actual_final_weight")), actual_net_weight: number(values.get("actual_net_weight")),
              quoted_metal_price: number(values.get("quoted_metal_price")), actual_metal_price: number(values.get("actual_metal_price")),
              quoted_loss_pct: number(values.get("quoted_loss_pct")),
              stone_markup_pct: number(values.get("stone_markup_pct")),
              final_item_markup_pct: number(values.get("final_item_markup_pct")),
              final_quote: calculatedSalePrice,
              calculation_profile_version: 1, calculation_profile: {}, item_description: "", finish_specification: "",
              overall_length_mm: number(values.get("overall_length_mm")),
              overall_width_mm: number(values.get("overall_width_mm")),
              overall_height_mm: number(values.get("overall_height_mm")), notes: text(values.get("notes"))
            },
            stones: stones.map((stone, index) => ({
              line_uid: String(stone.line_uid || `stone-${crypto.randomUUID()}`),
              stone_type: String(stone.stone_type || ""),
              stone_shape: String(stone.stone_shape || ""),
              stone_size: String(stone.stone_size || ""),
              color: String(stone.color || ""),
              clarity: String(stone.clarity || ""),
              lab: String(stone.lab || ""),
              certificate_id: String(stone.certificate_id || ""),
              quantity: Math.trunc(Number(stone.expected_quantity || 0)),
              expected_quantity: Math.trunc(Number(stone.expected_quantity || 0)),
              actual_quantity: Math.trunc(Number(stone.actual_quantity || 0)),
              inhouse_quantity: Math.trunc(Number(stone.inhouse_quantity || 0)),
              inhouse_weight: Number(stone.inhouse_weight || 0),
              expected_weight: Number(stone.expected_weight || 0),
              actual_weight: Number(stone.actual_weight || 0),
              expected_cost_per_carat: Number(stone.expected_cost_per_carat || 0),
              actual_cost_per_carat: Number(stone.actual_cost_per_carat || 0),
              markup_pct: Number(stone.markup_pct || 0),
              diamond_provider_key: String(stone.diamond_provider_key || ""),
              diamond_internal_status: String(stone.diamond_internal_status || "Available"),
              diamond_internal_record_id: stone.diamond_internal_record_id ? Number(stone.diamond_internal_record_id) : null,
              diamond_snapshot_json: stone.diamond_snapshot_json || null,
              line_number: index + 1
            })),
            manufacturing: manufacturing.map((row) => {
              const process = String(row.service_type || row.process_name || "");
              const expected = Number(row.expected_cost || 0);
              return {
                process_name: process, service_department: String(row.service_department || ""),
                service_type: String(row.service_type || process), unit: String(row.unit || ""),
                rate_minor: Math.trunc(Number(row.rate_minor || 0)),
                expected_quantity: Number(row.expected_quantity || 0),
                actual_quantity: Number(row.actual_quantity || 0),
                expected_cost: expected,
                actual_cost: Number(row.actual_cost || 0)
              };
            }),
            new_media: media,
            deleted_media_ids: deletedDesignMedia
          })
        }, token);
        await queryClient.invalidateQueries({ queryKey: ["entity", "diamondStock"] });
        await onSaved();
      } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not save design."); }
      finally { setBusy(false); }
    }}>
      <WorkflowTabs
        active={activeTab}
        onChange={setActiveTab}
        tabs={mode === "costing" ? [["expected", "Expected cost"],["actual", "Actual cost"],["markup", "Markup & pricing"],["summary", "Financial summary"]] : [["identity", "Technical information"]]}
      />
      {readOnly && <div className="historical-readonly-banner"><LockKeyhole size={16} /> Historical version - all values are read only.</div>}
      <fieldset className="design-form-fieldset" disabled={readOnly}>
      <div className={activeTab === "identity" ? "workflow-tab-content jewelry-technical" : "workflow-tab-content hidden"}>
      <section className="design-photo-preview create-preview">
        <div aria-hidden="true" />
        <div className="design-photo-frame">
          {(() => {
            const item = ["Hero Image", "Render Picture", "Final Photo", "Reference Photo"]
              .flatMap((kind) => designMedia.filter((entry) => !entry.stone_line_uid && entry.media_type === kind).slice(-1)).at(0);
            const saved = selectDisplayMedia(existingMedia.filter((entry) => !deletedDesignMedia.includes(Number(entry.id))));
            return item ? <PendingImage file={item.file} /> : saved ? <StoredImage mediaId={Number(saved.id)} token={token} alt={String(saved.original_name || "Design preview")} /> : <div className="preview-placeholder">No preview picture selected</div>;
          })()}
        </div>
      </section>
      <section className="technical-section"><h3>Design identity</h3>
      <div className="design-identity-layout">
        <div className="design-identity-row identity-row-two">
          <Field name="design_id" label="Application Design ID" value={generatedDesignId} readOnly required helpTooltip="Generated after category, sub category, metal and entry date are selected." />
          <Field name="designer_design_id" label="Designer’s initial Design ID" defaultValue={String(savedDesign.designer_design_id || "")} />
        </div>
        <div className="design-identity-row identity-row-five">
          <Field name="entry_date" label="Entry date" type="date" required value={entryDate} onChange={setEntryDate} />
          <Choice name="designer_name" label="Designer / prepared by" rows={designers} valueKey="value" value={designerName} onChange={setDesignerName} />
          <Choice name="customer_id" label="Customer" rows={customers} valueKey="id" labelKeys={["code", "name"]} value={customerId} onChange={setCustomerId} />
          <Choice name="category" label="Category" rows={categories} valueKey="value" required value={category} onChange={setCategory} />
          <Choice name="sub_category" label="Sub category" rows={subcategories} valueKey="value" required value={subcategory} onChange={setSubcategory} />
        </div>
        <div className="design-identity-row identity-row-four">
          <Choice name="metal_type" label="Metal type / karat" rows={metals} valueKey="value" required value={metalType} onChange={setMetalType} />
          <Choice name="metal_colour" label="Metal colour" rows={metalColours} valueKey="value" required value={metalColour} onChange={setMetalColour} />
          <Field name="expected_final_weight" label="Expected final wt. (g)" type="number" required step="0.01" value={expectedFinalWeight} onChange={setExpectedFinalWeight} />
          <Field name="expected_net_weight" label="Expected net wt. (g)" type="number" required step="0.01" value={expectedNetWeight} onChange={setExpectedNetWeight} />
        </div>
        <div className="design-identity-row identity-row-four identity-size-row">
        {sizes.length > 0 && (
          <Choice
            name="size_specification"
            label="Size specification"
            rows={sizes}
            valueKey="value"
            value={sizeSpecification}
            onChange={setSizeSpecification}
          />
        )}
        {sizeSpecification.toLowerCase().includes("custom") && (
          <>
            <Field name="custom_size_specification" label="Custom size specification" required defaultValue={String(savedDesign.custom_size_specification || "")} />
            <div className="dimension-field"><Field name="overall_length_mm" label="Length (mm)" type="number" step="0.01" defaultValue={String(savedDesign.overall_length_mm ?? "")} /></div>
            <div className="dimension-field"><Field name="overall_width_mm" label="Width (mm)" type="number" step="0.01" defaultValue={String(savedDesign.overall_width_mm ?? "")} /></div>
            <div className="dimension-field"><Field name="overall_height_mm" label="Height (mm)" type="number" step="0.01" defaultValue={String(savedDesign.overall_height_mm ?? "")} /></div>
          </>
        )}
        </div>
      </div>
      </section>
      <section className="technical-section"><h3>Stone details</h3>
        <EditableRows
          rows={stones}
          setRows={setStones}
          columns={[
            ["stone_type", "Stone type", "text"], ["stone_shape", "Shape", "text"], ["stone_size", "Size", "text"],
            ["color", "Colour", "text"], ["clarity", "Clarity", "text"],
            ["expected_quantity", "Expected qty", "integer"], ["expected_weight", "Expected wt. (ct)", "number"]
          ]}
          addLabel="Add stone row"
          allowCopy
          toolbarActions={<DiamondStockPicker token={token} usdToAed={usdToAed}
            existingProviderKeys={stones.map((stone) => String(stone.diamond_provider_key || ""))}
            onAdd={(rows, certificates) => {
            const types = stoneChoices.stone_type || [], shapes = stoneChoices.stone_shape || [];
            const normalized = rows.map((row) => ({ ...row,
              stone_type: types.find((value) => value.toLowerCase() === "diamond") || types.find((value) => value.toLowerCase().includes("diamond")) || row.stone_type,
              stone_shape: shapes.find((value) => value.toLowerCase() === String(row.stone_shape || "").toLowerCase()) || row.stone_shape
            }));
            setStones((current) => [...current, ...normalized]); setDesignMedia((current) => [...current, ...certificates]);
          }} />}
          rowActions={(stone, index) => <ManageFiles iconOnly title={`Stone ${index + 1} certificate files`}
            categories={["Stone Certificate"]} pending={designMedia} setPending={setDesignMedia}
            deletedIds={deletedDesignMedia} setDeletedIds={setDeletedDesignMedia}
            stoneLineUid={String(stone.line_uid || "")} />}
          choices={stoneChoices}
          choiceProvider={(key, row) => {
            if (key !== "color" && key !== "clarity") return stoneChoices[key] || [];
            return stoneAttributeChoices[key]?.[String(row.stone_type || "")] || [];
          }}
        />
      </section>
      <section className="technical-section"><h3>Design files</h3>
        <InlineDesignFiles categories={designFileCategories} pending={designMedia} setPending={setDesignMedia} existing={existingMedia} deletedIds={deletedDesignMedia} setDeletedIds={setDeletedDesignMedia} token={token} />
      </section>
      <section className="technical-section design-notes-section"><h3>Notes</h3>
        <Field name="notes" label="Notes" multiline defaultValue={String(savedDesign.notes || "")} />
      </section></div>
      <div className={activeTab === "expected" ? "workflow-tab-content" : "workflow-tab-content hidden"}>
        <section className="technical-section"><SectionTitle tooltip="Technical weights come from Technical Information. Metal loss is controlled in Markup & pricing.">01 · Expected metal cost</SectionTitle><div className="form-grid two cost-fields"><Field name="quoted_metal_price" label="Expected metal price / g (AED)" type="number" step="0.01" value={metalPrice} readOnly helpTooltip="Automatically populated from the latest saved Dubai gold/silver rate." /><CostValue label="Expected metal cost" value={money(expectedMetalCost)} /></div></section>
        <section className="technical-section"><SectionTitle tooltip="All technical stone lines are listed below. Enter the expected cost per carat.">02 · Expected stone cost</SectionTitle><StoneCostTable mode="expected" stones={stones} setStones={setStones} /><CostValue label="Expected stone cost" value={money(expectedStoneCost)} /></section>
        <section className="technical-section"><SectionTitle tooltip="Planned manufacturing expenses in AED.">03 · Expected manufacturing cost</SectionTitle><ManufacturingCostRows mode="expected" rows={manufacturing} setRows={setManufacturing} costingLines={effectiveCostingRates} profileName={effectiveCostingName} /><CostValue label="Expected manufacturing cost" value={money(expectedManufacturingCost)} /></section>
      </div>
      <div className={activeTab === "actual" ? "workflow-tab-content" : "workflow-tab-content hidden"}>
        <section className="technical-section"><SectionTitle tooltip="Record the completed item weights and the actual metal rate.">01 · Actual metal cost</SectionTitle><div className="form-grid three cost-fields"><Field name="actual_final_weight" label="Actual final wt. (g)" type="number" step="0.01" value={actualFinalWeight} onChange={setActualFinalWeight} /><Field name="actual_net_weight" label="Actual net wt. (g)" type="number" step="0.01" value={actualNetWeight} onChange={setActualNetWeight} /><Field name="actual_metal_price" label="Actual metal price / g (AED)" type="number" step="0.01" value={actualMetalPrice} onChange={setActualMetalPrice} /></div><CostValue label="Actual metal cost" value={money(actualMetalCost)} /></section>
        <section className="technical-section"><SectionTitle tooltip="Enter the actual quantity, weight, and cost per carat for every technical stone line.">02 · Actual stone cost</SectionTitle><StoneCostTable mode="actual" stones={stones} setStones={setStones} /><CostValue label="Actual stone cost" value={money(actualStoneCost)} /></section>
        <section className="technical-section"><SectionTitle tooltip="Final manufacturing expenses in AED.">03 · Actual manufacturing cost</SectionTitle><ManufacturingCostRows mode="actual" rows={manufacturing} setRows={setManufacturing} costingLines={effectiveCostingRates} profileName={effectiveCostingName} /><CostValue label="Actual manufacturing cost" value={money(actualManufacturingCost)} /></section>
      </div>
      <div className={activeTab === "markup" ? "workflow-tab-content" : "workflow-tab-content hidden"}>
        <section className="technical-section"><SectionTitle tooltip="Stone markup is applied once to expected stone cost. Final item markup is applied to expected metal, marked-up stones, and expected manufacturing.">Markup settings</SectionTitle><div className="form-grid three"><Field name="quoted_loss_pct" label="Metal loss %" type="number" step="0.01" value={metalLossPct} onChange={setMetalLossPct} /><Field name="stone_markup_pct" label="Stones markup %" type="number" step="0.01" value={stoneMarkupPct} onChange={setStoneMarkupPct} /><Field name="final_item_markup_pct" label="Final item markup %" type="number" step="0.01" value={finalItemMarkupPct} onChange={setFinalItemMarkupPct} /></div></section>
        <div className="workflow-summary pricing-summary"><CostValue label="Stone markup amount" value={money(stoneMarkupAmount)} /><CostValue label="Subtotal before final item markup" value={money(subtotalBeforeFinalMarkup)} /><CostValue label="Final item markup amount" value={money(finalItemMarkupAmount)} /><CostValue label="Calculated sale price" value={money(calculatedSalePrice)} emphasis /></div>
      </div>
      <div className={activeTab === "summary" ? "workflow-tab-content" : "workflow-tab-content hidden"}>
        <section className="technical-section"><h3>Design summary</h3><div className="workflow-summary summary-details">
          <CostValue label="Application Design ID" value={generatedDesignId || "Pending selections"} /><CostValue label="Entry date" value={entryDate || "—"} /><CostValue label="Design customer" value={String(customers.find((row) => String(row.id) === customerId)?.name || "—")} /><CostValue label="Category" value={category || "—"} /><CostValue label="Sub category" value={subcategory || "—"} />
          <CostValue label="Size specification" value={sizeSpecification || "—"} /><CostValue label="Metal type" value={metalType || "—"} /><CostValue label="Metal colour" value={metalColour || "—"} /><CostValue label="Manufacturing lines" value={String(manufacturing.length)} /><CostValue label="Expected / actual final weight" value={`${expectedFinalWeight || "0"} / ${actualFinalWeight || "0"} g`} /><CostValue label="Expected / actual net weight" value={`${expectedNetWeight || "0"} / ${actualNetWeight || "0"} g`} /><CostValue label="Stone lines" value={String(stones.length)} />
        </div></section>
        <section className="technical-section"><h3>Financial summary</h3><p className="financial-summary-caption">Estimated vs actual vs sale</p><div className="table-scroll"><table className="financial-comparison"><thead><tr><th>Component</th><th>Estimated cost</th><th>Actual cost</th><th>Sale calculation</th></tr></thead><tbody>{[
          ["Metal", expectedMetalCost, actualMetalCost, expectedMetalCost],
          ["Stones", expectedStoneCost, actualStoneCost, expectedStoneCost + stoneMarkupAmount],
          ["Manufacturing", expectedManufacturingCost, actualManufacturingCost, expectedManufacturingCost],
          ["Production subtotal", expectedMetalCost + expectedStoneCost + expectedManufacturingCost, actualMetalCost + actualStoneCost + actualManufacturingCost, subtotalBeforeFinalMarkup],
          ["Final item markup", null, null, finalItemMarkupAmount],
          ["Sale price", null, null, calculatedSalePrice],
          ["Gross margin vs actual", null, null, calculatedSalePrice - actualMetalCost - actualStoneCost - actualManufacturingCost]
        ].map(([label, estimated, actual, sale]) => <tr key={String(label)}><th>{label}</th><td>{estimated === null ? "—" : money(Number(estimated))}</td><td>{actual === null ? "—" : money(Number(actual))}</td><td>{sale === null ? "—" : money(Number(sale))}</td></tr>)}</tbody></table></div><div className="cost-variance"><span>Actual cost variance vs estimate</span><strong>{money((actualMetalCost + actualStoneCost + actualManufacturingCost) - (expectedMetalCost + expectedStoneCost + expectedManufacturingCost))}</strong></div></section>
      </div>
      </fieldset>
      {!readOnly && <footer className="form-footer">{error&&<div className="error-banner">{error}</div>}{editId ? <><Button type="submit" data-save-mode="current" className="action-button action-save" isLoading={busy}>Save Current</Button><Button type="submit" data-save-mode="new_version" className="action-button action-create" isLoading={busy}>Create New Version</Button></> : <Button type="submit" className="action-button action-create" isLoading={busy}>Create jewelry design</Button>}</footer>}
    </form>
  );
}

export function JobCardForm({ token, onSaved, initialDetail, editId, readOnly = false, mode = "technical" }: FormProps & { initialDetail?: JsonRecord; editId?: string; readOnly?: boolean; mode?:"technical"|"costing" }) {
  const savedCard = (initialDetail?.job_card as JsonRecord) || {};
  const [activeTab, setActiveTab] = useState(mode === "costing" ? "repairs" : "details");
  const [jobType, setJobType] = useState(String(savedCard.work_type || savedCard.job_type || "New"));
  const [status, setStatus] = useState(String(savedCard.status || "Draft"));
  const [priority, setPriority] = useState(String(savedCard.priority || "Normal"));
  const [jobDate, setJobDate] = useState(String(savedCard.job_date || today()).slice(0,10));
  const [dueDate, setDueDate] = useState(String(savedCard.due_date || "").slice(0,10));
  const [description, setDescription] = useState(String(savedCard.description || ""));
  const [customerStoneNotes, setCustomerStoneNotes] = useState(String(savedCard.customer_stone_notes || ""));
  const [customerGoldWeight, setCustomerGoldWeight] = useState(String(savedCard.customer_gold_weight || 0));
  const [context, setContext] = useState<JsonRecord>({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [stages, setStages] = useState<JsonRecord[]>([]);
  const [jobStones, setJobStones] = useState<JsonRecord[]>([]);
  const [repairOperations, setRepairOperations] = useState<JsonRecord[]>([]);
  const [jobMedia, setJobMedia] = useState<PendingMedia[]>([]);
  const [deletedJobMedia, setDeletedJobMedia] = useState<number[]>([]);
  const [customerId, setCustomerId] = useState(String(savedCard.customer_id || ""));
  const [designId, setDesignId] = useState(String(savedCard.design_id_fk || ""));
  const [itemType, setItemType] = useState(String(savedCard.item_type || ""));
  const [itemSubType, setItemSubType] = useState(String(savedCard.item_sub_type || ""));
  const [metalType, setMetalType] = useState(String(savedCard.metal_type || ""));
  const [metalColour, setMetalColour] = useState(String(savedCard.metal_colour || ""));
  const [expectedFinalWeight, setExpectedFinalWeight] = useState(String(savedCard.expected_final_weight || 0));
  const [expectedNetWeight, setExpectedNetWeight] = useState(String(savedCard.expected_net_weight || 0));
  const [actualFinalWeight, setActualFinalWeight] = useState(String(savedCard.actual_final_weight || 0));
  const [actualNetWeight, setActualNetWeight] = useState(String(savedCard.actual_final_net_weight || 0));
  const [stoneDetailsOpen, setStoneDetailsOpen] = useState(false);
  useEffect(() => {
    void api<JsonRecord>("/v1/operations/job-cards/editor-context", {}, token).then((data) => {
      setContext(data);
      const firstStage = (data.job_stage as JsonRecord[] || [])[0];
      setStages(initialDetail ? (initialDetail.stages as JsonRecord[] || []) : [{
        stage_name: firstStage?.value || "Other",
        stage_status: "Pending",
        operator_name: "",
        factory_workshop: "OCTADIAM",
        issue_type: "",
        issued_weight: 0,
        time_out: "",
        return_type: "",
        returned_weight: 0,
        recovery_weight: 0,
        time_in: "",
        notes: ""
      }]);
      setJobStones(initialDetail ? (initialDetail.stones as JsonRecord[] || []) : []);
      setRepairOperations(initialDetail ? (initialDetail.repair_operations as JsonRecord[] || []) : [{ costing_rate_id: "", service_department: "", service_type: "", unit: "", quantity: 1, job_notes: "" }]);
    })
      .catch((reason: Error) => setError(reason.message));
  }, [token]);
  useEffect(() => {
    const stoneWeight = jobStones.reduce((sum, row) => sum + Math.max(0, Number(row.issued_weight || 0) - Number(row.returned_weight || 0)) * 0.2, 0);
    setActualNetWeight(Math.max(0, Number(actualFinalWeight || 0) - stoneWeight).toFixed(3));
  }, [actualFinalWeight, jobStones]);
  return (
    <form className="workflow-form" onSubmit={async (event) => {
      event.preventDefault(); setBusy(true); setError("");
      const requestedSaveMode = ((event.nativeEvent as SubmitEvent).submitter as HTMLElement | null)?.dataset.saveMode === "new_version" ? "new_version" : "current";
      const values = new FormData(event.currentTarget);
      try {
        const stageValues = stages.map((row) => ({
          stage_name: String(row.stage_name || ""),
          stage_status: String(row.stage_status || "Pending"),
          operator_name: String(row.operator_name || ""),
          powder_weight: 0,
          time_in: String(row.time_in || ""),
          time_out: String(row.time_out || ""),
          issued_weight: Number(row.issued_weight || 0),
          solder_issued_weight: 0,
          returned_weight: Number(row.returned_weight || 0),
          recovery_weight: Number(row.recovery_weight || 0),
          notes: String(row.notes || ""),
          factory_workshop: String(row.factory_workshop || "OCTADIAM"),
          issue_type: String(row.issue_type || ""),
          return_type: String(row.return_type || "")
        }));
        const media = await pendingMediaPayload(jobMedia);
        await api(editId ? `/v1/operations/job-cards/${editId}` : "/v1/operations/job-cards", {
          method: editId ? "PATCH" : "POST",
          body: JSON.stringify({
            ...(editId ? { expected_updated_at: savedCard.updated_at } : {}),
            save_mode: editId ? requestedSaveMode : "new_version",
            idempotency_key: idempotencyKey(),
            job_card: {
              job_type: text(values.get("job_type")), status: text(values.get("status")),
              priority: text(values.get("priority")), job_date: text(values.get("job_date")),
              due_date: text(values.get("due_date")), customer_id: customerId,
              design_id_fk: text(values.get("design_id_fk")) || null, item_type: text(values.get("item_type")),
              item_sub_type: text(values.get("item_sub_type")), metal_type: text(values.get("metal_type")),
              metal_colour: text(values.get("metal_colour")), description: text(values.get("description")),
              customer_gold_weight: number(values.get("customer_gold_weight")),
              customer_stone_notes: text(values.get("customer_stone_notes")),
              expected_final_weight: number(values.get("expected_final_weight")),
              actual_final_weight: number(values.get("actual_final_weight")),
              expected_net_weight: number(values.get("expected_net_weight")),
              actual_final_net_weight: number(values.get("actual_final_net_weight"))
            },
            stages: stageValues,
            stones: jobStones
              .filter((row) => Number(row.issued_quantity || 0) > 0 && Number(row.issued_weight || 0) > 0)
              .map((row) => ({
                inventory_stone_id: Number(row.inventory_stone_id),
                stage_sequence_number: Number(row.stage_sequence_number) || null,
                stock_code: String(row.stock_code || ""),
                stone_type: String(row.stone_type || ""),
                stone_shape: String(row.stone_shape || ""),
                stone_identifier: String(row.certificate_id || ""),
                stone_size: String(row.stone_size || ""),
                issued_quantity: Math.trunc(Number(row.issued_quantity || 0)),
                issued_weight: Number(row.issued_weight || 0),
                returned_quantity: Math.trunc(Number(row.returned_quantity || 0)),
                returned_weight: Number(row.returned_weight || 0),
                actual_used_quantity: Math.max(0, Math.trunc(Number(row.issued_quantity || 0) - Number(row.returned_quantity || 0))),
                actual_used_weight: Math.max(0, Number(row.issued_weight || 0) - Number(row.returned_weight || 0)),
                notes: String(row.notes || "")
              })),
            repair_operations: jobType === "Repair"
              ? repairOperations
                .filter((row) => Number(row.costing_rate_id || 0) > 0 && Number(row.quantity || 0) > 0)
                .map((row) => ({
                  costing_rate_id: Number(row.costing_rate_id),
                  quantity: Number(row.quantity),
                  job_notes: String(row.job_notes || "")
                }))
              : [],
            new_media: media.map((item, index) => ({ ...item, is_main: index === 0 })),
            deleted_media_ids: deletedJobMedia, main_media_id: null
          })
        }, token);
        await onSaved();
      } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not save Job Card."); }
      finally { setBusy(false); }
    }}><fieldset disabled={readOnly} className="workflow-fieldset">
      {mode !== "costing" && <div className="job-card-hero">
        <div className="design-photo-preview job-photo-preview">
          {jobMedia.find((item) => item.is_main) ? <PendingImage file={jobMedia.find((item) => item.is_main)!.file} />
            : Number((initialDetail?.media as JsonRecord[] || []).find((item)=>Number(item.is_main)===1)?.id||0)
              ? <JobCardStoredImage mediaId={Number((initialDetail?.media as JsonRecord[]).find((item)=>Number(item.is_main)===1)?.id)} token={token}/>
            : designId && Number((context.designs as JsonRecord[] || []).find((row) => String(row.id) === designId)?.display_media_id)
              ? <StoredImage mediaId={Number((context.designs as JsonRecord[]).find((row) => String(row.id) === designId)?.display_media_id)} token={token} alt="Linked jewelry design" />
              : <div className="preview-placeholder">No preview picture selected</div>}
        </div>
        {!readOnly && <label className="file-add-button"><Upload size={17}/><span>Upload or take job picture</span><input type="file" accept="image/*" capture="environment" onChange={(event) => { const file=event.currentTarget.files?.[0]; if(!file)return; setJobMedia((current)=>[...current.map((item)=>({...item,is_main:false})),{key:crypto.randomUUID(),file,media_type:"Job Card Picture",is_main:true}]); event.currentTarget.value=""; }}/></label>}
      </div>}
      <WorkflowTabs
        active={activeTab}
        onChange={setActiveTab}
        tabs={mode === "costing" ? [["repairs", "Repair costing"],["summary", "Cost summary"]] : [
          ["details", "Job details"],
          ["stages", `Workshop stages (${stages.length})`],
          ["files", "Pictures & files"],
          ["summary", "Summary"]
        ]}
      />
      <div className={activeTab === "details" ? "workflow-tab-content" : "workflow-tab-content hidden"}>
      <div className="job-number-above-fields">Job Card <strong>{String(savedCard.job_card_number || context.next_job_card_number || "Generated on save")}</strong></div>
      <div className="form-grid three">
        <Choice name="job_type" label="Job type" options={["New", "Repair", "Casting"]} required value={jobType} onChange={setJobType} />
        <Choice name="status" label="Status" options={["Draft", "In Progress", "On hold", "Closed"]} required value={status} onChange={setStatus} />
        <Choice name="priority" label="Priority" options={["Low", "Normal", "High", "Urgent"]} required value={priority} onChange={setPriority} />
        <Field name="job_date" label="Job date" type="date" required value={jobDate} onChange={setJobDate} />
        <Field name="due_date" label="Due date" type="date" value={dueDate} onChange={setDueDate} />
        <SearchableChoice name="customer_id" label="Customer" rows={context.customers as JsonRecord[] || []} value={customerId} onChange={setCustomerId} valueKey="id" labelKeys={["customer_code", "customer_name"]} required />
        {jobType !== "Repair" && <SearchableChoice name="design_id_fk" label="Linked jewelry design" rows={context.designs as JsonRecord[] || []} value={designId} valueKey="id" labelKeys={["design_id", "created_at", "category", "sub_category", "metal_type"]} filterOptions={{category:(context.jewelry_categories as JsonRecord[]||[]).map(row=>String(row.value||"")),sub_category:(context.jewelry_subcategories as JsonRecord[]||[]).map(row=>String(row.value||"")),metal_type:[...new Set((context.designs as JsonRecord[]||[]).map(row=>String(row.metal_type||"")).filter(Boolean))]}} onChange={(value) => { setDesignId(value); const design=(context.designs as JsonRecord[] || []).find((row)=>String(row.id)===value); if(!design)return; setCustomerId(String(design.customer_id||"")); setItemType(String(design.category||"")); setItemSubType(String(design.sub_category||"")); setMetalType(String(design.metal_type||"")); setMetalColour(String(design.metal_colour||"")); setExpectedFinalWeight(String(design.expected_final_weight||0)); setExpectedNetWeight(String(design.expected_net_weight||0)); }} />}
        <Choice name="item_type" label="Category" options={(context.jewelry_categories as JsonRecord[] || []).map((row)=>String(row.value||"")).filter(Boolean)} value={itemType} onChange={(value)=>{setItemType(value);if(itemSubType&&!((context.jewelry_subcategories as JsonRecord[]||[]).some(row=>String(row.category)===value&&String(row.value)===itemSubType)))setItemSubType("");}} />
        <Choice name="item_sub_type" label="Subcategory" options={(context.jewelry_subcategories as JsonRecord[] || []).filter((row)=>!itemType||String(row.category||"")===itemType).map((row)=>String(row.value||"")).filter(Boolean)} value={itemSubType} onChange={setItemSubType} />
        <Choice name="metal_type" label="Metal type" options={[...new Set((context.designs as JsonRecord[] || []).map((row)=>String(row.metal_type||"")).filter(Boolean))]} value={metalType} onChange={setMetalType} />
        <Choice name="metal_colour" label="Metal colour" options={[...new Set((context.designs as JsonRecord[] || []).map((row)=>String(row.metal_colour||"")).filter(Boolean))]} value={metalColour} onChange={setMetalColour} />
        <Field name="customer_gold_weight" label="Customer gold (g)" type="number" step="0.01" required={jobType === "Repair"} value={customerGoldWeight} onChange={setCustomerGoldWeight} />
      </div>
      <Field name="description" label="Description / work requested" multiline value={description} onChange={setDescription} />
      <Field name="customer_stone_notes" label="Customer stones / supplied material notes" multiline value={customerStoneNotes} onChange={setCustomerStoneNotes} />
      </div>
      <div className={activeTab === "stages" ? "workflow-tab-content" : "workflow-tab-content hidden"}>
        <EditableRows
          rows={stages}
          setRows={setStages}
          columns={[
            ["factory_workshop", "Factory / workshop", "text"], ["stage_status", "Status", "text"],
            ["stage_name", "Stage", "text"], ["operator_name", "Operator", "text"],
            ["issue_type", "Issue type", "text"], ["issued_weight", "Issued g", "number"],
            ["time_out", "Time out", "datetime-local"], ["return_type", "Return type", "text"],
            ["returned_weight", "Return g", "number"], ["recovery_weight", "Recovery g", "number"],
            ["time_in", "Time in", "datetime-local"], ["notes", "Notes", "text"]
          ]}
          addLabel="Add workshop stage"
          choices={{
            factory_workshop: (context.job_workshop as JsonRecord[] || []).map((row) => String(row.value || "")),
            stage_status: ["Pending", "In Progress", "Completed", "Skipped"],
            stage_name: (context.job_stage as JsonRecord[] || []).map((row) => String(row.value || "")),
            operator_name: (context.job_operator as JsonRecord[] || []).map((row) => String(row.value || "")),
            issue_type: (context.job_issue_type as JsonRecord[] || []).map((row) => String(row.value || "")),
            return_type: (context.job_issue_type as JsonRecord[] || []).map((row) => String(row.value || ""))
          }}
          rowActions={(row, rowIndex) => {
            const settingStones=["setting","settings"].includes(String(row.stage_name||"").toLowerCase()) && ["stone","stones","stone(s)"].includes(String(row.issue_type||"").toLowerCase());
            return settingStones ? <Button type="button" size="sm" className="action-button action-view" title="Add or manage setting stones" onPress={()=>setStoneDetailsOpen(true)}><BadgeCheck size={15}/> Details</Button> : null;
          }}
          rowActionsAfterDelete
          onCellChange={(row,key,value)=>{
            const next={...row,[key]:value};
            if(key==="issue_type") next.return_type=value;
            if((key==="returned_weight"||key==="recovery_weight") && !(Number(row.issued_weight||0)>0 && String(row.time_out||"").trim())) return row;
            return next;
          }}
          isCellDisabled={(row,key)=>{
            const settingStones=["setting","settings"].includes(String(row.stage_name||"").toLowerCase()) && ["stone","stones","stone(s)"].includes(String(row.issue_type||"").toLowerCase());
            if(key==="issued_weight" && settingStones) return true;
            return ["returned_weight","recovery_weight","time_in"].includes(key) && !settingStones && !(Number(row.issued_weight||0)>0 && String(row.time_out||"").trim());
          }}
        />
      </div>
      {stoneDetailsOpen && <div className="dialog-backdrop nested-dialog" role="presentation"><section className="workflow-dialog stone-setting-details-dialog" role="dialog" aria-modal="true" aria-label="Stone setting details"><header><div><p className="eyebrow">Workshop stage details</p><h2>Stone setting details</h2></div><button type="button" className="dialog-close" onClick={()=>setStoneDetailsOpen(false)} aria-label="Close"><X/></button></header><div className="workflow-body"><JobStoneEditor context={context} rows={jobStones} setRows={setJobStones} stages={stages}/><footer className="form-footer"><Button type="button" className="action-button action-save" onPress={()=>setStoneDetailsOpen(false)}>Done</Button></footer></div></section></div>}
      {jobType === "Repair" && (
        <div className={activeTab === "repairs" ? "workflow-tab-content" : "workflow-tab-content hidden"}>
          <DynamicRepairOperationEditor rows={repairOperations} setRows={setRepairOperations} rates={context.costing_rates as JsonRecord[] || []} customerId={customerId} customers={context.customers as JsonRecord[] || []} defaultProfile={context.default_costing_profile as JsonRecord || {}} />
        </div>
      )}
      <div className={activeTab === "files" ? "workflow-tab-content" : "workflow-tab-content hidden"}>
        <InlineDesignFiles categories={["Job Card Picture", "Workshop Document", "Customer Reference"]} pending={jobMedia} setPending={setJobMedia} existing={initialDetail?.media as JsonRecord[] || []} deletedIds={deletedJobMedia} setDeletedIds={readOnly ? undefined : setDeletedJobMedia} token={token} mediaKind="job-card"/>
      </div>
      <div className={activeTab === "summary" ? "workflow-tab-content" : "workflow-tab-content hidden"}>
        {jobType !== "Repair" && <div className="form-grid four job-weight-summary">
          <Field name="expected_final_weight" label="Expected final wt. (g)" type="number" step="0.01" value={expectedFinalWeight} onChange={setExpectedFinalWeight}/>
          <Field name="expected_net_weight" label="Expected net wt. (g)" type="number" step="0.01" value={expectedNetWeight} onChange={setExpectedNetWeight}/>
          <Field name="actual_final_weight" label="Actual final wt. (g)" type="number" step="0.01" value={actualFinalWeight} onChange={setActualFinalWeight}/>
          <Field name="actual_final_net_weight" label="Actual net wt. (g)" type="number" step="0.01" value={actualNetWeight} onChange={setActualNetWeight}/>
        </div>}
        <div className="workflow-summary">
          <strong>{jobType} Job Card</strong>
          <span>{stages.length} workshop stage{stages.length === 1 ? "" : "s"}</span>
          <span>{jobStones.filter((row) => Number(row.issued_quantity || 0) > 0).length} issued stone line(s)</span>
          {jobType === "Repair" && <span>{repairOperations.filter((row) => Number(row.quantity || 0) > 0).length} repair operation(s)</span>}
          {mode === "costing" && jobType === "Repair" && <span>Repair cost: AED {(repairOperations.reduce((sum,row)=>sum+(Number(row.rate_minor||0)*Math.trunc(Number(row.quantity||0))),0)/100).toFixed(2)}</span>}
        </div>
      </div>
      </fieldset>
      {!readOnly && <footer className="form-footer">{error&&<div className="error-banner">{error}</div>}{editId ? <><Button type="submit" data-save-mode="current" className="action-button action-save" isLoading={busy}>Save Current</Button><Button type="submit" data-save-mode="new_version" className="action-button action-create" isLoading={busy}>Create New Version</Button></> : <Button type="submit" className="action-button action-create" isLoading={busy}>Create job card</Button>}</footer>}
    </form>
  );
}

function DynamicRepairOperationEditor({ rows, setRows, rates, customerId, customers, defaultProfile }: {
  rows: JsonRecord[]; setRows: Dispatch<SetStateAction<JsonRecord[]>>; rates: JsonRecord[];
  customerId: string; customers: JsonRecord[]; defaultProfile: JsonRecord;
}) {
  const customer = customers.find((row) => String(row.id) === customerId);
  const profileIds = new Set([Number(customer?.costing_profile_id || 0), Number(defaultProfile?.id || 0)].filter(Boolean));
  const available = rates.filter((rate) => !profileIds.size || profileIds.has(Number(rate.profile_id || 0)));
  const departments = [...new Set(available.map((rate) => String(rate.service_department || "")).filter(Boolean))];
  const update = (index: number, key: string, value: string | number) => setRows((current) => current.map((row, rowIndex) => {
    if (rowIndex !== index) return row;
    if (key === "service_department") return { ...row, service_department: value, service_type: "", unit: "", costing_rate_id: "", rate_minor: 0 };
    if (key === "service_type") {
      const rate = available.find((item) => String(item.service_department) === String(row.service_department) && String(item.service_type) === String(value));
      return { ...row, service_type: value, unit: String(rate?.unit || ""), costing_rate_id: rate?.id || "", rate_minor: Number(rate?.rate_minor || 0) };
    }
    return { ...row, [key]: value };
  }));
  return <section className="editable-lines dynamic-repair-lines">
    <div className="line-toolbar"><div><strong>Repair operations</strong><small>Unit and rate come from the customer costing profile with OctaDiam Standard fallback.</small></div><Button type="button" isIconOnly className="action-button action-create" title="Add repair operation" aria-label="Add repair operation" onPress={() => setRows((current) => [...current, { costing_rate_id: "", service_department: "", service_type: "", unit: "", quantity: 1, job_notes: "" }])}><Plus size={17} /></Button></div>
    <div className="editable-table-wrap"><table className="editable-table repair-table"><thead><tr><th>#</th><th>Department</th><th>Operation</th><th>Unit</th><th>Qty</th><th>Notes</th><th>Actions</th></tr></thead><tbody>{rows.map((row, index) => {
      const services = [...new Set(available.filter((rate) => String(rate.service_department) === String(row.service_department)).map((rate) => String(rate.service_type || "")).filter(Boolean))];
      return <tr key={index}><td>{index + 1}</td><td><select value={String(row.service_department || "")} onChange={(event) => update(index, "service_department", event.target.value)}><option value="">Select…</option>{departments.map((entry) => <option key={entry}>{entry}</option>)}</select></td><td><select value={String(row.service_type || "")} disabled={!row.service_department} onChange={(event) => update(index, "service_type", event.target.value)}><option value="">Select…</option>{services.map((entry) => <option key={entry}>{entry}</option>)}</select></td><td><input value={String(row.unit || "")} readOnly /></td><td><input type="number" min="1" step="1" value={String(row.quantity || 1)} onChange={(event) => update(index, "quantity", Math.max(1, Math.trunc(Number(event.target.value || 1))))} /></td><td><input value={String(row.job_notes || "")} onChange={(event) => update(index, "job_notes", event.target.value)} /></td><td><Button type="button" isIconOnly className="action-button action-delete" title="Delete repair operation" aria-label="Delete repair operation" onPress={() => setRows((current) => current.filter((_, rowIndex) => rowIndex !== index))}><Trash2 size={16} /></Button></td></tr>;
    })}</tbody></table></div>
  </section>;
}

function InventoryForm({ token, onSaved }: FormProps) {
  const [type, setType] = useState("stone");
  const [supplyMethod, setSupplyMethod] = useState("Purchase");
  const [context, setContext] = useState<JsonRecord>({});
  const [lookups, setLookups] = useState<Record<string, string[]>>({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void Promise.all([
      api<JsonRecord>(`/v1/operations/inventory/editor-context?type=${type}`, {}, token),
      api<Record<string, string[]>>("/v1/reference/lookup-bundle?categories=stone_type,stone_shape,gem_lab,metal_type,jewelry_category,supply_method", {}, token)
    ]).then(([editorContext, lookupValues]) => {
      setContext(editorContext);
      setLookups(lookupValues);
    }).catch((reason) => setError(reason instanceof Error ? reason.message : "Inventory lookup values could not be loaded."));
  }, [token, type]);
  return (
    <form className="workflow-form" onSubmit={async (event) => {
      event.preventDefault(); setBusy(true); setError("");
      const values = new FormData(event.currentTarget);
      try {
        const next = await api<{ stock_code: string }>(`/v1/operations/inventory/${type}/next-code`, {}, token);
        const common = {
          stock_code: next.stock_code, entry_date: text(values.get("entry_date")), supply_method: text(values.get("supply_method")),
          supplier_id: number(values.get("supplier_id")) || null, customer_id: number(values.get("customer_id")) || null,
          receipt_reference: text(values.get("receipt_reference")), location: text(values.get("location")), notes: text(values.get("notes"))
        };
        const item = type === "stone" ? {
          ...common, stone_type: text(values.get("stone_type")), stone_shape: text(values.get("stone_shape")),
          stone_size: text(values.get("stone_size")), received_weight: number(values.get("received_weight")),
          received_quantity: number(values.get("received_quantity")), cost_per_carat: number(values.get("unit_cost")),
          lab: text(values.get("lab")), certificate_id: text(values.get("certificate_id")),
          reorder_quantity: number(values.get("reorder_quantity")), reorder_weight: number(values.get("reorder_weight")), status: "Active"
        } : type === "metal" ? {
          ...common, metal_type: text(values.get("metal_type")), purity: text(values.get("purity")),
          metal_form: text(values.get("metal_form")), received_weight: number(values.get("received_weight")),
          cost_per_gram: number(values.get("unit_cost")), reorder_weight: number(values.get("reorder_weight")), status: "Active"
        } : {
          ...common, design_id_fk: number(values.get("design_id_fk")) || null,
          item_name: text(values.get("item_name")), category: text(values.get("category")),
          sub_category: text(values.get("sub_category")), received_quantity: number(values.get("received_quantity")),
          received_gross_weight: number(values.get("received_weight")), metal_weight: number(values.get("metal_weight")),
          stone_weight: number(values.get("stone_weight")), cost_per_item: number(values.get("unit_cost")),
          selling_price: number(values.get("selling_price")), status: "Finished"
        };
        const attachments = await filePayloads(values, "attachments");
        await api(`/v1/operations/inventory/${type}`, {
          method: "POST",
          body: JSON.stringify({ idempotency_key: idempotencyKey(), item, new_attachments: attachments, deleted_attachment_ids: [] })
        }, token);
        await onSaved();
      } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not save inventory entry."); }
      finally { setBusy(false); }
    }}>
      <Choice name="entry_type" label="Inventory type" options={["stone", "metal", "jewelry"]} required value={type} onChange={setType} />
      <div className="form-grid three">
        <Field name="entry_date" label="Received date" type="date" required defaultValue={today()} />
        <Choice name="supply_method" label="Supply method"
          options={lookups.supply_method?.length ? lookups.supply_method : ["Purchase", "Consignment", "Internal / Inhouse", "Customer Supplied", "Return"]}
          required value={supplyMethod} onChange={setSupplyMethod} />
        {["Purchase", "Consignment"].includes(supplyMethod) &&
          <Choice name="supplier_id" label="Supplier name" rows={context.suppliers as JsonRecord[] || []}
            valueKey="id" labelKeys={["supplier_code", "supplier_name"]} required />}
        {supplyMethod === "Customer Supplied" &&
          <Choice name="customer_id" label="Customer name" rows={context.customers as JsonRecord[] || []}
            valueKey="id" labelKeys={["customer_code", "customer_name"]} required />}
        <Field name="receipt_reference" label="Purchase / receipt reference" />
        {type === "stone" && <>
          <Choice name="stone_type" label="Stone type" options={lookups.stone_type || []} required />
          <Choice name="stone_shape" label="Shape" options={lookups.stone_shape || []} required />
          <Field name="stone_size" label="Size" required /><Choice name="lab" label="Gem lab" options={lookups.gem_lab || []} />
          <Field name="certificate_id" label="Certificate ID" />
        </>}
        {type === "metal" && <>
          <Choice name="metal_type" label="Metal type" options={lookups.metal_type || []} required /><Field name="purity" label="Purity" />
          <Choice name="metal_form" label="Metal form" options={["Bar", "Grain", "Sheet", "Wire", "Scrap", "Finished component"]} />
        </>}
        {type === "jewelry" && <>
          <Choice name="design_id_fk" label="Linked Design ID" rows={context.designs as JsonRecord[] || []}
            valueKey="id" labelKeys={["design_id", "category", "sub_category"]} />
          <Field name="item_name" label="Item name" required />
          <Choice name="category" label="Category" options={lookups.jewelry_category || []} required />
          <Choice name="sub_category" label="Sub category" rows={context.subcategories as JsonRecord[] || []} />
          <Field name="metal_weight" label="Metal weight" type="number" step="0.01" />
          <Field name="stone_weight" label="Stone weight" type="number" step="0.01" />
          <Field name="selling_price" label="Selling price" type="number" step="0.01" />
        </>}
        <Field name="received_weight" label={type === "stone" ? "Received weight (ct)" : "Received weight (g)"} type="number" required step="0.01" />
        {type !== "metal" && <Field name="received_quantity" label="Received quantity" type="number" required step="1" />}
        <Field name="unit_cost" label={type === "stone" ? "Average cost / ct" : type === "metal" ? "Cost / g" : "Cost / item"} type="number" step="0.01" />
        <Field name="location" label="Location / bin" />
        {type !== "jewelry" && <Field name="reorder_weight" label="Reorder weight" type="number" step="0.01" />}
        {type === "stone" && <Field name="reorder_quantity" label="Reorder quantity" type="number" step="1" />}
      </div>
      <Field name="notes" label="Notes" multiline />
      <FileField name="attachments" label="Inventory attachments" multiple />
      <FormFooter busy={busy} error={error} label="Create inventory entry" />
    </form>
  );
}

type PendingManagedFile = { key: string; file: File; category: string };
function PendingFileManager({ label, categories, files, onChange }: { label: string; categories: string[]; files: PendingManagedFile[]; onChange: (files: PendingManagedFile[]) => void }) {
  const [category, setCategory] = useState(categories[0] || "Other");
  useEffect(() => { if (categories.length && !categories.includes(category)) setCategory(categories[0]); }, [categories.join("|"), category]);
  return <section className="inline-file-manager"><div className="inline-file-manager-head"><div><b>{label}</b><small>Add multiple files, then click a file name to preview it.</small></div><div><select aria-label={`${label} category`} value={category} onChange={(event)=>setCategory(event.target.value)}>{categories.map((value)=><option key={value}>{value}</option>)}</select><label className="action-button action-create icon-upload" title="Add files"><Upload size={17}/><input type="file" multiple onChange={(event)=>{const added=[...(event.currentTarget.files||[])].map((file)=>({key:crypto.randomUUID(),file,category}));onChange([...files,...added]);event.currentTarget.value="";}}/></label></div></div>
    <div className="inline-file-list">{files.map((entry)=><div key={entry.key}><a href={URL.createObjectURL(entry.file)} target="_blank" rel="noreferrer" title={`Preview ${entry.file.name}`}><FileText size={16}/><span>{entry.file.name}</span></a><small>{entry.category}</small><Button isIconOnly className="action-button action-delete" title={`Remove ${entry.file.name}`} aria-label={`Remove ${entry.file.name}`} onPress={()=>onChange(files.filter((item)=>item.key!==entry.key))}><Trash2 size={15}/></Button></div>)}{!files.length&&<p className="muted">No files selected.</p>}</div>
  </section>;
}

function PartyForm({ kind, token, onSaved }: FormProps & { kind: "customer" | "supplier" }) {
  const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  const [files,setFiles]=useState<PendingManagedFile[]>([]); const [lookups,setLookups]=useState<Record<string,string[]>>({});
  useEffect(()=>{void api<Record<string,string[]>>(`/v1/reference/lookup-bundle?categories=${kind}_type,${kind}_file_category`,{},token).then(setLookups);},[kind,token]);
  return (
    <form className="workflow-form" onSubmit={async (event) => {
      event.preventDefault(); setBusy(true); setError("");
      const values = new FormData(event.currentTarget);
      try {
        const created = await api<{ id: number }>(`/v1/reference/${kind}s`, {
          method: "POST",
          body: JSON.stringify({
            name: text(values.get("name")), contactPerson: text(values.get("contactPerson")),
            phone: text(values.get("phone")), email: text(values.get("email")), address: text(values.get("address")),
            taxRegistration: text(values.get("taxRegistration")), paymentTerms: text(values.get("paymentTerms")),
            status: "Active", notes: text(values.get("notes")),
            ...(kind === "customer" ? { customerType: text(values.get("partyType")), creditLimit: number(values.get("creditLimit")) } :
              { supplierType: text(values.get("partyType")) })
          })
        }, token);
        for (const entry of files) {
          await api(`/v1/reference/${kind}s/${created.id}/attachments`, {
            method: "POST",
            body: JSON.stringify({
              originalName: entry.file.name,
              fileCategory: entry.category,
              contentBase64: await fileBase64(entry.file)
            })
          }, token);
        }
        await onSaved();
      } catch (reason) { setError(reason instanceof Error ? reason.message : `Could not save ${kind}.`); }
      finally { setBusy(false); }
    }}>
      <div className="form-grid two">
        <Field name="name" label={`${kind === "customer" ? "Customer" : "Supplier"} name`} required />
        <Choice name="partyType" label={`${kind === "customer" ? "Customer" : "Supplier"} type`} options={lookups[`${kind}_type`]||[]} required />
        <Field name="contactPerson" label="Contact person" /><Field name="phone" label="Phone" />
        <Field name="email" label="Email" type="email" /><Field name="taxRegistration" label="Tax registration" />
        <Field name="paymentTerms" label="Payment terms" />
        {kind === "customer" && <Field name="creditLimit" label="Credit limit" type="number" step="0.01" />}
      </div>
      <Field name="address" label="Address" multiline /><Field name="notes" label="Notes" multiline />
      <PendingFileManager label={`${kind === "customer" ? "Customer" : "Supplier"} files`} categories={lookups[`${kind}_file_category`]||["Other"]} files={files} onChange={setFiles}/>
      <FormFooter busy={busy} error={error} label={`Create ${kind}`} />
    </form>
  );
}

function InvoiceForm({ token, onSaved }: FormProps) {
  const [context, setContext] = useState<JsonRecord>({}); const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  const [files,setFiles]=useState<PendingManagedFile[]>([]); const [fileCategories,setFileCategories]=useState<string[]>(["Invoice"]);
  useEffect(() => { void api<JsonRecord>("/v1/commercial/received-invoices/editor-context", {}, token).then(setContext); }, [token]);
  useEffect(()=>{void api<Record<string,string[]>>("/v1/reference/lookup-bundle?categories=invoice_file_category",{},token).then((result)=>setFileCategories(result.invoice_file_category||["Invoice"]));},[token]);
  return (
    <form className="workflow-form" onSubmit={async (event) => {
      event.preventDefault(); setBusy(true); setError(""); const values = new FormData(event.currentTarget);
      try {
        const attachments = await Promise.all(files.map(async(entry)=>({original_name:entry.file.name,content_base64:await fileBase64(entry.file),file_category:entry.category})));
        await api("/v1/commercial/received-invoices", { method: "POST", body: JSON.stringify({
          idempotency_key: idempotencyKey(), invoice: {
            invoice_number: text(values.get("invoice_number")), invoice_date: text(values.get("invoice_date")),
            supplier_id: text(values.get("supplier_id")) || null, amount_minor: Math.round(number(values.get("amount")) * 100),
            tax_minor: Math.round(number(values.get("tax")) * 100), status: text(values.get("status")),
            description: text(values.get("description"))
          }, new_attachments: attachments
        }) }, token); await onSaved();
      } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not record invoice."); } finally { setBusy(false); }
    }}>
      <div className="form-grid two">
        <Field name="invoice_number" label="Invoice number" required /><Field name="invoice_date" label="Invoice date" type="date" defaultValue={today()} required />
        <Choice name="supplier_id" label="Supplier" rows={context.suppliers as JsonRecord[] || []} valueKey="id" labelKeys={["supplier_code", "supplier_name"]} />
        <Choice name="status" label="Status" options={context.statuses as string[] || ["Received"]} required />
        <Field name="amount" label="Amount (AED)" type="number" step="0.01" required /><Field name="tax" label="Tax (AED)" type="number" step="0.01" />
      </div>
      <Field name="description" label="Description" multiline />
      <PendingFileManager label="Invoice files" categories={fileCategories} files={files} onChange={setFiles}/>
      <FormFooter busy={busy} error={error} label="Record invoice" />
    </form>
  );
}

function PettyCashForm({ token, onSaved }: FormProps) {
  const [context, setContext] = useState<JsonRecord>({}); const [type, setType] = useState("Expense");
  const [error, setError] = useState(""); const [busy, setBusy] = useState(false);
  useEffect(() => { void api<JsonRecord>("/v1/commercial/petty-cash/editor-context", {}, token).then(setContext); }, [token]);
  const categories = type === "Expense" ? context.expense_categories : context.receipt_categories;
  return (
    <form className="workflow-form" onSubmit={async (event) => {
      event.preventDefault(); setBusy(true); setError(""); const values = new FormData(event.currentTarget);
      try {
        const attachments = await filePayloads(values, "attachment");
        await api("/v1/commercial/petty-cash", { method: "POST", body: JSON.stringify({
          idempotency_key: idempotencyKey(), transaction: {
            transaction_date: text(values.get("transaction_date")), transaction_type: type,
            category: text(values.get("category")), amount_minor: Math.round(number(values.get("amount")) * 100),
            payee: text(values.get("payee")), supplier_id: text(values.get("supplier_id")) || null,
            has_invoice: Boolean(values.get("has_invoice")), invoice_number: text(values.get("invoice_number")),
            description: text(values.get("description"))
          }, attachment: attachments[0] ? {
            original_name: attachments[0].original_name,
            content_base64: attachments[0].content_base64
          } : null
        }) }, token); await onSaved();
      } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not save petty cash."); } finally { setBusy(false); }
    }}>
      <div className="form-grid two">
        <Field name="transaction_date" label="Date" type="date" defaultValue={today()} required />
        <Choice name="transaction_type" label="Type" options={["Expense", "Receipt"]} required value={type} onChange={setType} />
        <Choice name="category" label="Category" options={categories as string[] || []} required />
        <Field name="amount" label="Amount (AED)" type="number" step="0.01" required />
        <Choice name="supplier_id" label="Payee / supplier" rows={context.suppliers as JsonRecord[] || []} valueKey="id" labelKeys={["supplier_code", "supplier_name"]} />
        <Field name="payee" label="Other payee / source" />
        <label className="check-field"><input type="checkbox" name="has_invoice" /> Expense has an invoice</label>
        <Field name="invoice_number" label="Invoice number" />
      </div>
      <Field name="description" label="Description" multiline />
      <FileField name="attachment" label="Supporting file" />
      <FormFooter busy={busy} error={error} label="Save petty-cash entry" />
    </form>
  );
}

type FormProps = { token: string | null; onSaved: () => void | Promise<unknown> };

function Field({
  name, label, type = "text", required = false, step, defaultValue, help, multiline = false,
  value, readOnly = false, onChange, helpTooltip
}: {
  name: string; label: string; type?: string; required?: boolean; step?: string;
  defaultValue?: string; help?: string; multiline?: boolean; value?: string; readOnly?: boolean;
  onChange?: (value: string) => void; helpTooltip?: string;
}) {
  const numericDefault = type === "number" && step !== "1" && defaultValue !== undefined && defaultValue !== "" && Number.isFinite(Number(defaultValue))
    ? Number(defaultValue).toFixed(2) : defaultValue;
  const numericStep = type === "number" ? (step === "1" ? "1" : "0.01") : step;
  return (
    <label className="form-field">
      <span title={helpTooltip}>{label}{required && <b aria-hidden="true"> *</b>}{helpTooltip && <small className="field-tooltip" aria-label={helpTooltip}> ⓘ</small>}</span>
      {multiline ? <textarea name={name} required={required} defaultValue={value === undefined ? defaultValue : undefined} value={value} readOnly={readOnly} onChange={onChange ? (event) => onChange(event.target.value) : undefined} /> :
        <input name={name} type={type} required={required} step={numericStep} min={type === "number" ? "0" : undefined}
          defaultValue={value === undefined ? numericDefault : undefined} value={value} readOnly={readOnly}
          onChange={onChange ? (event) => onChange(event.target.value) : undefined} />}
      {help && <small>{help}</small>}
    </label>
  );
}

function FileField({ name, label, multiple = false }: { name: string; label: string; multiple?: boolean }) {
  return (
    <label className="form-field file-field">
      <span>{label}</span>
      <input name={name} type="file" multiple={multiple} />
      <small>Files are copied into the Web application’s isolated object storage.</small>
    </label>
  );
}

function Choice({
  name, label, rows = [], options = [], valueKey = "value", labelKeys = ["value"], required = false,
  value, onChange
}: {
  name: string; label: string; rows?: JsonRecord[]; options?: string[]; valueKey?: string; labelKeys?: string[];
  required?: boolean; value?: string; onChange?: (value: string) => void;
}) {
  const [current, setCurrent] = useState(value ?? "");
  useEffect(() => {
    if (value !== undefined) setCurrent(value);
  }, [value]);
  return (
    <label className="form-field">
      <span>{label}{required && <b aria-hidden="true"> *</b>}</span>
      <select
        name={name}
        required={required}
        value={current}
        onChange={(event) => {
          setCurrent(event.target.value);
          onChange?.(event.target.value);
        }}
      >
        <option value="">Select…</option>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
        {rows.map((row, index) => {
          const optionValue = String(row[valueKey] ?? "");
          return <option key={`${optionValue}-${index}`} value={optionValue}>
            {labelKeys.map((key) => row[key]).filter(Boolean).join(" — ") || optionValue}
          </option>;
        })}
      </select>
    </label>
  );
}

function SearchableChoice({
  name, label, rows, valueKey = "id", labelKeys, required = false, value, onChange, filterOptions = {}
}: {
  name: string; label: string; rows: JsonRecord[]; valueKey?: string; labelKeys: string[];
  required?: boolean; value: string; onChange: (value: string) => void; filterOptions?: Record<string,string[]>;
}) {
  const selected = rows.find((row) => String(row[valueKey] ?? "") === value);
  const format = (row: JsonRecord) => labelKeys.map((key) => String(row[key] ?? "").trim()).filter(Boolean).join(" — ");
  const [query, setQuery] = useState(selected ? format(selected) : "");
  const [browseOpen, setBrowseOpen] = useState(false);
  const [criteria, setCriteria] = useState<Record<string,string>>({});
  useEffect(() => { const row=rows.find((entry)=>String(entry[valueKey]??"")===value); setQuery(row ? format(row) : ""); }, [value, rows]);
  const matches = rows.filter((row) => format(row).toLowerCase().includes(query.trim().toLowerCase())).slice(0, 100);
  const browseMatches = rows.filter((row) => labelKeys.every((key) => !criteria[key]?.trim() || String(row[key] || "").toLowerCase().includes(criteria[key].trim().toLowerCase()))).slice(0,250);
  return <div className="form-field searchable-choice">
    <span>{label}{required && <b aria-hidden="true"> *</b>}</span>
    <input type="hidden" name={name} value={value}/>
    <div className="searchable-choice-control"><input value={query} required={required} placeholder={`Search ${label.toLowerCase()}…`} autoComplete="off"
      onChange={(event)=>{setQuery(event.target.value); if(value)onChange("");}}/><Button type="button" isIconOnly className="action-button action-view" title={`Open ${label} search`} aria-label={`Open ${label} search`} onPress={()=>setBrowseOpen(true)}><Search size={17}/></Button></div>
    {query && !value && <div className="searchable-choice-results" role="listbox">
      {matches.map((row)=><button type="button" key={String(row[valueKey])} onClick={()=>{onChange(String(row[valueKey]));setQuery(format(row));}}>{format(row)}</button>)}
      {!matches.length && <small>No matching records</small>}
    </div>}
    {browseOpen && <div className="dialog-backdrop nested-dialog" role="presentation"><section className="workflow-dialog record-search-dialog" role="dialog" aria-modal="true" aria-label={`Search ${label}`}><header><div><p className="eyebrow">Record selection</p><h2>Search {label}</h2></div><button type="button" className="dialog-close" onClick={()=>setBrowseOpen(false)} aria-label="Close"><X/></button></header><div className="workflow-body"><div className="record-search-criteria">{labelKeys.map((key)=><label className="form-field" key={key}><span>{key.replaceAll("_"," ")}</span>{filterOptions[key]?.length ? <select value={criteria[key]||""} onChange={(event)=>setCriteria((current)=>({...current,[key]:event.target.value}))}><option value="">Any</option>{filterOptions[key].map(option=><option key={option}>{option}</option>)}</select> : <input value={criteria[key]||""} onChange={(event)=>setCriteria((current)=>({...current,[key]:event.target.value}))} placeholder={`Filter by ${key.replaceAll("_"," ")}`}/>}</label>)}</div><div className="record-search-results">{browseMatches.map((row)=><button type="button" key={String(row[valueKey])} onClick={()=>{onChange(String(row[valueKey]));setQuery(format(row));setBrowseOpen(false);}}><strong>{format(row)}</strong></button>)}{!browseMatches.length&&<div className="inline-empty">No matching records.</div>}</div><small>Showing up to 250 matching records, newest first where a creation date is available.</small></div></section></div>}
  </div>;
}

function FormFooter({ busy, error, label }: { busy: boolean; error: string; label: string }) {
  return (
    <footer className="form-footer">
      {error && <div className="error-banner">{error}</div>}
      <Button type="submit" className="action-button action-create" isLoading={busy}>{label}</Button>
    </footer>
  );
}

function workflowTitle(moduleKey: string) {
  return moduleKey === "designs" ? "New jewelry design" :
    moduleKey === "jobCards" ? "New production job card" :
      moduleKey === "inventory" ? "New inventory entry" :
        moduleKey === "receivedInvoices" ? "Record received invoice" :
          moduleKey === "pettyCash" ? "New petty-cash transaction" :
            `New ${moduleKey === "customers" ? "customer" : "supplier"}`;
}

function extractRows(value: unknown): JsonRecord[] {
  if (Array.isArray(value)) return value as JsonRecord[];
  if (!value || typeof value !== "object") return [];
  for (const key of ["items", "rows", "records"]) {
    const rows = (value as JsonRecord)[key];
    if (Array.isArray(rows)) return rows as JsonRecord[];
  }
  return [];
}

function detailPath(moduleKey: string, id: string, selected?: JsonRecord) {
  if (!id) return "";
  if (moduleKey === "designs") return `/v1/jewelry/designs/${id}`;
  if (moduleKey === "jobCards") return `/v1/operations/job-cards/${id}`;
  if (moduleKey === "customers") return `/v1/reference/customers/${id}`;
  if (moduleKey === "suppliers") return `/v1/reference/suppliers/${id}`;
  if (moduleKey === "receivedInvoices") return `/v1/commercial/received-invoices/${id}`;
  if (moduleKey === "inventory") {
    const type = String(selected?.inventory_type || "").toLowerCase();
    return type ? `/v1/operations/inventory/${type}/${id}` : "";
  }
  if (moduleKey === "diamondStock") return `/v1/diamond-stock/${id}`;
  if (moduleKey === "shopify") return `/v1/shopify/products/${id}`;
  if (moduleKey === "shopifyPreparation") return `/v1/shopify/preparations/${id}`;
  return "";
}

function recordLabel(row: JsonRecord, index: number) {
  for (const key of [
    "design_id", "job_card_number", "stock_code", "customer_name", "supplier_name",
    "invoice_number", "transaction_number", "certificate_no", "title", "name"
  ]) {
    if (row[key]) return String(row[key]);
  }
  return `Record ${index + 1}`;
}

function humanize(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}
